import { type StateCode } from "./states";

/**
 * Brazilian municipalities by state, published by the IBGE. `DATA` holds, for each state, a
 * `[name, ibgeCode]` tuple per municipality, sorted by name with `localeCompare` in the
 * "pt-BR" locale. Generated by `scripts/cities.ts`.
 *
 * @see Official: https://servicodados.ibge.gov.br/api/docs/localidades
 */
export type Municipality = {
	/** The 7-digit IBGE municipality code. */
	code: string;
	/** The municipality name. */
	name: string;
	/** The two-letter code of the state the municipality belongs to. */
	stateCode: StateCode;
};

export const DATA: Record<StateCode, readonly (readonly [string, string])[]> = {
	AC: [
		["Acrelândia", "1200013"],
		["Assis Brasil", "1200054"],
		["Brasiléia", "1200104"],
		["Bujari", "1200138"],
		["Capixaba", "1200179"],
		["Cruzeiro do Sul", "1200203"],
		["Epitaciolândia", "1200252"],
		["Feijó", "1200302"],
		["Jordão", "1200328"],
		["Mâncio Lima", "1200336"],
		["Manoel Urbano", "1200344"],
		["Marechal Thaumaturgo", "1200351"],
		["Plácido de Castro", "1200385"],
		["Porto Acre", "1200807"],
		["Porto Walter", "1200393"],
		["Rio Branco", "1200401"],
		["Rodrigues Alves", "1200427"],
		["Santa Rosa do Purus", "1200435"],
		["Sena Madureira", "1200500"],
		["Senador Guiomard", "1200450"],
		["Tarauacá", "1200609"],
		["Xapuri", "1200708"],
	],
	AL: [
		["Água Branca", "2700102"],
		["Anadia", "2700201"],
		["Arapiraca", "2700300"],
		["Atalaia", "2700409"],
		["Barra de Santo Antônio", "2700508"],
		["Barra de São Miguel", "2700607"],
		["Batalha", "2700706"],
		["Belém", "2700805"],
		["Belo Monte", "2700904"],
		["Boca da Mata", "2701001"],
		["Branquinha", "2701100"],
		["Cacimbinhas", "2701209"],
		["Cajueiro", "2701308"],
		["Campestre", "2701357"],
		["Campo Alegre", "2701407"],
		["Campo Grande", "2701506"],
		["Canapi", "2701605"],
		["Capela", "2701704"],
		["Carneiros", "2701803"],
		["Chã Preta", "2701902"],
		["Coité do Nóia", "2702009"],
		["Colônia Leopoldina", "2702108"],
		["Coqueiro Seco", "2702207"],
		["Coruripe", "2702306"],
		["Craíbas", "2702355"],
		["Delmiro Gouveia", "2702405"],
		["Dois Riachos", "2702504"],
		["Estrela de Alagoas", "2702553"],
		["Feira Grande", "2702603"],
		["Feliz Deserto", "2702702"],
		["Flexeiras", "2702801"],
		["Girau do Ponciano", "2702900"],
		["Ibateguara", "2703007"],
		["Igaci", "2703106"],
		["Igreja Nova", "2703205"],
		["Inhapi", "2703304"],
		["Jacaré dos Homens", "2703403"],
		["Jacuípe", "2703502"],
		["Japaratinga", "2703601"],
		["Jaramataia", "2703700"],
		["Jequiá da Praia", "2703759"],
		["Joaquim Gomes", "2703809"],
		["Jundiá", "2703908"],
		["Junqueiro", "2704005"],
		["Lagoa da Canoa", "2704104"],
		["Limoeiro de Anadia", "2704203"],
		["Maceió", "2704302"],
		["Major Isidoro", "2704401"],
		["Mar Vermelho", "2704906"],
		["Maragogi", "2704500"],
		["Maravilha", "2704609"],
		["Marechal Deodoro", "2704708"],
		["Maribondo", "2704807"],
		["Mata Grande", "2705002"],
		["Matriz de Camaragibe", "2705101"],
		["Messias", "2705200"],
		["Minador do Negrão", "2705309"],
		["Monteirópolis", "2705408"],
		["Murici", "2705507"],
		["Novo Lino", "2705606"],
		["Olho d'Água das Flores", "2705705"],
		["Olho d'Água do Casado", "2705804"],
		["Olho d'Água Grande", "2705903"],
		["Olivença", "2706000"],
		["Ouro Branco", "2706109"],
		["Palestina", "2706208"],
		["Palmeira dos Índios", "2706307"],
		["Pão de Açúcar", "2706406"],
		["Pariconha", "2706422"],
		["Paripueira", "2706448"],
		["Passo de Camaragibe", "2706505"],
		["Paulo Jacinto", "2706604"],
		["Penedo", "2706703"],
		["Piaçabuçu", "2706802"],
		["Pilar", "2706901"],
		["Pindoba", "2707008"],
		["Piranhas", "2707107"],
		["Poço das Trincheiras", "2707206"],
		["Porto Calvo", "2707305"],
		["Porto de Pedras", "2707404"],
		["Porto Real do Colégio", "2707503"],
		["Quebrangulo", "2707602"],
		["Rio Largo", "2707701"],
		["Roteiro", "2707800"],
		["Santa Luzia do Norte", "2707909"],
		["Santana do Ipanema", "2708006"],
		["Santana do Mundaú", "2708105"],
		["São Brás", "2708204"],
		["São José da Laje", "2708303"],
		["São José da Tapera", "2708402"],
		["São Luís do Quitunde", "2708501"],
		["São Miguel dos Campos", "2708600"],
		["São Miguel dos Milagres", "2708709"],
		["São Sebastião", "2708808"],
		["Satuba", "2708907"],
		["Senador Rui Palmeira", "2708956"],
		["Tanque d'Arca", "2709004"],
		["Taquarana", "2709103"],
		["Teotônio Vilela", "2709152"],
		["Traipu", "2709202"],
		["União dos Palmares", "2709301"],
		["Viçosa", "2709400"],
	],
	AM: [
		["Alvarães", "1300029"],
		["Amaturá", "1300060"],
		["Anamã", "1300086"],
		["Anori", "1300102"],
		["Apuí", "1300144"],
		["Atalaia do Norte", "1300201"],
		["Autazes", "1300300"],
		["Barcelos", "1300409"],
		["Barreirinha", "1300508"],
		["Benjamin Constant", "1300607"],
		["Beruri", "1300631"],
		["Boa Vista do Ramos", "1300680"],
		["Boca do Acre", "1300706"],
		["Borba", "1300805"],
		["Caapiranga", "1300839"],
		["Canutama", "1300904"],
		["Carauari", "1301001"],
		["Careiro", "1301100"],
		["Careiro da Várzea", "1301159"],
		["Coari", "1301209"],
		["Codajás", "1301308"],
		["Eirunepé", "1301407"],
		["Envira", "1301506"],
		["Fonte Boa", "1301605"],
		["Guajará", "1301654"],
		["Humaitá", "1301704"],
		["Ipixuna", "1301803"],
		["Iranduba", "1301852"],
		["Itacoatiara", "1301902"],
		["Itamarati", "1301951"],
		["Itapiranga", "1302009"],
		["Japurá", "1302108"],
		["Juruá", "1302207"],
		["Jutaí", "1302306"],
		["Lábrea", "1302405"],
		["Manacapuru", "1302504"],
		["Manaquiri", "1302553"],
		["Manaus", "1302603"],
		["Manicoré", "1302702"],
		["Maraã", "1302801"],
		["Maués", "1302900"],
		["Nhamundá", "1303007"],
		["Nova Olinda do Norte", "1303106"],
		["Novo Airão", "1303205"],
		["Novo Aripuanã", "1303304"],
		["Parintins", "1303403"],
		["Pauini", "1303502"],
		["Presidente Figueiredo", "1303536"],
		["Rio Preto da Eva", "1303569"],
		["Santa Isabel do Rio Negro", "1303601"],
		["Santo Antônio do Içá", "1303700"],
		["São Gabriel da Cachoeira", "1303809"],
		["São Paulo de Olivença", "1303908"],
		["São Sebastião do Uatumã", "1303957"],
		["Silves", "1304005"],
		["Tabatinga", "1304062"],
		["Tapauá", "1304104"],
		["Tefé", "1304203"],
		["Tonantins", "1304237"],
		["Uarini", "1304260"],
		["Urucará", "1304302"],
		["Urucurituba", "1304401"],
	],
	AP: [
		["Amapá", "1600105"],
		["Calçoene", "1600204"],
		["Cutias", "1600212"],
		["Ferreira Gomes", "1600238"],
		["Itaubal", "1600253"],
		["Laranjal do Jari", "1600279"],
		["Macapá", "1600303"],
		["Mazagão", "1600402"],
		["Oiapoque", "1600501"],
		["Pedra Branca do Amapari", "1600154"],
		["Porto Grande", "1600535"],
		["Pracuúba", "1600550"],
		["Santana", "1600600"],
		["Serra do Navio", "1600055"],
		["Tartarugalzinho", "1600709"],
		["Vitória do Jari", "1600808"],
	],
	BA: [
		["Abaíra", "2900108"],
		["Abaré", "2900207"],
		["Acajutiba", "2900306"],
		["Adustina", "2900355"],
		["Água Fria", "2900405"],
		["Aiquara", "2900603"],
		["Alagoinhas", "2900702"],
		["Alcobaça", "2900801"],
		["Almadina", "2900900"],
		["Amargosa", "2901007"],
		["Amélia Rodrigues", "2901106"],
		["América Dourada", "2901155"],
		["Anagé", "2901205"],
		["Andaraí", "2901304"],
		["Andorinha", "2901353"],
		["Angical", "2901403"],
		["Anguera", "2901502"],
		["Antas", "2901601"],
		["Antônio Cardoso", "2901700"],
		["Antônio Gonçalves", "2901809"],
		["Aporá", "2901908"],
		["Apuarema", "2901957"],
		["Araçás", "2902054"],
		["Aracatu", "2902005"],
		["Araci", "2902104"],
		["Aramari", "2902203"],
		["Arataca", "2902252"],
		["Aratuípe", "2902302"],
		["Aurelino Leal", "2902401"],
		["Baianópolis", "2902500"],
		["Baixa Grande", "2902609"],
		["Banzaê", "2902658"],
		["Barra", "2902708"],
		["Barra da Estiva", "2902807"],
		["Barra do Choça", "2902906"],
		["Barra do Mendes", "2903003"],
		["Barra do Rocha", "2903102"],
		["Barreiras", "2903201"],
		["Barro Alto", "2903235"],
		["Barro Preto", "2903300"],
		["Barrocas", "2903276"],
		["Belmonte", "2903409"],
		["Belo Campo", "2903508"],
		["Biritinga", "2903607"],
		["Boa Nova", "2903706"],
		["Boa Vista do Tupim", "2903805"],
		["Bom Jesus da Lapa", "2903904"],
		["Bom Jesus da Serra", "2903953"],
		["Boninal", "2904001"],
		["Bonito", "2904050"],
		["Boquira", "2904100"],
		["Botuporã", "2904209"],
		["Brejões", "2904308"],
		["Brejolândia", "2904407"],
		["Brotas de Macaúbas", "2904506"],
		["Brumado", "2904605"],
		["Buerarema", "2904704"],
		["Buritirama", "2904753"],
		["Caatiba", "2904803"],
		["Cabaceiras do Paraguaçu", "2904852"],
		["Cachoeira", "2904902"],
		["Caculé", "2905008"],
		["Caém", "2905107"],
		["Caetanos", "2905156"],
		["Caetité", "2905206"],
		["Cafarnaum", "2905305"],
		["Cairu", "2905404"],
		["Caldeirão Grande", "2905503"],
		["Camacan", "2905602"],
		["Camaçari", "2905701"],
		["Camamu", "2905800"],
		["Campo Alegre de Lourdes", "2905909"],
		["Campo Formoso", "2906006"],
		["Canápolis", "2906105"],
		["Canarana", "2906204"],
		["Canavieiras", "2906303"],
		["Candeal", "2906402"],
		["Candeias", "2906501"],
		["Candiba", "2906600"],
		["Cândido Sales", "2906709"],
		["Cansanção", "2906808"],
		["Canudos", "2906824"],
		["Capela do Alto Alegre", "2906857"],
		["Capim Grosso", "2906873"],
		["Caraíbas", "2906899"],
		["Caravelas", "2906907"],
		["Cardeal da Silva", "2907004"],
		["Carinhanha", "2907103"],
		["Casa Nova", "2907202"],
		["Castro Alves", "2907301"],
		["Catolândia", "2907400"],
		["Catu", "2907509"],
		["Caturama", "2907558"],
		["Central", "2907608"],
		["Chorrochó", "2907707"],
		["Cícero Dantas", "2907806"],
		["Cipó", "2907905"],
		["Coaraci", "2908002"],
		["Cocos", "2908101"],
		["Conceição da Feira", "2908200"],
		["Conceição do Almeida", "2908309"],
		["Conceição do Coité", "2908408"],
		["Conceição do Jacuípe", "2908507"],
		["Conde", "2908606"],
		["Condeúba", "2908705"],
		["Contendas do Sincorá", "2908804"],
		["Coração de Maria", "2908903"],
		["Cordeiros", "2909000"],
		["Coribe", "2909109"],
		["Coronel João Sá", "2909208"],
		["Correntina", "2909307"],
		["Cotegipe", "2909406"],
		["Cravolândia", "2909505"],
		["Crisópolis", "2909604"],
		["Cristópolis", "2909703"],
		["Cruz das Almas", "2909802"],
		["Curaçá", "2909901"],
		["Dário Meira", "2910008"],
		["Dias d'Ávila", "2910057"],
		["Dom Basílio", "2910107"],
		["Dom Macedo Costa", "2910206"],
		["Elísio Medrado", "2910305"],
		["Encruzilhada", "2910404"],
		["Entre Rios", "2910503"],
		["Érico Cardoso", "2900504"],
		["Esplanada", "2910602"],
		["Euclides da Cunha", "2910701"],
		["Eunápolis", "2910727"],
		["Fátima", "2910750"],
		["Feira da Mata", "2910776"],
		["Feira de Santana", "2910800"],
		["Filadélfia", "2910859"],
		["Firmino Alves", "2910909"],
		["Floresta Azul", "2911006"],
		["Formosa do Rio Preto", "2911105"],
		["Gandu", "2911204"],
		["Gavião", "2911253"],
		["Gentio do Ouro", "2911303"],
		["Glória", "2911402"],
		["Gongogi", "2911501"],
		["Governador Mangabeira", "2911600"],
		["Guajeru", "2911659"],
		["Guanambi", "2911709"],
		["Guaratinga", "2911808"],
		["Heliópolis", "2911857"],
		["Iaçu", "2911907"],
		["Ibiassucê", "2912004"],
		["Ibicaraí", "2912103"],
		["Ibicoara", "2912202"],
		["Ibicuí", "2912301"],
		["Ibipeba", "2912400"],
		["Ibipitanga", "2912509"],
		["Ibiquera", "2912608"],
		["Ibirapitanga", "2912707"],
		["Ibirapuã", "2912806"],
		["Ibirataia", "2912905"],
		["Ibitiara", "2913002"],
		["Ibititá", "2913101"],
		["Ibotirama", "2913200"],
		["Ichu", "2913309"],
		["Igaporã", "2913408"],
		["Igrapiúna", "2913457"],
		["Iguaí", "2913507"],
		["Ilhéus", "2913606"],
		["Inhambupe", "2913705"],
		["Ipecaetá", "2913804"],
		["Ipiaú", "2913903"],
		["Ipirá", "2914000"],
		["Ipupiara", "2914109"],
		["Irajuba", "2914208"],
		["Iramaia", "2914307"],
		["Iraquara", "2914406"],
		["Irará", "2914505"],
		["Irecê", "2914604"],
		["Itabela", "2914653"],
		["Itaberaba", "2914703"],
		["Itabuna", "2914802"],
		["Itacaré", "2914901"],
		["Itaeté", "2915007"],
		["Itagi", "2915106"],
		["Itagibá", "2915205"],
		["Itagimirim", "2915304"],
		["Itaguaçu da Bahia", "2915353"],
		["Itaju do Colônia", "2915403"],
		["Itajuípe", "2915502"],
		["Itamaraju", "2915601"],
		["Itamari", "2915700"],
		["Itambé", "2915809"],
		["Itanagra", "2915908"],
		["Itanhém", "2916005"],
		["Itaparica", "2916104"],
		["Itapé", "2916203"],
		["Itapebi", "2916302"],
		["Itapetinga", "2916401"],
		["Itapicuru", "2916500"],
		["Itapitanga", "2916609"],
		["Itaquara", "2916708"],
		["Itarantim", "2916807"],
		["Itatim", "2916856"],
		["Itiruçu", "2916906"],
		["Itiúba", "2917003"],
		["Itororó", "2917102"],
		["Ituaçu", "2917201"],
		["Ituberá", "2917300"],
		["Iuiu", "2917334"],
		["Jaborandi", "2917359"],
		["Jacaraci", "2917409"],
		["Jacobina", "2917508"],
		["Jaguaquara", "2917607"],
		["Jaguarari", "2917706"],
		["Jaguaripe", "2917805"],
		["Jandaíra", "2917904"],
		["Jequié", "2918001"],
		["Jeremoabo", "2918100"],
		["Jiquiriçá", "2918209"],
		["Jitaúna", "2918308"],
		["João Dourado", "2918357"],
		["Juazeiro", "2918407"],
		["Jucuruçu", "2918456"],
		["Jussara", "2918506"],
		["Jussari", "2918555"],
		["Jussiape", "2918605"],
		["Lafaiete Coutinho", "2918704"],
		["Lagoa Real", "2918753"],
		["Laje", "2918803"],
		["Lajedão", "2918902"],
		["Lajedinho", "2919009"],
		["Lajedo do Tabocal", "2919058"],
		["Lamarão", "2919108"],
		["Lapão", "2919157"],
		["Lauro de Freitas", "2919207"],
		["Lençóis", "2919306"],
		["Licínio de Almeida", "2919405"],
		["Livramento de Nossa Senhora", "2919504"],
		["Luís Eduardo Magalhães", "2919553"],
		["Macajuba", "2919603"],
		["Macarani", "2919702"],
		["Macaúbas", "2919801"],
		["Macururé", "2919900"],
		["Madre de Deus", "2919926"],
		["Maetinga", "2919959"],
		["Maiquinique", "2920007"],
		["Mairi", "2920106"],
		["Malhada", "2920205"],
		["Malhada de Pedras", "2920304"],
		["Manoel Vitorino", "2920403"],
		["Mansidão", "2920452"],
		["Maracás", "2920502"],
		["Maragogipe", "2920601"],
		["Maraú", "2920700"],
		["Marcionílio Souza", "2920809"],
		["Mascote", "2920908"],
		["Mata de São João", "2921005"],
		["Matina", "2921054"],
		["Medeiros Neto", "2921104"],
		["Miguel Calmon", "2921203"],
		["Milagres", "2921302"],
		["Mirangaba", "2921401"],
		["Mirante", "2921450"],
		["Monte Santo", "2921500"],
		["Morpará", "2921609"],
		["Morro do Chapéu", "2921708"],
		["Mortugaba", "2921807"],
		["Mucugê", "2921906"],
		["Mucuri", "2922003"],
		["Mulungu do Morro", "2922052"],
		["Mundo Novo", "2922102"],
		["Muniz Ferreira", "2922201"],
		["Muquém do São Francisco", "2922250"],
		["Muritiba", "2922300"],
		["Mutuípe", "2922409"],
		["Nazaré", "2922508"],
		["Nilo Peçanha", "2922607"],
		["Nordestina", "2922656"],
		["Nova Canaã", "2922706"],
		["Nova Fátima", "2922730"],
		["Nova Ibiá", "2922755"],
		["Nova Itarana", "2922805"],
		["Nova Redenção", "2922854"],
		["Nova Soure", "2922904"],
		["Nova Viçosa", "2923001"],
		["Novo Horizonte", "2923035"],
		["Novo Triunfo", "2923050"],
		["Olindina", "2923100"],
		["Oliveira dos Brejinhos", "2923209"],
		["Ouriçangas", "2923308"],
		["Ourolândia", "2923357"],
		["Palmas de Monte Alto", "2923407"],
		["Palmeiras", "2923506"],
		["Paramirim", "2923605"],
		["Paratinga", "2923704"],
		["Paripiranga", "2923803"],
		["Pau Brasil", "2923902"],
		["Paulo Afonso", "2924009"],
		["Pé de Serra", "2924058"],
		["Pedrão", "2924108"],
		["Pedro Alexandre", "2924207"],
		["Piatã", "2924306"],
		["Pilão Arcado", "2924405"],
		["Pindaí", "2924504"],
		["Pindobaçu", "2924603"],
		["Pintadas", "2924652"],
		["Piraí do Norte", "2924678"],
		["Piripá", "2924702"],
		["Piritiba", "2924801"],
		["Planaltino", "2924900"],
		["Planalto", "2925006"],
		["Poções", "2925105"],
		["Pojuca", "2925204"],
		["Ponto Novo", "2925253"],
		["Porto Seguro", "2925303"],
		["Potiraguá", "2925402"],
		["Prado", "2925501"],
		["Presidente Dutra", "2925600"],
		["Presidente Jânio Quadros", "2925709"],
		["Presidente Tancredo Neves", "2925758"],
		["Queimadas", "2925808"],
		["Quijingue", "2925907"],
		["Quixabeira", "2925931"],
		["Rafael Jambeiro", "2925956"],
		["Remanso", "2926004"],
		["Retirolândia", "2926103"],
		["Riachão das Neves", "2926202"],
		["Riachão do Jacuípe", "2926301"],
		["Riacho de Santana", "2926400"],
		["Ribeira do Amparo", "2926509"],
		["Ribeira do Pombal", "2926608"],
		["Ribeirão do Largo", "2926657"],
		["Rio de Contas", "2926707"],
		["Rio do Antônio", "2926806"],
		["Rio do Pires", "2926905"],
		["Rio Real", "2927002"],
		["Rodelas", "2927101"],
		["Ruy Barbosa", "2927200"],
		["Salinas da Margarida", "2927309"],
		["Salvador", "2927408"],
		["Santa Bárbara", "2927507"],
		["Santa Brígida", "2927606"],
		["Santa Cruz Cabrália", "2927705"],
		["Santa Cruz da Vitória", "2927804"],
		["Santa Inês", "2927903"],
		["Santa Luzia", "2928059"],
		["Santa Maria da Vitória", "2928109"],
		["Santa Rita de Cássia", "2928406"],
		["Santa Terezinha", "2928505"],
		["Santaluz", "2928000"],
		["Santana", "2928208"],
		["Santanópolis", "2928307"],
		["Santo Amaro", "2928604"],
		["Santo Antônio de Jesus", "2928703"],
		["Santo Estêvão", "2928802"],
		["São Desidério", "2928901"],
		["São Domingos", "2928950"],
		["São Felipe", "2929107"],
		["São Félix", "2929008"],
		["São Félix do Coribe", "2929057"],
		["São Francisco do Conde", "2929206"],
		["São Gabriel", "2929255"],
		["São Gonçalo dos Campos", "2929305"],
		["São José da Vitória", "2929354"],
		["São José do Jacuípe", "2929370"],
		["São Miguel das Matas", "2929404"],
		["São Sebastião do Passé", "2929503"],
		["Sapeaçu", "2929602"],
		["Sátiro Dias", "2929701"],
		["Saubara", "2929750"],
		["Saúde", "2929800"],
		["Seabra", "2929909"],
		["Sebastião Laranjeiras", "2930006"],
		["Senhor do Bonfim", "2930105"],
		["Sento Sé", "2930204"],
		["Serra do Ramalho", "2930154"],
		["Serra Dourada", "2930303"],
		["Serra Preta", "2930402"],
		["Serrinha", "2930501"],
		["Serrolândia", "2930600"],
		["Simões Filho", "2930709"],
		["Sítio do Mato", "2930758"],
		["Sítio do Quinto", "2930766"],
		["Sobradinho", "2930774"],
		["Souto Soares", "2930808"],
		["Tabocas do Brejo Velho", "2930907"],
		["Tanhaçu", "2931004"],
		["Tanque Novo", "2931053"],
		["Tanquinho", "2931103"],
		["Taperoá", "2931202"],
		["Tapiramutá", "2931301"],
		["Teixeira de Freitas", "2931350"],
		["Teodoro Sampaio", "2931400"],
		["Teofilândia", "2931509"],
		["Teolândia", "2931608"],
		["Terra Nova", "2931707"],
		["Tremedal", "2931806"],
		["Tucano", "2931905"],
		["Uauá", "2932002"],
		["Ubaíra", "2932101"],
		["Ubaitaba", "2932200"],
		["Ubatã", "2932309"],
		["Uibaí", "2932408"],
		["Umburanas", "2932457"],
		["Una", "2932507"],
		["Urandi", "2932606"],
		["Uruçuca", "2932705"],
		["Utinga", "2932804"],
		["Valença", "2932903"],
		["Valente", "2933000"],
		["Várzea da Roça", "2933059"],
		["Várzea do Poço", "2933109"],
		["Várzea Nova", "2933158"],
		["Varzedo", "2933174"],
		["Vera Cruz", "2933208"],
		["Vereda", "2933257"],
		["Vitória da Conquista", "2933307"],
		["Wagner", "2933406"],
		["Wanderley", "2933455"],
		["Wenceslau Guimarães", "2933505"],
		["Xique-Xique", "2933604"],
	],
	CE: [
		["Abaiara", "2300101"],
		["Acarape", "2300150"],
		["Acaraú", "2300200"],
		["Acopiara", "2300309"],
		["Aiuaba", "2300408"],
		["Alcântaras", "2300507"],
		["Altaneira", "2300606"],
		["Alto Santo", "2300705"],
		["Amontada", "2300754"],
		["Antonina do Norte", "2300804"],
		["Apuiarés", "2300903"],
		["Aquiraz", "2301000"],
		["Aracati", "2301109"],
		["Aracoiaba", "2301208"],
		["Ararendá", "2301257"],
		["Araripe", "2301307"],
		["Aratuba", "2301406"],
		["Arneiroz", "2301505"],
		["Assaré", "2301604"],
		["Aurora", "2301703"],
		["Baixio", "2301802"],
		["Banabuiú", "2301851"],
		["Barbalha", "2301901"],
		["Barreira", "2301950"],
		["Barro", "2302008"],
		["Barroquinha", "2302057"],
		["Baturité", "2302107"],
		["Beberibe", "2302206"],
		["Bela Cruz", "2302305"],
		["Boa Viagem", "2302404"],
		["Brejo Santo", "2302503"],
		["Camocim", "2302602"],
		["Campos Sales", "2302701"],
		["Canindé", "2302800"],
		["Capistrano", "2302909"],
		["Caridade", "2303006"],
		["Cariré", "2303105"],
		["Caririaçu", "2303204"],
		["Cariús", "2303303"],
		["Carnaubal", "2303402"],
		["Cascavel", "2303501"],
		["Catarina", "2303600"],
		["Catunda", "2303659"],
		["Caucaia", "2303709"],
		["Cedro", "2303808"],
		["Chaval", "2303907"],
		["Choró", "2303931"],
		["Chorozinho", "2303956"],
		["Coreaú", "2304004"],
		["Crateús", "2304103"],
		["Crato", "2304202"],
		["Croatá", "2304236"],
		["Cruz", "2304251"],
		["Deputado Irapuan Pinheiro", "2304269"],
		["Ereré", "2304277"],
		["Eusébio", "2304285"],
		["Farias Brito", "2304301"],
		["Forquilha", "2304350"],
		["Fortaleza", "2304400"],
		["Fortim", "2304459"],
		["Frecheirinha", "2304509"],
		["General Sampaio", "2304608"],
		["Graça", "2304657"],
		["Granja", "2304707"],
		["Granjeiro", "2304806"],
		["Groaíras", "2304905"],
		["Guaiúba", "2304954"],
		["Guaraciaba do Norte", "2305001"],
		["Guaramiranga", "2305100"],
		["Hidrolândia", "2305209"],
		["Horizonte", "2305233"],
		["Ibaretama", "2305266"],
		["Ibiapina", "2305308"],
		["Ibicuitinga", "2305332"],
		["Icapuí", "2305357"],
		["Icó", "2305407"],
		["Iguatu", "2305506"],
		["Independência", "2305605"],
		["Ipaporanga", "2305654"],
		["Ipaumirim", "2305704"],
		["Ipu", "2305803"],
		["Ipueiras", "2305902"],
		["Iracema", "2306009"],
		["Irauçuba", "2306108"],
		["Itaiçaba", "2306207"],
		["Itaitinga", "2306256"],
		["Itapajé", "2306306"],
		["Itapipoca", "2306405"],
		["Itapiúna", "2306504"],
		["Itarema", "2306553"],
		["Itatira", "2306603"],
		["Jaguaretama", "2306702"],
		["Jaguaribara", "2306801"],
		["Jaguaribe", "2306900"],
		["Jaguaruana", "2307007"],
		["Jardim", "2307106"],
		["Jati", "2307205"],
		["Jijoca de Jericoacoara", "2307254"],
		["Juazeiro do Norte", "2307304"],
		["Jucás", "2307403"],
		["Lavras da Mangabeira", "2307502"],
		["Limoeiro do Norte", "2307601"],
		["Madalena", "2307635"],
		["Maracanaú", "2307650"],
		["Maranguape", "2307700"],
		["Marco", "2307809"],
		["Martinópole", "2307908"],
		["Massapê", "2308005"],
		["Mauriti", "2308104"],
		["Meruoca", "2308203"],
		["Milagres", "2308302"],
		["Milhã", "2308351"],
		["Miraíma", "2308377"],
		["Missão Velha", "2308401"],
		["Mombaça", "2308500"],
		["Monsenhor Tabosa", "2308609"],
		["Morada Nova", "2308708"],
		["Moraújo", "2308807"],
		["Morrinhos", "2308906"],
		["Mucambo", "2309003"],
		["Mulungu", "2309102"],
		["Nova Olinda", "2309201"],
		["Nova Russas", "2309300"],
		["Novo Oriente", "2309409"],
		["Ocara", "2309458"],
		["Orós", "2309508"],
		["Pacajus", "2309607"],
		["Pacatuba", "2309706"],
		["Pacoti", "2309805"],
		["Pacujá", "2309904"],
		["Palhano", "2310001"],
		["Palmácia", "2310100"],
		["Paracuru", "2310209"],
		["Paraipaba", "2310258"],
		["Parambu", "2310308"],
		["Paramoti", "2310407"],
		["Pedra Branca", "2310506"],
		["Penaforte", "2310605"],
		["Pentecoste", "2310704"],
		["Pereiro", "2310803"],
		["Pindoretama", "2310852"],
		["Piquet Carneiro", "2310902"],
		["Pires Ferreira", "2310951"],
		["Poranga", "2311009"],
		["Porteiras", "2311108"],
		["Potengi", "2311207"],
		["Potiretama", "2311231"],
		["Quiterianópolis", "2311264"],
		["Quixadá", "2311306"],
		["Quixelô", "2311355"],
		["Quixeramobim", "2311405"],
		["Quixeré", "2311504"],
		["Redenção", "2311603"],
		["Reriutaba", "2311702"],
		["Russas", "2311801"],
		["Saboeiro", "2311900"],
		["Salitre", "2311959"],
		["Santa Quitéria", "2312205"],
		["Santana do Acaraú", "2312007"],
		["Santana do Cariri", "2312106"],
		["São Benedito", "2312304"],
		["São Gonçalo do Amarante", "2312403"],
		["São João do Jaguaribe", "2312502"],
		["São Luís do Curu", "2312601"],
		["Senador Pompeu", "2312700"],
		["Senador Sá", "2312809"],
		["Sobral", "2312908"],
		["Solonópole", "2313005"],
		["Tabuleiro do Norte", "2313104"],
		["Tamboril", "2313203"],
		["Tarrafas", "2313252"],
		["Tauá", "2313302"],
		["Tejuçuoca", "2313351"],
		["Tianguá", "2313401"],
		["Trairi", "2313500"],
		["Tururu", "2313559"],
		["Ubajara", "2313609"],
		["Umari", "2313708"],
		["Umirim", "2313757"],
		["Uruburetama", "2313807"],
		["Uruoca", "2313906"],
		["Varjota", "2313955"],
		["Várzea Alegre", "2314003"],
		["Viçosa do Ceará", "2314102"],
	],
	DF: [["Brasília", "5300108"]],
	ES: [
		["Afonso Cláudio", "3200102"],
		["Água Doce do Norte", "3200169"],
		["Águia Branca", "3200136"],
		["Alegre", "3200201"],
		["Alfredo Chaves", "3200300"],
		["Alto Rio Novo", "3200359"],
		["Anchieta", "3200409"],
		["Apiacá", "3200508"],
		["Aracruz", "3200607"],
		["Atílio Vivácqua", "3200706"],
		["Baixo Guandu", "3200805"],
		["Barra de São Francisco", "3200904"],
		["Boa Esperança", "3201001"],
		["Bom Jesus do Norte", "3201100"],
		["Brejetuba", "3201159"],
		["Cachoeiro de Itapemirim", "3201209"],
		["Cariacica", "3201308"],
		["Castelo", "3201407"],
		["Colatina", "3201506"],
		["Conceição da Barra", "3201605"],
		["Conceição do Castelo", "3201704"],
		["Divino de São Lourenço", "3201803"],
		["Domingos Martins", "3201902"],
		["Dores do Rio Preto", "3202009"],
		["Ecoporanga", "3202108"],
		["Fundão", "3202207"],
		["Governador Lindenberg", "3202256"],
		["Guaçuí", "3202306"],
		["Guarapari", "3202405"],
		["Ibatiba", "3202454"],
		["Ibiraçu", "3202504"],
		["Ibitirama", "3202553"],
		["Iconha", "3202603"],
		["Irupi", "3202652"],
		["Itaguaçu", "3202702"],
		["Itapemirim", "3202801"],
		["Itarana", "3202900"],
		["Iúna", "3203007"],
		["Jaguaré", "3203056"],
		["Jerônimo Monteiro", "3203106"],
		["João Neiva", "3203130"],
		["Laranja da Terra", "3203163"],
		["Linhares", "3203205"],
		["Mantenópolis", "3203304"],
		["Marataízes", "3203320"],
		["Marechal Floriano", "3203346"],
		["Marilândia", "3203353"],
		["Mimoso do Sul", "3203403"],
		["Montanha", "3203502"],
		["Mucurici", "3203601"],
		["Muniz Freire", "3203700"],
		["Muqui", "3203809"],
		["Nova Venécia", "3203908"],
		["Pancas", "3204005"],
		["Pedro Canário", "3204054"],
		["Pinheiros", "3204104"],
		["Piúma", "3204203"],
		["Ponto Belo", "3204252"],
		["Presidente Kennedy", "3204302"],
		["Rio Bananal", "3204351"],
		["Rio Novo do Sul", "3204401"],
		["Santa Leopoldina", "3204500"],
		["Santa Maria de Jetibá", "3204559"],
		["Santa Teresa", "3204609"],
		["São Domingos do Norte", "3204658"],
		["São Gabriel da Palha", "3204708"],
		["São José do Calçado", "3204807"],
		["São Mateus", "3204906"],
		["São Roque do Canaã", "3204955"],
		["Serra", "3205002"],
		["Sooretama", "3205010"],
		["Vargem Alta", "3205036"],
		["Venda Nova do Imigrante", "3205069"],
		["Viana", "3205101"],
		["Vila Pavão", "3205150"],
		["Vila Valério", "3205176"],
		["Vila Velha", "3205200"],
		["Vitória", "3205309"],
	],
	GO: [
		["Abadia de Goiás", "5200050"],
		["Abadiânia", "5200100"],
		["Acreúna", "5200134"],
		["Adelândia", "5200159"],
		["Água Fria de Goiás", "5200175"],
		["Água Limpa", "5200209"],
		["Águas Lindas de Goiás", "5200258"],
		["Alexânia", "5200308"],
		["Aloândia", "5200506"],
		["Alto Horizonte", "5200555"],
		["Alto Paraíso de Goiás", "5200605"],
		["Alvorada do Norte", "5200803"],
		["Amaralina", "5200829"],
		["Americano do Brasil", "5200852"],
		["Amorinópolis", "5200902"],
		["Anápolis", "5201108"],
		["Anhanguera", "5201207"],
		["Anicuns", "5201306"],
		["Aparecida de Goiânia", "5201405"],
		["Aparecida do Rio Doce", "5201454"],
		["Aporé", "5201504"],
		["Araçu", "5201603"],
		["Aragarças", "5201702"],
		["Aragoiânia", "5201801"],
		["Araguapaz", "5202155"],
		["Arenópolis", "5202353"],
		["Aruanã", "5202502"],
		["Aurilândia", "5202601"],
		["Avelinópolis", "5202809"],
		["Baliza", "5203104"],
		["Barro Alto", "5203203"],
		["Bela Vista de Goiás", "5203302"],
		["Bom Jardim de Goiás", "5203401"],
		["Bom Jesus de Goiás", "5203500"],
		["Bonfinópolis", "5203559"],
		["Bonópolis", "5203575"],
		["Brazabrantes", "5203609"],
		["Britânia", "5203807"],
		["Buriti Alegre", "5203906"],
		["Buriti de Goiás", "5203939"],
		["Buritinópolis", "5203962"],
		["Cabeceiras", "5204003"],
		["Cachoeira Alta", "5204102"],
		["Cachoeira de Goiás", "5204201"],
		["Cachoeira Dourada", "5204250"],
		["Caçu", "5204300"],
		["Caiapônia", "5204409"],
		["Caldas Novas", "5204508"],
		["Caldazinha", "5204557"],
		["Campestre de Goiás", "5204607"],
		["Campinaçu", "5204656"],
		["Campinorte", "5204706"],
		["Campo Alegre de Goiás", "5204805"],
		["Campo Limpo de Goiás", "5204854"],
		["Campos Belos", "5204904"],
		["Campos Verdes", "5204953"],
		["Carmo do Rio Verde", "5205000"],
		["Castelândia", "5205059"],
		["Catalão", "5205109"],
		["Caturaí", "5205208"],
		["Cavalcante", "5205307"],
		["Ceres", "5205406"],
		["Cezarina", "5205455"],
		["Chapadão do Céu", "5205471"],
		["Cidade Ocidental", "5205497"],
		["Cocalzinho de Goiás", "5205513"],
		["Colinas do Sul", "5205521"],
		["Córrego do Ouro", "5205703"],
		["Corumbá de Goiás", "5205802"],
		["Corumbaíba", "5205901"],
		["Cristalina", "5206206"],
		["Cristianópolis", "5206305"],
		["Crixás", "5206404"],
		["Cromínia", "5206503"],
		["Cumari", "5206602"],
		["Damianópolis", "5206701"],
		["Damolândia", "5206800"],
		["Davinópolis", "5206909"],
		["Diorama", "5207105"],
		["Divinópolis de Goiás", "5208301"],
		["Doverlândia", "5207253"],
		["Edealina", "5207352"],
		["Edéia", "5207402"],
		["Estrela do Norte", "5207501"],
		["Faina", "5207535"],
		["Fazenda Nova", "5207600"],
		["Firminópolis", "5207808"],
		["Flores de Goiás", "5207907"],
		["Formosa", "5208004"],
		["Formoso", "5208103"],
		["Gameleira de Goiás", "5208152"],
		["Goianápolis", "5208400"],
		["Goiandira", "5208509"],
		["Goianésia", "5208608"],
		["Goiânia", "5208707"],
		["Goianira", "5208806"],
		["Goiás", "5208905"],
		["Goiatuba", "5209101"],
		["Gouvelândia", "5209150"],
		["Guapó", "5209200"],
		["Guaraíta", "5209291"],
		["Guarani de Goiás", "5209408"],
		["Guarinos", "5209457"],
		["Heitoraí", "5209606"],
		["Hidrolândia", "5209705"],
		["Hidrolina", "5209804"],
		["Iaciara", "5209903"],
		["Inaciolândia", "5209937"],
		["Indiara", "5209952"],
		["Inhumas", "5210000"],
		["Ipameri", "5210109"],
		["Ipiranga de Goiás", "5210158"],
		["Iporá", "5210208"],
		["Israelândia", "5210307"],
		["Itaberaí", "5210406"],
		["Itaguari", "5210562"],
		["Itaguaru", "5210604"],
		["Itajá", "5210802"],
		["Itapaci", "5210901"],
		["Itapirapuã", "5211008"],
		["Itapuranga", "5211206"],
		["Itarumã", "5211305"],
		["Itauçu", "5211404"],
		["Itumbiara", "5211503"],
		["Ivolândia", "5211602"],
		["Jandaia", "5211701"],
		["Jaraguá", "5211800"],
		["Jataí", "5211909"],
		["Jaupaci", "5212006"],
		["Jesúpolis", "5212055"],
		["Joviânia", "5212105"],
		["Jussara", "5212204"],
		["Lagoa Santa", "5212253"],
		["Leopoldo de Bulhões", "5212303"],
		["Luziânia", "5212501"],
		["Mairipotaba", "5212600"],
		["Mambaí", "5212709"],
		["Mara Rosa", "5212808"],
		["Marzagão", "5212907"],
		["Matrinchã", "5212956"],
		["Maurilândia", "5213004"],
		["Mimoso de Goiás", "5213053"],
		["Minaçu", "5213087"],
		["Mineiros", "5213103"],
		["Moiporá", "5213400"],
		["Monte Alegre de Goiás", "5213509"],
		["Montes Claros de Goiás", "5213707"],
		["Montividiu", "5213756"],
		["Montividiu do Norte", "5213772"],
		["Morrinhos", "5213806"],
		["Morro Agudo de Goiás", "5213855"],
		["Mossâmedes", "5213905"],
		["Mozarlândia", "5214002"],
		["Mundo Novo", "5214051"],
		["Mutunópolis", "5214101"],
		["Nazário", "5214408"],
		["Nerópolis", "5214507"],
		["Niquelândia", "5214606"],
		["Nova América", "5214705"],
		["Nova Aurora", "5214804"],
		["Nova Crixás", "5214838"],
		["Nova Glória", "5214861"],
		["Nova Iguaçu de Goiás", "5214879"],
		["Nova Roma", "5214903"],
		["Nova Veneza", "5215009"],
		["Novo Brasil", "5215207"],
		["Novo Gama", "5215231"],
		["Novo Planalto", "5215256"],
		["Orizona", "5215306"],
		["Ouro Verde de Goiás", "5215405"],
		["Ouvidor", "5215504"],
		["Padre Bernardo", "5215603"],
		["Palestina de Goiás", "5215652"],
		["Palmeiras de Goiás", "5215702"],
		["Palmelo", "5215801"],
		["Palminópolis", "5215900"],
		["Panamá", "5216007"],
		["Paranaiguara", "5216304"],
		["Paraúna", "5216403"],
		["Perolândia", "5216452"],
		["Petrolina de Goiás", "5216809"],
		["Pilar de Goiás", "5216908"],
		["Piracanjuba", "5217104"],
		["Piranhas", "5217203"],
		["Pirenópolis", "5217302"],
		["Pires do Rio", "5217401"],
		["Planaltina", "5217609"],
		["Pontalina", "5217708"],
		["Porangatu", "5218003"],
		["Porteirão", "5218052"],
		["Portelândia", "5218102"],
		["Posse", "5218300"],
		["Professor Jamil", "5218391"],
		["Quirinópolis", "5218508"],
		["Rialma", "5218607"],
		["Rianápolis", "5218706"],
		["Rio Quente", "5218789"],
		["Rio Verde", "5218805"],
		["Rubiataba", "5218904"],
		["Sanclerlândia", "5219001"],
		["Santa Bárbara de Goiás", "5219100"],
		["Santa Cruz de Goiás", "5219209"],
		["Santa Fé de Goiás", "5219258"],
		["Santa Helena de Goiás", "5219308"],
		["Santa Isabel", "5219357"],
		["Santa Rita do Araguaia", "5219407"],
		["Santa Rita do Novo Destino", "5219456"],
		["Santa Rosa de Goiás", "5219506"],
		["Santa Tereza de Goiás", "5219605"],
		["Santa Terezinha de Goiás", "5219704"],
		["Santo Antônio da Barra", "5219712"],
		["Santo Antônio de Goiás", "5219738"],
		["Santo Antônio do Descoberto", "5219753"],
		["São Domingos", "5219803"],
		["São Francisco de Goiás", "5219902"],
		["São João d'Aliança", "5220009"],
		["São João da Paraúna", "5220058"],
		["São Luís de Montes Belos", "5220108"],
		["São Luiz do Norte", "5220157"],
		["São Miguel do Araguaia", "5220207"],
		["São Miguel do Passa Quatro", "5220264"],
		["São Patrício", "5220280"],
		["São Simão", "5220405"],
		["Senador Canedo", "5220454"],
		["Serranópolis", "5220504"],
		["Silvânia", "5220603"],
		["Simolândia", "5220686"],
		["Sítio d'Abadia", "5220702"],
		["Taquaral de Goiás", "5221007"],
		["Teresina de Goiás", "5221080"],
		["Terezópolis de Goiás", "5221197"],
		["Três Ranchos", "5221304"],
		["Trindade", "5221403"],
		["Trombas", "5221452"],
		["Turvânia", "5221502"],
		["Turvelândia", "5221551"],
		["Uirapuru", "5221577"],
		["Uruaçu", "5221601"],
		["Uruana", "5221700"],
		["Urutaí", "5221809"],
		["Valparaíso de Goiás", "5221858"],
		["Varjão", "5221908"],
		["Vianópolis", "5222005"],
		["Vicentinópolis", "5222054"],
		["Vila Boa", "5222203"],
		["Vila Propício", "5222302"],
	],
	MA: [
		["Açailândia", "2100055"],
		["Afonso Cunha", "2100105"],
		["Água Doce do Maranhão", "2100154"],
		["Alcântara", "2100204"],
		["Aldeias Altas", "2100303"],
		["Altamira do Maranhão", "2100402"],
		["Alto Alegre do Maranhão", "2100436"],
		["Alto Alegre do Pindaré", "2100477"],
		["Alto Parnaíba", "2100501"],
		["Amapá do Maranhão", "2100550"],
		["Amarante do Maranhão", "2100600"],
		["Anajatuba", "2100709"],
		["Anapurus", "2100808"],
		["Apicum-Açu", "2100832"],
		["Araguanã", "2100873"],
		["Araioses", "2100907"],
		["Arame", "2100956"],
		["Arari", "2101004"],
		["Axixá", "2101103"],
		["Bacabal", "2101202"],
		["Bacabeira", "2101251"],
		["Bacuri", "2101301"],
		["Bacurituba", "2101350"],
		["Balsas", "2101400"],
		["Barão de Grajaú", "2101509"],
		["Barra do Corda", "2101608"],
		["Barreirinhas", "2101707"],
		["Bela Vista do Maranhão", "2101772"],
		["Belágua", "2101731"],
		["Benedito Leite", "2101806"],
		["Bequimão", "2101905"],
		["Bernardo do Mearim", "2101939"],
		["Boa Vista do Gurupi", "2101970"],
		["Bom Jardim", "2102002"],
		["Bom Jesus das Selvas", "2102036"],
		["Bom Lugar", "2102077"],
		["Brejo", "2102101"],
		["Brejo de Areia", "2102150"],
		["Buriti", "2102200"],
		["Buriti Bravo", "2102309"],
		["Buriticupu", "2102325"],
		["Buritirana", "2102358"],
		["Cachoeira Grande", "2102374"],
		["Cajapió", "2102408"],
		["Cajari", "2102507"],
		["Campestre do Maranhão", "2102556"],
		["Cândido Mendes", "2102606"],
		["Cantanhede", "2102705"],
		["Capinzal do Norte", "2102754"],
		["Carolina", "2102804"],
		["Carutapera", "2102903"],
		["Caxias", "2103000"],
		["Cedral", "2103109"],
		["Central do Maranhão", "2103125"],
		["Centro do Guilherme", "2103158"],
		["Centro Novo do Maranhão", "2103174"],
		["Chapadinha", "2103208"],
		["Cidelândia", "2103257"],
		["Codó", "2103307"],
		["Coelho Neto", "2103406"],
		["Colinas", "2103505"],
		["Conceição do Lago-Açu", "2103554"],
		["Coroatá", "2103604"],
		["Cururupu", "2103703"],
		["Davinópolis", "2103752"],
		["Dom Pedro", "2103802"],
		["Duque Bacelar", "2103901"],
		["Esperantinópolis", "2104008"],
		["Estreito", "2104057"],
		["Feira Nova do Maranhão", "2104073"],
		["Fernando Falcão", "2104081"],
		["Formosa da Serra Negra", "2104099"],
		["Fortaleza dos Nogueiras", "2104107"],
		["Fortuna", "2104206"],
		["Godofredo Viana", "2104305"],
		["Gonçalves Dias", "2104404"],
		["Governador Archer", "2104503"],
		["Governador Edison Lobão", "2104552"],
		["Governador Eugênio Barros", "2104602"],
		["Governador Luiz Rocha", "2104628"],
		["Governador Newton Bello", "2104651"],
		["Governador Nunes Freire", "2104677"],
		["Graça Aranha", "2104701"],
		["Grajaú", "2104800"],
		["Guimarães", "2104909"],
		["Humberto de Campos", "2105005"],
		["Icatu", "2105104"],
		["Igarapé do Meio", "2105153"],
		["Igarapé Grande", "2105203"],
		["Imperatriz", "2105302"],
		["Itaipava do Grajaú", "2105351"],
		["Itapecuru Mirim", "2105401"],
		["Itinga do Maranhão", "2105427"],
		["Jatobá", "2105450"],
		["Jenipapo dos Vieiras", "2105476"],
		["João Lisboa", "2105500"],
		["Joselândia", "2105609"],
		["Junco do Maranhão", "2105658"],
		["Lago da Pedra", "2105708"],
		["Lago do Junco", "2105807"],
		["Lago dos Rodrigues", "2105948"],
		["Lago Verde", "2105906"],
		["Lagoa do Mato", "2105922"],
		["Lagoa Grande do Maranhão", "2105963"],
		["Lajeado Novo", "2105989"],
		["Lima Campos", "2106003"],
		["Loreto", "2106102"],
		["Luís Domingues", "2106201"],
		["Magalhães de Almeida", "2106300"],
		["Maracaçumé", "2106326"],
		["Marajá do Sena", "2106359"],
		["Maranhãozinho", "2106375"],
		["Mata Roma", "2106409"],
		["Matinha", "2106508"],
		["Matões", "2106607"],
		["Matões do Norte", "2106631"],
		["Milagres do Maranhão", "2106672"],
		["Mirador", "2106706"],
		["Miranda do Norte", "2106755"],
		["Mirinzal", "2106805"],
		["Monção", "2106904"],
		["Montes Altos", "2107001"],
		["Morros", "2107100"],
		["Nina Rodrigues", "2107209"],
		["Nova Colinas", "2107258"],
		["Nova Iorque", "2107308"],
		["Nova Olinda do Maranhão", "2107357"],
		["Olho d'Água das Cunhãs", "2107407"],
		["Olinda Nova do Maranhão", "2107456"],
		["Paço do Lumiar", "2107506"],
		["Palmeirândia", "2107605"],
		["Paraibano", "2107704"],
		["Parnarama", "2107803"],
		["Passagem Franca", "2107902"],
		["Pastos Bons", "2108009"],
		["Paulino Neves", "2108058"],
		["Paulo Ramos", "2108108"],
		["Pedreiras", "2108207"],
		["Pedro do Rosário", "2108256"],
		["Penalva", "2108306"],
		["Peri Mirim", "2108405"],
		["Peritoró", "2108454"],
		["Pindaré-Mirim", "2108504"],
		["Pinheiro", "2108603"],
		["Pio XII", "2108702"],
		["Pirapemas", "2108801"],
		["Poção de Pedras", "2108900"],
		["Porto Franco", "2109007"],
		["Porto Rico do Maranhão", "2109056"],
		["Presidente Dutra", "2109106"],
		["Presidente Juscelino", "2109205"],
		["Presidente Médici", "2109239"],
		["Presidente Sarney", "2109270"],
		["Presidente Vargas", "2109304"],
		["Primeira Cruz", "2109403"],
		["Raposa", "2109452"],
		["Riachão", "2109502"],
		["Ribamar Fiquene", "2109551"],
		["Rosário", "2109601"],
		["Sambaíba", "2109700"],
		["Santa Filomena do Maranhão", "2109759"],
		["Santa Helena", "2109809"],
		["Santa Inês", "2109908"],
		["Santa Luzia", "2110005"],
		["Santa Luzia do Paruá", "2110039"],
		["Santa Quitéria do Maranhão", "2110104"],
		["Santa Rita", "2110203"],
		["Santana do Maranhão", "2110237"],
		["Santo Amaro do Maranhão", "2110278"],
		["Santo Antônio dos Lopes", "2110302"],
		["São Benedito do Rio Preto", "2110401"],
		["São Bento", "2110500"],
		["São Bernardo", "2110609"],
		["São Domingos do Azeitão", "2110658"],
		["São Domingos do Maranhão", "2110708"],
		["São Félix de Balsas", "2110807"],
		["São Francisco do Brejão", "2110856"],
		["São Francisco do Maranhão", "2110906"],
		["São João Batista", "2111003"],
		["São João do Carú", "2111029"],
		["São João do Paraíso", "2111052"],
		["São João do Soter", "2111078"],
		["São João dos Patos", "2111102"],
		["São José de Ribamar", "2111201"],
		["São José dos Basílios", "2111250"],
		["São Luís", "2111300"],
		["São Luís Gonzaga do Maranhão", "2111409"],
		["São Mateus do Maranhão", "2111508"],
		["São Pedro da Água Branca", "2111532"],
		["São Pedro dos Crentes", "2111573"],
		["São Raimundo das Mangabeiras", "2111607"],
		["São Raimundo do Doca Bezerra", "2111631"],
		["São Roberto", "2111672"],
		["São Vicente Ferrer", "2111706"],
		["Satubinha", "2111722"],
		["Senador Alexandre Costa", "2111748"],
		["Senador La Rocque", "2111763"],
		["Serrano do Maranhão", "2111789"],
		["Sítio Novo", "2111805"],
		["Sucupira do Norte", "2111904"],
		["Sucupira do Riachão", "2111953"],
		["Tasso Fragoso", "2112001"],
		["Timbiras", "2112100"],
		["Timon", "2112209"],
		["Trizidela do Vale", "2112233"],
		["Tufilândia", "2112274"],
		["Tuntum", "2112308"],
		["Turiaçu", "2112407"],
		["Turilândia", "2112456"],
		["Tutóia", "2112506"],
		["Urbano Santos", "2112605"],
		["Vargem Grande", "2112704"],
		["Viana", "2112803"],
		["Vila Nova dos Martírios", "2112852"],
		["Vitória do Mearim", "2112902"],
		["Vitorino Freire", "2113009"],
		["Zé Doca", "2114007"],
	],
	MG: [
		["Abadia dos Dourados", "3100104"],
		["Abaeté", "3100203"],
		["Abre Campo", "3100302"],
		["Acaiaca", "3100401"],
		["Açucena", "3100500"],
		["Água Boa", "3100609"],
		["Água Comprida", "3100708"],
		["Aguanil", "3100807"],
		["Águas Formosas", "3100906"],
		["Águas Vermelhas", "3101003"],
		["Aimorés", "3101102"],
		["Aiuruoca", "3101201"],
		["Alagoa", "3101300"],
		["Albertina", "3101409"],
		["Além Paraíba", "3101508"],
		["Alfenas", "3101607"],
		["Alfredo Vasconcelos", "3101631"],
		["Almenara", "3101706"],
		["Alpercata", "3101805"],
		["Alpinópolis", "3101904"],
		["Alterosa", "3102001"],
		["Alto Caparaó", "3102050"],
		["Alto Jequitibá", "3153509"],
		["Alto Rio Doce", "3102100"],
		["Alvarenga", "3102209"],
		["Alvinópolis", "3102308"],
		["Alvorada de Minas", "3102407"],
		["Amparo do Serra", "3102506"],
		["Andradas", "3102605"],
		["Andrelândia", "3102803"],
		["Angelândia", "3102852"],
		["Antônio Carlos", "3102902"],
		["Antônio Dias", "3103009"],
		["Antônio Prado de Minas", "3103108"],
		["Araçaí", "3103207"],
		["Aracitaba", "3103306"],
		["Araçuaí", "3103405"],
		["Araguari", "3103504"],
		["Arantina", "3103603"],
		["Araponga", "3103702"],
		["Araporã", "3103751"],
		["Arapuá", "3103801"],
		["Araújos", "3103900"],
		["Araxá", "3104007"],
		["Arceburgo", "3104106"],
		["Arcos", "3104205"],
		["Areado", "3104304"],
		["Argirita", "3104403"],
		["Aricanduva", "3104452"],
		["Arinos", "3104502"],
		["Astolfo Dutra", "3104601"],
		["Ataléia", "3104700"],
		["Augusto de Lima", "3104809"],
		["Baependi", "3104908"],
		["Baldim", "3105004"],
		["Bambuí", "3105103"],
		["Bandeira", "3105202"],
		["Bandeira do Sul", "3105301"],
		["Barão de Cocais", "3105400"],
		["Barão do Monte Alto", "3105509"],
		["Barbacena", "3105608"],
		["Barra Longa", "3105707"],
		["Barroso", "3105905"],
		["Bela Vista de Minas", "3106002"],
		["Belmiro Braga", "3106101"],
		["Belo Horizonte", "3106200"],
		["Belo Oriente", "3106309"],
		["Belo Vale", "3106408"],
		["Berilo", "3106507"],
		["Berizal", "3106655"],
		["Bertópolis", "3106606"],
		["Betim", "3106705"],
		["Bias Fortes", "3106804"],
		["Bicas", "3106903"],
		["Biquinhas", "3107000"],
		["Boa Esperança", "3107109"],
		["Bocaina de Minas", "3107208"],
		["Bocaiúva", "3107307"],
		["Bom Despacho", "3107406"],
		["Bom Jardim de Minas", "3107505"],
		["Bom Jesus da Penha", "3107604"],
		["Bom Jesus do Amparo", "3107703"],
		["Bom Jesus do Galho", "3107802"],
		["Bom Repouso", "3107901"],
		["Bom Sucesso", "3108008"],
		["Bonfim", "3108107"],
		["Bonfinópolis de Minas", "3108206"],
		["Bonito de Minas", "3108255"],
		["Borda da Mata", "3108305"],
		["Botelhos", "3108404"],
		["Botumirim", "3108503"],
		["Brás Pires", "3108701"],
		["Brasilândia de Minas", "3108552"],
		["Brasília de Minas", "3108602"],
		["Braúnas", "3108800"],
		["Brazópolis", "3108909"],
		["Brumadinho", "3109006"],
		["Bueno Brandão", "3109105"],
		["Buenópolis", "3109204"],
		["Bugre", "3109253"],
		["Buritis", "3109303"],
		["Buritizeiro", "3109402"],
		["Cabeceira Grande", "3109451"],
		["Cabo Verde", "3109501"],
		["Cachoeira da Prata", "3109600"],
		["Cachoeira de Minas", "3109709"],
		["Cachoeira de Pajeú", "3102704"],
		["Cachoeira Dourada", "3109808"],
		["Caetanópolis", "3109907"],
		["Caeté", "3110004"],
		["Caiana", "3110103"],
		["Cajuri", "3110202"],
		["Caldas", "3110301"],
		["Camacho", "3110400"],
		["Camanducaia", "3110509"],
		["Cambuí", "3110608"],
		["Cambuquira", "3110707"],
		["Campanário", "3110806"],
		["Campanha", "3110905"],
		["Campestre", "3111002"],
		["Campina Verde", "3111101"],
		["Campo Azul", "3111150"],
		["Campo Belo", "3111200"],
		["Campo do Meio", "3111309"],
		["Campo Florido", "3111408"],
		["Campos Altos", "3111507"],
		["Campos Gerais", "3111606"],
		["Cana Verde", "3111903"],
		["Canaã", "3111705"],
		["Canápolis", "3111804"],
		["Candeias", "3112000"],
		["Cantagalo", "3112059"],
		["Caparaó", "3112109"],
		["Capela Nova", "3112208"],
		["Capelinha", "3112307"],
		["Capetinga", "3112406"],
		["Capim Branco", "3112505"],
		["Capinópolis", "3112604"],
		["Capitão Andrade", "3112653"],
		["Capitão Enéas", "3112703"],
		["Capitólio", "3112802"],
		["Caputira", "3112901"],
		["Caraí", "3113008"],
		["Caranaíba", "3113107"],
		["Carandaí", "3113206"],
		["Carangola", "3113305"],
		["Caratinga", "3113404"],
		["Carbonita", "3113503"],
		["Careaçu", "3113602"],
		["Carlos Chagas", "3113701"],
		["Carmésia", "3113800"],
		["Carmo da Cachoeira", "3113909"],
		["Carmo da Mata", "3114006"],
		["Carmo de Minas", "3114105"],
		["Carmo do Cajuru", "3114204"],
		["Carmo do Paranaíba", "3114303"],
		["Carmo do Rio Claro", "3114402"],
		["Carmópolis de Minas", "3114501"],
		["Carneirinho", "3114550"],
		["Carrancas", "3114600"],
		["Carvalhópolis", "3114709"],
		["Carvalhos", "3114808"],
		["Casa Grande", "3114907"],
		["Cascalho Rico", "3115003"],
		["Cássia", "3115102"],
		["Cataguases", "3115300"],
		["Catas Altas", "3115359"],
		["Catas Altas da Noruega", "3115409"],
		["Catuji", "3115458"],
		["Catuti", "3115474"],
		["Caxambu", "3115508"],
		["Cedro do Abaeté", "3115607"],
		["Central de Minas", "3115706"],
		["Centralina", "3115805"],
		["Chácara", "3115904"],
		["Chalé", "3116001"],
		["Chapada do Norte", "3116100"],
		["Chapada Gaúcha", "3116159"],
		["Chiador", "3116209"],
		["Cipotânea", "3116308"],
		["Claraval", "3116407"],
		["Claro dos Poções", "3116506"],
		["Cláudio", "3116605"],
		["Coimbra", "3116704"],
		["Coluna", "3116803"],
		["Comendador Gomes", "3116902"],
		["Comercinho", "3117009"],
		["Conceição da Aparecida", "3117108"],
		["Conceição da Barra de Minas", "3115201"],
		["Conceição das Alagoas", "3117306"],
		["Conceição das Pedras", "3117207"],
		["Conceição de Ipanema", "3117405"],
		["Conceição do Mato Dentro", "3117504"],
		["Conceição do Pará", "3117603"],
		["Conceição do Rio Verde", "3117702"],
		["Conceição dos Ouros", "3117801"],
		["Cônego Marinho", "3117836"],
		["Confins", "3117876"],
		["Congonhal", "3117900"],
		["Congonhas", "3118007"],
		["Congonhas do Norte", "3118106"],
		["Conquista", "3118205"],
		["Conselheiro Lafaiete", "3118304"],
		["Conselheiro Pena", "3118403"],
		["Consolação", "3118502"],
		["Contagem", "3118601"],
		["Coqueiral", "3118700"],
		["Coração de Jesus", "3118809"],
		["Cordisburgo", "3118908"],
		["Cordislândia", "3119005"],
		["Corinto", "3119104"],
		["Coroaci", "3119203"],
		["Coromandel", "3119302"],
		["Coronel Fabriciano", "3119401"],
		["Coronel Murta", "3119500"],
		["Coronel Pacheco", "3119609"],
		["Coronel Xavier Chaves", "3119708"],
		["Córrego Danta", "3119807"],
		["Córrego do Bom Jesus", "3119906"],
		["Córrego Fundo", "3119955"],
		["Córrego Novo", "3120003"],
		["Couto de Magalhães de Minas", "3120102"],
		["Crisólita", "3120151"],
		["Cristais", "3120201"],
		["Cristália", "3120300"],
		["Cristiano Otoni", "3120409"],
		["Cristina", "3120508"],
		["Crucilândia", "3120607"],
		["Cruzeiro da Fortaleza", "3120706"],
		["Cruzília", "3120805"],
		["Cuparaque", "3120839"],
		["Curral de Dentro", "3120870"],
		["Curvelo", "3120904"],
		["Datas", "3121001"],
		["Delfim Moreira", "3121100"],
		["Delfinópolis", "3121209"],
		["Delta", "3121258"],
		["Descoberto", "3121308"],
		["Desterro de Entre Rios", "3121407"],
		["Desterro do Melo", "3121506"],
		["Diamantina", "3121605"],
		["Diogo de Vasconcelos", "3121704"],
		["Dionísio", "3121803"],
		["Divinésia", "3121902"],
		["Divino", "3122009"],
		["Divino das Laranjeiras", "3122108"],
		["Divinolândia de Minas", "3122207"],
		["Divinópolis", "3122306"],
		["Divisa Alegre", "3122355"],
		["Divisa Nova", "3122405"],
		["Divisópolis", "3122454"],
		["Dom Bosco", "3122470"],
		["Dom Cavati", "3122504"],
		["Dom Joaquim", "3122603"],
		["Dom Silvério", "3122702"],
		["Dom Viçoso", "3122801"],
		["Dona Euzébia", "3122900"],
		["Dores de Campos", "3123007"],
		["Dores de Guanhães", "3123106"],
		["Dores do Indaiá", "3123205"],
		["Dores do Turvo", "3123304"],
		["Doresópolis", "3123403"],
		["Douradoquara", "3123502"],
		["Durandé", "3123528"],
		["Elói Mendes", "3123601"],
		["Engenheiro Caldas", "3123700"],
		["Engenheiro Navarro", "3123809"],
		["Entre Folhas", "3123858"],
		["Entre Rios de Minas", "3123908"],
		["Ervália", "3124005"],
		["Esmeraldas", "3124104"],
		["Espera Feliz", "3124203"],
		["Espinosa", "3124302"],
		["Espírito Santo do Dourado", "3124401"],
		["Estiva", "3124500"],
		["Estrela Dalva", "3124609"],
		["Estrela do Indaiá", "3124708"],
		["Estrela do Sul", "3124807"],
		["Eugenópolis", "3124906"],
		["Ewbank da Câmara", "3125002"],
		["Extrema", "3125101"],
		["Fama", "3125200"],
		["Faria Lemos", "3125309"],
		["Felício dos Santos", "3125408"],
		["Felisburgo", "3125606"],
		["Felixlândia", "3125705"],
		["Fernandes Tourinho", "3125804"],
		["Ferros", "3125903"],
		["Fervedouro", "3125952"],
		["Florestal", "3126000"],
		["Formiga", "3126109"],
		["Formoso", "3126208"],
		["Fortaleza de Minas", "3126307"],
		["Fortuna de Minas", "3126406"],
		["Francisco Badaró", "3126505"],
		["Francisco Dumont", "3126604"],
		["Francisco Sá", "3126703"],
		["Franciscópolis", "3126752"],
		["Frei Gaspar", "3126802"],
		["Frei Inocêncio", "3126901"],
		["Frei Lagonegro", "3126950"],
		["Fronteira", "3127008"],
		["Fronteira dos Vales", "3127057"],
		["Fruta de Leite", "3127073"],
		["Frutal", "3127107"],
		["Funilândia", "3127206"],
		["Galiléia", "3127305"],
		["Gameleiras", "3127339"],
		["Glaucilândia", "3127354"],
		["Goiabeira", "3127370"],
		["Goianá", "3127388"],
		["Gonçalves", "3127404"],
		["Gonzaga", "3127503"],
		["Gouveia", "3127602"],
		["Governador Valadares", "3127701"],
		["Grão Mogol", "3127800"],
		["Grupiara", "3127909"],
		["Guanhães", "3128006"],
		["Guapé", "3128105"],
		["Guaraciaba", "3128204"],
		["Guaraciama", "3128253"],
		["Guaranésia", "3128303"],
		["Guarani", "3128402"],
		["Guarará", "3128501"],
		["Guarda-Mor", "3128600"],
		["Guaxupé", "3128709"],
		["Guidoval", "3128808"],
		["Guimarânia", "3128907"],
		["Guiricema", "3129004"],
		["Gurinhatã", "3129103"],
		["Heliodora", "3129202"],
		["Iapu", "3129301"],
		["Ibertioga", "3129400"],
		["Ibiá", "3129509"],
		["Ibiaí", "3129608"],
		["Ibiracatu", "3129657"],
		["Ibiraci", "3129707"],
		["Ibirité", "3129806"],
		["Ibitiúra de Minas", "3129905"],
		["Ibituruna", "3130002"],
		["Icaraí de Minas", "3130051"],
		["Igarapé", "3130101"],
		["Igaratinga", "3130200"],
		["Iguatama", "3130309"],
		["Ijaci", "3130408"],
		["Ilicínea", "3130507"],
		["Imbé de Minas", "3130556"],
		["Inconfidentes", "3130606"],
		["Indaiabira", "3130655"],
		["Indianópolis", "3130705"],
		["Ingaí", "3130804"],
		["Inhapim", "3130903"],
		["Inhaúma", "3131000"],
		["Inimutaba", "3131109"],
		["Ipaba", "3131158"],
		["Ipanema", "3131208"],
		["Ipatinga", "3131307"],
		["Ipiaçu", "3131406"],
		["Ipuiúna", "3131505"],
		["Iraí de Minas", "3131604"],
		["Itabira", "3131703"],
		["Itabirinha", "3131802"],
		["Itabirito", "3131901"],
		["Itacambira", "3132008"],
		["Itacarambi", "3132107"],
		["Itaguara", "3132206"],
		["Itaipé", "3132305"],
		["Itajubá", "3132404"],
		["Itamarandiba", "3132503"],
		["Itamarati de Minas", "3132602"],
		["Itambacuri", "3132701"],
		["Itambé do Mato Dentro", "3132800"],
		["Itamogi", "3132909"],
		["Itamonte", "3133006"],
		["Itanhandu", "3133105"],
		["Itanhomi", "3133204"],
		["Itaobim", "3133303"],
		["Itapagipe", "3133402"],
		["Itapecerica", "3133501"],
		["Itapeva", "3133600"],
		["Itatiaiuçu", "3133709"],
		["Itaú de Minas", "3133758"],
		["Itaúna", "3133808"],
		["Itaverava", "3133907"],
		["Itinga", "3134004"],
		["Itueta", "3134103"],
		["Ituiutaba", "3134202"],
		["Itumirim", "3134301"],
		["Iturama", "3134400"],
		["Itutinga", "3134509"],
		["Jaboticatubas", "3134608"],
		["Jacinto", "3134707"],
		["Jacuí", "3134806"],
		["Jacutinga", "3134905"],
		["Jaguaraçu", "3135001"],
		["Jaíba", "3135050"],
		["Jampruca", "3135076"],
		["Janaúba", "3135100"],
		["Januária", "3135209"],
		["Japaraíba", "3135308"],
		["Japonvar", "3135357"],
		["Jeceaba", "3135407"],
		["Jenipapo de Minas", "3135456"],
		["Jequeri", "3135506"],
		["Jequitaí", "3135605"],
		["Jequitibá", "3135704"],
		["Jequitinhonha", "3135803"],
		["Jesuânia", "3135902"],
		["Joaíma", "3136009"],
		["Joanésia", "3136108"],
		["João Monlevade", "3136207"],
		["João Pinheiro", "3136306"],
		["Joaquim Felício", "3136405"],
		["Jordânia", "3136504"],
		["José Gonçalves de Minas", "3136520"],
		["José Raydan", "3136553"],
		["Josenópolis", "3136579"],
		["Juatuba", "3136652"],
		["Juiz de Fora", "3136702"],
		["Juramento", "3136801"],
		["Juruaia", "3136900"],
		["Juvenília", "3136959"],
		["Ladainha", "3137007"],
		["Lagamar", "3137106"],
		["Lagoa da Prata", "3137205"],
		["Lagoa dos Patos", "3137304"],
		["Lagoa Dourada", "3137403"],
		["Lagoa Formosa", "3137502"],
		["Lagoa Grande", "3137536"],
		["Lagoa Santa", "3137601"],
		["Lajinha", "3137700"],
		["Lambari", "3137809"],
		["Lamim", "3137908"],
		["Laranjal", "3138005"],
		["Lassance", "3138104"],
		["Lavras", "3138203"],
		["Leandro Ferreira", "3138302"],
		["Leme do Prado", "3138351"],
		["Leopoldina", "3138401"],
		["Liberdade", "3138500"],
		["Lima Duarte", "3138609"],
		["Limeira do Oeste", "3138625"],
		["Lontra", "3138658"],
		["Luisburgo", "3138674"],
		["Luislândia", "3138682"],
		["Luminárias", "3138708"],
		["Luz", "3138807"],
		["Machacalis", "3138906"],
		["Machado", "3139003"],
		["Madre de Deus de Minas", "3139102"],
		["Malacacheta", "3139201"],
		["Mamonas", "3139250"],
		["Manga", "3139300"],
		["Manhuaçu", "3139409"],
		["Manhumirim", "3139508"],
		["Mantena", "3139607"],
		["Mar de Espanha", "3139805"],
		["Maravilhas", "3139706"],
		["Maria da Fé", "3139904"],
		["Mariana", "3140001"],
		["Marilac", "3140100"],
		["Mário Campos", "3140159"],
		["Maripá de Minas", "3140209"],
		["Marliéria", "3140308"],
		["Marmelópolis", "3140407"],
		["Martinho Campos", "3140506"],
		["Martins Soares", "3140530"],
		["Mata Verde", "3140555"],
		["Materlândia", "3140605"],
		["Mateus Leme", "3140704"],
		["Mathias Lobato", "3171501"],
		["Matias Barbosa", "3140803"],
		["Matias Cardoso", "3140852"],
		["Matipó", "3140902"],
		["Mato Verde", "3141009"],
		["Matozinhos", "3141108"],
		["Matutina", "3141207"],
		["Medeiros", "3141306"],
		["Medina", "3141405"],
		["Mendes Pimentel", "3141504"],
		["Mercês", "3141603"],
		["Mesquita", "3141702"],
		["Minas Novas", "3141801"],
		["Minduri", "3141900"],
		["Mirabela", "3142007"],
		["Miradouro", "3142106"],
		["Miraí", "3142205"],
		["Miravânia", "3142254"],
		["Moeda", "3142304"],
		["Moema", "3142403"],
		["Monjolos", "3142502"],
		["Monsenhor Paulo", "3142601"],
		["Montalvânia", "3142700"],
		["Monte Alegre de Minas", "3142809"],
		["Monte Azul", "3142908"],
		["Monte Belo", "3143005"],
		["Monte Carmelo", "3143104"],
		["Monte Formoso", "3143153"],
		["Monte Santo de Minas", "3143203"],
		["Monte Sião", "3143401"],
		["Montes Claros", "3143302"],
		["Montezuma", "3143450"],
		["Morada Nova de Minas", "3143500"],
		["Morro da Garça", "3143609"],
		["Morro do Pilar", "3143708"],
		["Munhoz", "3143807"],
		["Muriaé", "3143906"],
		["Mutum", "3144003"],
		["Muzambinho", "3144102"],
		["Nacip Raydan", "3144201"],
		["Nanuque", "3144300"],
		["Naque", "3144359"],
		["Natalândia", "3144375"],
		["Natércia", "3144409"],
		["Nazareno", "3144508"],
		["Nepomuceno", "3144607"],
		["Ninheira", "3144656"],
		["Nova Belém", "3144672"],
		["Nova Era", "3144706"],
		["Nova Lima", "3144805"],
		["Nova Módica", "3144904"],
		["Nova Ponte", "3145000"],
		["Nova Porteirinha", "3145059"],
		["Nova Resende", "3145109"],
		["Nova Serrana", "3145208"],
		["Nova União", "3136603"],
		["Novo Cruzeiro", "3145307"],
		["Novo Oriente de Minas", "3145356"],
		["Novorizonte", "3145372"],
		["Olaria", "3145406"],
		["Olhos-d'Água", "3145455"],
		["Olímpio Noronha", "3145505"],
		["Oliveira", "3145604"],
		["Oliveira Fortes", "3145703"],
		["Onça de Pitangui", "3145802"],
		["Oratórios", "3145851"],
		["Orizânia", "3145877"],
		["Ouro Branco", "3145901"],
		["Ouro Fino", "3146008"],
		["Ouro Preto", "3146107"],
		["Ouro Verde de Minas", "3146206"],
		["Padre Carvalho", "3146255"],
		["Padre Paraíso", "3146305"],
		["Pai Pedro", "3146552"],
		["Paineiras", "3146404"],
		["Pains", "3146503"],
		["Paiva", "3146602"],
		["Palma", "3146701"],
		["Palmópolis", "3146750"],
		["Papagaios", "3146909"],
		["Pará de Minas", "3147105"],
		["Paracatu", "3147006"],
		["Paraguaçu", "3147204"],
		["Paraisópolis", "3147303"],
		["Paraopeba", "3147402"],
		["Passa Quatro", "3147600"],
		["Passa Tempo", "3147709"],
		["Passa Vinte", "3147808"],
		["Passabém", "3147501"],
		["Passos", "3147907"],
		["Patis", "3147956"],
		["Patos de Minas", "3148004"],
		["Patrocínio", "3148103"],
		["Patrocínio do Muriaé", "3148202"],
		["Paula Cândido", "3148301"],
		["Paulistas", "3148400"],
		["Pavão", "3148509"],
		["Peçanha", "3148608"],
		["Pedra Azul", "3148707"],
		["Pedra Bonita", "3148756"],
		["Pedra do Anta", "3148806"],
		["Pedra do Indaiá", "3148905"],
		["Pedra Dourada", "3149002"],
		["Pedralva", "3149101"],
		["Pedras de Maria da Cruz", "3149150"],
		["Pedrinópolis", "3149200"],
		["Pedro Leopoldo", "3149309"],
		["Pedro Teixeira", "3149408"],
		["Pequeri", "3149507"],
		["Pequi", "3149606"],
		["Perdigão", "3149705"],
		["Perdizes", "3149804"],
		["Perdões", "3149903"],
		["Periquito", "3149952"],
		["Pescador", "3150000"],
		["Piau", "3150109"],
		["Piedade de Caratinga", "3150158"],
		["Piedade de Ponte Nova", "3150208"],
		["Piedade do Rio Grande", "3150307"],
		["Piedade dos Gerais", "3150406"],
		["Pimenta", "3150505"],
		["Pingo-d'Água", "3150539"],
		["Pintópolis", "3150570"],
		["Piracema", "3150604"],
		["Pirajuba", "3150703"],
		["Piranga", "3150802"],
		["Piranguçu", "3150901"],
		["Piranguinho", "3151008"],
		["Pirapetinga", "3151107"],
		["Pirapora", "3151206"],
		["Piraúba", "3151305"],
		["Pitangui", "3151404"],
		["Piumhi", "3151503"],
		["Planura", "3151602"],
		["Poço Fundo", "3151701"],
		["Poços de Caldas", "3151800"],
		["Pocrane", "3151909"],
		["Pompéu", "3152006"],
		["Ponte Nova", "3152105"],
		["Ponto Chique", "3152131"],
		["Ponto dos Volantes", "3152170"],
		["Porteirinha", "3152204"],
		["Porto Firme", "3152303"],
		["Poté", "3152402"],
		["Pouso Alegre", "3152501"],
		["Pouso Alto", "3152600"],
		["Prados", "3152709"],
		["Prata", "3152808"],
		["Pratápolis", "3152907"],
		["Pratinha", "3153004"],
		["Presidente Bernardes", "3153103"],
		["Presidente Juscelino", "3153202"],
		["Presidente Kubitschek", "3153301"],
		["Presidente Olegário", "3153400"],
		["Prudente de Morais", "3153608"],
		["Quartel Geral", "3153707"],
		["Queluzito", "3153806"],
		["Raposos", "3153905"],
		["Raul Soares", "3154002"],
		["Recreio", "3154101"],
		["Reduto", "3154150"],
		["Resende Costa", "3154200"],
		["Resplendor", "3154309"],
		["Ressaquinha", "3154408"],
		["Riachinho", "3154457"],
		["Riacho dos Machados", "3154507"],
		["Ribeirão das Neves", "3154606"],
		["Ribeirão Vermelho", "3154705"],
		["Rio Acima", "3154804"],
		["Rio Casca", "3154903"],
		["Rio do Prado", "3155108"],
		["Rio Doce", "3155009"],
		["Rio Espera", "3155207"],
		["Rio Manso", "3155306"],
		["Rio Novo", "3155405"],
		["Rio Paranaíba", "3155504"],
		["Rio Pardo de Minas", "3155603"],
		["Rio Piracicaba", "3155702"],
		["Rio Pomba", "3155801"],
		["Rio Preto", "3155900"],
		["Rio Vermelho", "3156007"],
		["Ritápolis", "3156106"],
		["Rochedo de Minas", "3156205"],
		["Rodeiro", "3156304"],
		["Romaria", "3156403"],
		["Rosário da Limeira", "3156452"],
		["Rubelita", "3156502"],
		["Rubim", "3156601"],
		["Sabará", "3156700"],
		["Sabinópolis", "3156809"],
		["Sacramento", "3156908"],
		["Salinas", "3157005"],
		["Salto da Divisa", "3157104"],
		["Santa Bárbara", "3157203"],
		["Santa Bárbara do Leste", "3157252"],
		["Santa Bárbara do Monte Verde", "3157278"],
		["Santa Bárbara do Tugúrio", "3157302"],
		["Santa Cruz de Minas", "3157336"],
		["Santa Cruz de Salinas", "3157377"],
		["Santa Cruz do Escalvado", "3157401"],
		["Santa Efigênia de Minas", "3157500"],
		["Santa Fé de Minas", "3157609"],
		["Santa Helena de Minas", "3157658"],
		["Santa Juliana", "3157708"],
		["Santa Luzia", "3157807"],
		["Santa Margarida", "3157906"],
		["Santa Maria de Itabira", "3158003"],
		["Santa Maria do Salto", "3158102"],
		["Santa Maria do Suaçuí", "3158201"],
		["Santa Rita de Caldas", "3159209"],
		["Santa Rita de Ibitipoca", "3159407"],
		["Santa Rita de Jacutinga", "3159308"],
		["Santa Rita de Minas", "3159357"],
		["Santa Rita do Itueto", "3159506"],
		["Santa Rita do Sapucaí", "3159605"],
		["Santa Rosa da Serra", "3159704"],
		["Santa Vitória", "3159803"],
		["Santana da Vargem", "3158300"],
		["Santana de Cataguases", "3158409"],
		["Santana de Pirapama", "3158508"],
		["Santana do Deserto", "3158607"],
		["Santana do Garambéu", "3158706"],
		["Santana do Jacaré", "3158805"],
		["Santana do Manhuaçu", "3158904"],
		["Santana do Paraíso", "3158953"],
		["Santana do Riacho", "3159001"],
		["Santana dos Montes", "3159100"],
		["Santo Antônio do Amparo", "3159902"],
		["Santo Antônio do Aventureiro", "3160009"],
		["Santo Antônio do Grama", "3160108"],
		["Santo Antônio do Itambé", "3160207"],
		["Santo Antônio do Jacinto", "3160306"],
		["Santo Antônio do Monte", "3160405"],
		["Santo Antônio do Retiro", "3160454"],
		["Santo Antônio do Rio Abaixo", "3160504"],
		["Santo Hipólito", "3160603"],
		["Santos Dumont", "3160702"],
		["São Bento Abade", "3160801"],
		["São Brás do Suaçuí", "3160900"],
		["São Domingos das Dores", "3160959"],
		["São Domingos do Prata", "3161007"],
		["São Félix de Minas", "3161056"],
		["São Francisco", "3161106"],
		["São Francisco de Paula", "3161205"],
		["São Francisco de Sales", "3161304"],
		["São Francisco do Glória", "3161403"],
		["São Geraldo", "3161502"],
		["São Geraldo da Piedade", "3161601"],
		["São Geraldo do Baixio", "3161650"],
		["São Gonçalo do Abaeté", "3161700"],
		["São Gonçalo do Pará", "3161809"],
		["São Gonçalo do Rio Abaixo", "3161908"],
		["São Gonçalo do Rio Preto", "3125507"],
		["São Gonçalo do Sapucaí", "3162005"],
		["São Gotardo", "3162104"],
		["São João Batista do Glória", "3162203"],
		["São João da Lagoa", "3162252"],
		["São João da Mata", "3162302"],
		["São João da Ponte", "3162401"],
		["São João das Missões", "3162450"],
		["São João del Rei", "3162500"],
		["São João do Manhuaçu", "3162559"],
		["São João do Manteninha", "3162575"],
		["São João do Oriente", "3162609"],
		["São João do Pacuí", "3162658"],
		["São João do Paraíso", "3162708"],
		["São João Evangelista", "3162807"],
		["São João Nepomuceno", "3162906"],
		["São Joaquim de Bicas", "3162922"],
		["São José da Barra", "3162948"],
		["São José da Lapa", "3162955"],
		["São José da Safira", "3163003"],
		["São José da Varginha", "3163102"],
		["São José do Alegre", "3163201"],
		["São José do Divino", "3163300"],
		["São José do Goiabal", "3163409"],
		["São José do Jacuri", "3163508"],
		["São José do Mantimento", "3163607"],
		["São Lourenço", "3163706"],
		["São Miguel do Anta", "3163805"],
		["São Pedro da União", "3163904"],
		["São Pedro do Suaçuí", "3164100"],
		["São Pedro dos Ferros", "3164001"],
		["São Romão", "3164209"],
		["São Roque de Minas", "3164308"],
		["São Sebastião da Bela Vista", "3164407"],
		["São Sebastião da Vargem Alegre", "3164431"],
		["São Sebastião do Anta", "3164472"],
		["São Sebastião do Maranhão", "3164506"],
		["São Sebastião do Oeste", "3164605"],
		["São Sebastião do Paraíso", "3164704"],
		["São Sebastião do Rio Preto", "3164803"],
		["São Sebastião do Rio Verde", "3164902"],
		["São Tiago", "3165008"],
		["São Tomás de Aquino", "3165107"],
		["São Tomé das Letras", "3165206"],
		["São Vicente de Minas", "3165305"],
		["Sapucaí-Mirim", "3165404"],
		["Sardoá", "3165503"],
		["Sarzedo", "3165537"],
		["Sem-Peixe", "3165560"],
		["Senador Amaral", "3165578"],
		["Senador Cortes", "3165602"],
		["Senador Firmino", "3165701"],
		["Senador José Bento", "3165800"],
		["Senador Modestino Gonçalves", "3165909"],
		["Senhora de Oliveira", "3166006"],
		["Senhora do Porto", "3166105"],
		["Senhora dos Remédios", "3166204"],
		["Sericita", "3166303"],
		["Seritinga", "3166402"],
		["Serra Azul de Minas", "3166501"],
		["Serra da Saudade", "3166600"],
		["Serra do Salitre", "3166808"],
		["Serra dos Aimorés", "3166709"],
		["Serrania", "3166907"],
		["Serranópolis de Minas", "3166956"],
		["Serranos", "3167004"],
		["Serro", "3167103"],
		["Sete Lagoas", "3167202"],
		["Setubinha", "3165552"],
		["Silveirânia", "3167301"],
		["Silvianópolis", "3167400"],
		["Simão Pereira", "3167509"],
		["Simonésia", "3167608"],
		["Sobrália", "3167707"],
		["Soledade de Minas", "3167806"],
		["Tabuleiro", "3167905"],
		["Taiobeiras", "3168002"],
		["Taparuba", "3168051"],
		["Tapira", "3168101"],
		["Tapiraí", "3168200"],
		["Taquaraçu de Minas", "3168309"],
		["Tarumirim", "3168408"],
		["Teixeiras", "3168507"],
		["Teófilo Otoni", "3168606"],
		["Timóteo", "3168705"],
		["Tiradentes", "3168804"],
		["Tiros", "3168903"],
		["Tocantins", "3169000"],
		["Tocos do Moji", "3169059"],
		["Toledo", "3169109"],
		["Tombos", "3169208"],
		["Três Corações", "3169307"],
		["Três Marias", "3169356"],
		["Três Pontas", "3169406"],
		["Tumiritinga", "3169505"],
		["Tupaciguara", "3169604"],
		["Turmalina", "3169703"],
		["Turvolândia", "3169802"],
		["Ubá", "3169901"],
		["Ubaí", "3170008"],
		["Ubaporanga", "3170057"],
		["Uberaba", "3170107"],
		["Uberlândia", "3170206"],
		["Umburatiba", "3170305"],
		["Unaí", "3170404"],
		["União de Minas", "3170438"],
		["Uruana de Minas", "3170479"],
		["Urucânia", "3170503"],
		["Urucuia", "3170529"],
		["Vargem Alegre", "3170578"],
		["Vargem Bonita", "3170602"],
		["Vargem Grande do Rio Pardo", "3170651"],
		["Varginha", "3170701"],
		["Varjão de Minas", "3170750"],
		["Várzea da Palma", "3170800"],
		["Varzelândia", "3170909"],
		["Vazante", "3171006"],
		["Verdelândia", "3171030"],
		["Veredinha", "3171071"],
		["Veríssimo", "3171105"],
		["Vermelho Novo", "3171154"],
		["Vespasiano", "3171204"],
		["Viçosa", "3171303"],
		["Vieiras", "3171402"],
		["Virgem da Lapa", "3171600"],
		["Virgínia", "3171709"],
		["Virginópolis", "3171808"],
		["Virgolândia", "3171907"],
		["Visconde do Rio Branco", "3172004"],
		["Volta Grande", "3172103"],
		["Wenceslau Braz", "3172202"],
	],
	MS: [
		["Água Clara", "5000203"],
		["Alcinópolis", "5000252"],
		["Amambai", "5000609"],
		["Anastácio", "5000708"],
		["Anaurilândia", "5000807"],
		["Angélica", "5000856"],
		["Antônio João", "5000906"],
		["Aparecida do Taboado", "5001003"],
		["Aquidauana", "5001102"],
		["Aral Moreira", "5001243"],
		["Bandeirantes", "5001508"],
		["Bataguassu", "5001904"],
		["Batayporã", "5002001"],
		["Bela Vista", "5002100"],
		["Bodoquena", "5002159"],
		["Bonito", "5002209"],
		["Brasilândia", "5002308"],
		["Caarapó", "5002407"],
		["Camapuã", "5002605"],
		["Campo Grande", "5002704"],
		["Caracol", "5002803"],
		["Cassilândia", "5002902"],
		["Chapadão do Sul", "5002951"],
		["Corguinho", "5003108"],
		["Coronel Sapucaia", "5003157"],
		["Corumbá", "5003207"],
		["Costa Rica", "5003256"],
		["Coxim", "5003306"],
		["Deodápolis", "5003454"],
		["Dois Irmãos do Buriti", "5003488"],
		["Douradina", "5003504"],
		["Dourados", "5003702"],
		["Eldorado", "5003751"],
		["Fátima do Sul", "5003801"],
		["Figueirão", "5003900"],
		["Glória de Dourados", "5004007"],
		["Guia Lopes da Laguna", "5004106"],
		["Iguatemi", "5004304"],
		["Inocência", "5004403"],
		["Itaporã", "5004502"],
		["Itaquiraí", "5004601"],
		["Ivinhema", "5004700"],
		["Japorã", "5004809"],
		["Jaraguari", "5004908"],
		["Jardim", "5005004"],
		["Jateí", "5005103"],
		["Juti", "5005152"],
		["Ladário", "5005202"],
		["Laguna Carapã", "5005251"],
		["Maracaju", "5005400"],
		["Miranda", "5005608"],
		["Mundo Novo", "5005681"],
		["Naviraí", "5005707"],
		["Nioaque", "5005806"],
		["Nova Alvorada do Sul", "5006002"],
		["Nova Andradina", "5006200"],
		["Novo Horizonte do Sul", "5006259"],
		["Paraíso das Águas", "5006275"],
		["Paranaíba", "5006309"],
		["Paranhos", "5006358"],
		["Pedro Gomes", "5006408"],
		["Ponta Porã", "5006606"],
		["Porto Murtinho", "5006903"],
		["Ribas do Rio Pardo", "5007109"],
		["Rio Brilhante", "5007208"],
		["Rio Negro", "5007307"],
		["Rio Verde de Mato Grosso", "5007406"],
		["Rochedo", "5007505"],
		["Santa Rita do Pardo", "5007554"],
		["São Gabriel do Oeste", "5007695"],
		["Selvíria", "5007802"],
		["Sete Quedas", "5007703"],
		["Sidrolândia", "5007901"],
		["Sonora", "5007935"],
		["Tacuru", "5007950"],
		["Taquarussu", "5007976"],
		["Terenos", "5008008"],
		["Três Lagoas", "5008305"],
		["Vicentina", "5008404"],
	],
	MT: [
		["Acorizal", "5100102"],
		["Água Boa", "5100201"],
		["Alta Floresta", "5100250"],
		["Alto Araguaia", "5100300"],
		["Alto Boa Vista", "5100359"],
		["Alto Garças", "5100409"],
		["Alto Paraguai", "5100508"],
		["Alto Taquari", "5100607"],
		["Apiacás", "5100805"],
		["Araguaiana", "5101001"],
		["Araguainha", "5101209"],
		["Araputanga", "5101258"],
		["Arenápolis", "5101308"],
		["Aripuanã", "5101407"],
		["Barão de Melgaço", "5101605"],
		["Barra do Bugres", "5101704"],
		["Barra do Garças", "5101803"],
		["Boa Esperança do Norte", "5101837"],
		["Bom Jesus do Araguaia", "5101852"],
		["Brasnorte", "5101902"],
		["Cáceres", "5102504"],
		["Campinápolis", "5102603"],
		["Campo Novo do Parecis", "5102637"],
		["Campo Verde", "5102678"],
		["Campos de Júlio", "5102686"],
		["Canabrava do Norte", "5102694"],
		["Canarana", "5102702"],
		["Carlinda", "5102793"],
		["Castanheira", "5102850"],
		["Chapada dos Guimarães", "5103007"],
		["Cláudia", "5103056"],
		["Cocalinho", "5103106"],
		["Colíder", "5103205"],
		["Colniza", "5103254"],
		["Comodoro", "5103304"],
		["Confresa", "5103353"],
		["Conquista D'Oeste", "5103361"],
		["Cotriguaçu", "5103379"],
		["Cuiabá", "5103403"],
		["Curvelândia", "5103437"],
		["Denise", "5103452"],
		["Diamantino", "5103502"],
		["Dom Aquino", "5103601"],
		["Feliz Natal", "5103700"],
		["Figueirópolis D'Oeste", "5103809"],
		["Gaúcha do Norte", "5103858"],
		["General Carneiro", "5103908"],
		["Glória D'Oeste", "5103957"],
		["Guarantã do Norte", "5104104"],
		["Guiratinga", "5104203"],
		["Indiavaí", "5104500"],
		["Ipiranga do Norte", "5104526"],
		["Itanhangá", "5104542"],
		["Itaúba", "5104559"],
		["Itiquira", "5104609"],
		["Jaciara", "5104807"],
		["Jangada", "5104906"],
		["Jauru", "5105002"],
		["Juara", "5105101"],
		["Juína", "5105150"],
		["Juruena", "5105176"],
		["Juscimeira", "5105200"],
		["Lambari D'Oeste", "5105234"],
		["Lucas do Rio Verde", "5105259"],
		["Luciara", "5105309"],
		["Marcelândia", "5105580"],
		["Matupá", "5105606"],
		["Mirassol d'Oeste", "5105622"],
		["Nobres", "5105903"],
		["Nortelândia", "5106000"],
		["Nossa Senhora do Livramento", "5106109"],
		["Nova Bandeirantes", "5106158"],
		["Nova Brasilândia", "5106208"],
		["Nova Canaã do Norte", "5106216"],
		["Nova Guarita", "5108808"],
		["Nova Lacerda", "5106182"],
		["Nova Marilândia", "5108857"],
		["Nova Maringá", "5108907"],
		["Nova Monte Verde", "5108956"],
		["Nova Mutum", "5106224"],
		["Nova Nazaré", "5106174"],
		["Nova Olímpia", "5106232"],
		["Nova Santa Helena", "5106190"],
		["Nova Ubiratã", "5106240"],
		["Nova Xavantina", "5106257"],
		["Novo Horizonte do Norte", "5106273"],
		["Novo Mundo", "5106265"],
		["Novo Santo Antônio", "5106315"],
		["Novo São Joaquim", "5106281"],
		["Paranaíta", "5106299"],
		["Paranatinga", "5106307"],
		["Pedra Preta", "5106372"],
		["Peixoto de Azevedo", "5106422"],
		["Planalto da Serra", "5106455"],
		["Poconé", "5106505"],
		["Pontal do Araguaia", "5106653"],
		["Ponte Branca", "5106703"],
		["Pontes e Lacerda", "5106752"],
		["Porto Alegre do Norte", "5106778"],
		["Porto dos Gaúchos", "5106802"],
		["Porto Esperidião", "5106828"],
		["Porto Estrela", "5106851"],
		["Poxoréu", "5107008"],
		["Primavera do Leste", "5107040"],
		["Querência", "5107065"],
		["Reserva do Cabaçal", "5107156"],
		["Ribeirão Cascalheira", "5107180"],
		["Ribeirãozinho", "5107198"],
		["Rio Branco", "5107206"],
		["Rondolândia", "5107578"],
		["Rondonópolis", "5107602"],
		["Rosário Oeste", "5107701"],
		["Salto do Céu", "5107750"],
		["Santa Carmem", "5107248"],
		["Santa Cruz do Xingu", "5107743"],
		["Santa Rita do Trivelato", "5107768"],
		["Santa Terezinha", "5107776"],
		["Santo Afonso", "5107263"],
		["Santo Antônio de Leverger", "5107800"],
		["Santo Antônio do Leste", "5107792"],
		["São Félix do Araguaia", "5107859"],
		["São José do Povo", "5107297"],
		["São José do Rio Claro", "5107305"],
		["São José do Xingu", "5107354"],
		["São José dos Quatro Marcos", "5107107"],
		["São Pedro da Cipa", "5107404"],
		["Sapezal", "5107875"],
		["Serra Nova Dourada", "5107883"],
		["Sinop", "5107909"],
		["Sorriso", "5107925"],
		["Tabaporã", "5107941"],
		["Tangará da Serra", "5107958"],
		["Tapurah", "5108006"],
		["Terra Nova do Norte", "5108055"],
		["Tesouro", "5108105"],
		["Torixoréu", "5108204"],
		["União do Sul", "5108303"],
		["Vale de São Domingos", "5108352"],
		["Várzea Grande", "5108402"],
		["Vera", "5108501"],
		["Vila Bela da Santíssima Trindade", "5105507"],
		["Vila Rica", "5108600"],
	],
	PA: [
		["Abaetetuba", "1500107"],
		["Abel Figueiredo", "1500131"],
		["Acará", "1500206"],
		["Afuá", "1500305"],
		["Água Azul do Norte", "1500347"],
		["Alenquer", "1500404"],
		["Almeirim", "1500503"],
		["Altamira", "1500602"],
		["Anajás", "1500701"],
		["Ananindeua", "1500800"],
		["Anapu", "1500859"],
		["Augusto Corrêa", "1500909"],
		["Aurora do Pará", "1500958"],
		["Aveiro", "1501006"],
		["Bagre", "1501105"],
		["Baião", "1501204"],
		["Bannach", "1501253"],
		["Barcarena", "1501303"],
		["Belém", "1501402"],
		["Belterra", "1501451"],
		["Benevides", "1501501"],
		["Bom Jesus do Tocantins", "1501576"],
		["Bonito", "1501600"],
		["Bragança", "1501709"],
		["Brasil Novo", "1501725"],
		["Brejo Grande do Araguaia", "1501758"],
		["Breu Branco", "1501782"],
		["Breves", "1501808"],
		["Bujaru", "1501907"],
		["Cachoeira do Arari", "1502004"],
		["Cachoeira do Piriá", "1501956"],
		["Cametá", "1502103"],
		["Canaã dos Carajás", "1502152"],
		["Capanema", "1502202"],
		["Capitão Poço", "1502301"],
		["Castanhal", "1502400"],
		["Chaves", "1502509"],
		["Colares", "1502608"],
		["Conceição do Araguaia", "1502707"],
		["Concórdia do Pará", "1502756"],
		["Cumaru do Norte", "1502764"],
		["Curionópolis", "1502772"],
		["Curralinho", "1502806"],
		["Curuá", "1502855"],
		["Curuçá", "1502905"],
		["Dom Eliseu", "1502939"],
		["Eldorado do Carajás", "1502954"],
		["Faro", "1503002"],
		["Floresta do Araguaia", "1503044"],
		["Garrafão do Norte", "1503077"],
		["Goianésia do Pará", "1503093"],
		["Gurupá", "1503101"],
		["Igarapé-Açu", "1503200"],
		["Igarapé-Miri", "1503309"],
		["Inhangapi", "1503408"],
		["Ipixuna do Pará", "1503457"],
		["Irituia", "1503507"],
		["Itaituba", "1503606"],
		["Itupiranga", "1503705"],
		["Jacareacanga", "1503754"],
		["Jacundá", "1503804"],
		["Juruti", "1503903"],
		["Limoeiro do Ajuru", "1504000"],
		["Mãe do Rio", "1504059"],
		["Magalhães Barata", "1504109"],
		["Marabá", "1504208"],
		["Maracanã", "1504307"],
		["Marapanim", "1504406"],
		["Marituba", "1504422"],
		["Medicilândia", "1504455"],
		["Melgaço", "1504505"],
		["Mocajuba", "1504604"],
		["Moju", "1504703"],
		["Mojuí dos Campos", "1504752"],
		["Monte Alegre", "1504802"],
		["Muaná", "1504901"],
		["Nova Esperança do Piriá", "1504950"],
		["Nova Ipixuna", "1504976"],
		["Nova Timboteua", "1505007"],
		["Novo Progresso", "1505031"],
		["Novo Repartimento", "1505064"],
		["Óbidos", "1505106"],
		["Oeiras do Pará", "1505205"],
		["Oriximiná", "1505304"],
		["Ourém", "1505403"],
		["Ourilândia do Norte", "1505437"],
		["Pacajá", "1505486"],
		["Palestina do Pará", "1505494"],
		["Paragominas", "1505502"],
		["Parauapebas", "1505536"],
		["Pau D'Arco", "1505551"],
		["Peixe-Boi", "1505601"],
		["Piçarra", "1505635"],
		["Placas", "1505650"],
		["Ponta de Pedras", "1505700"],
		["Portel", "1505809"],
		["Porto de Moz", "1505908"],
		["Prainha", "1506005"],
		["Primavera", "1506104"],
		["Quatipuru", "1506112"],
		["Redenção", "1506138"],
		["Rio Maria", "1506161"],
		["Rondon do Pará", "1506187"],
		["Rurópolis", "1506195"],
		["Salinópolis", "1506203"],
		["Salvaterra", "1506302"],
		["Santa Bárbara do Pará", "1506351"],
		["Santa Cruz do Arari", "1506401"],
		["Santa Izabel do Pará", "1506500"],
		["Santa Luzia do Pará", "1506559"],
		["Santa Maria das Barreiras", "1506583"],
		["Santa Maria do Pará", "1506609"],
		["Santana do Araguaia", "1506708"],
		["Santarém", "1506807"],
		["Santarém Novo", "1506906"],
		["Santo Antônio do Tauá", "1507003"],
		["São Caetano de Odivelas", "1507102"],
		["São Domingos do Araguaia", "1507151"],
		["São Domingos do Capim", "1507201"],
		["São Félix do Xingu", "1507300"],
		["São Francisco do Pará", "1507409"],
		["São Geraldo do Araguaia", "1507458"],
		["São João da Ponta", "1507466"],
		["São João de Pirabas", "1507474"],
		["São João do Araguaia", "1507508"],
		["São Miguel do Guamá", "1507607"],
		["São Sebastião da Boa Vista", "1507706"],
		["Sapucaia", "1507755"],
		["Senador José Porfírio", "1507805"],
		["Soure", "1507904"],
		["Tailândia", "1507953"],
		["Terra Alta", "1507961"],
		["Terra Santa", "1507979"],
		["Tomé-Açu", "1508001"],
		["Tracuateua", "1508035"],
		["Trairão", "1508050"],
		["Tucumã", "1508084"],
		["Tucuruí", "1508100"],
		["Ulianópolis", "1508126"],
		["Uruará", "1508159"],
		["Vigia", "1508209"],
		["Viseu", "1508308"],
		["Vitória do Xingu", "1508357"],
		["Xinguara", "1508407"],
	],
	PB: [
		["Água Branca", "2500106"],
		["Aguiar", "2500205"],
		["Alagoa Grande", "2500304"],
		["Alagoa Nova", "2500403"],
		["Alagoinha", "2500502"],
		["Alcantil", "2500536"],
		["Algodão de Jandaíra", "2500577"],
		["Alhandra", "2500601"],
		["Amparo", "2500734"],
		["Aparecida", "2500775"],
		["Araçagi", "2500809"],
		["Arara", "2500908"],
		["Araruna", "2501005"],
		["Areia", "2501104"],
		["Areia de Baraúnas", "2501153"],
		["Areial", "2501203"],
		["Aroeiras", "2501302"],
		["Assunção", "2501351"],
		["Baía da Traição", "2501401"],
		["Bananeiras", "2501500"],
		["Baraúna", "2501534"],
		["Barra de Santa Rosa", "2501609"],
		["Barra de Santana", "2501575"],
		["Barra de São Miguel", "2501708"],
		["Bayeux", "2501807"],
		["Belém", "2501906"],
		["Belém do Brejo do Cruz", "2502003"],
		["Bernardino Batista", "2502052"],
		["Boa Ventura", "2502102"],
		["Boa Vista", "2502151"],
		["Bom Jesus", "2502201"],
		["Bom Sucesso", "2502300"],
		["Bonito de Santa Fé", "2502409"],
		["Boqueirão", "2502508"],
		["Borborema", "2502706"],
		["Brejo do Cruz", "2502805"],
		["Brejo dos Santos", "2502904"],
		["Caaporã", "2503001"],
		["Cabaceiras", "2503100"],
		["Cabedelo", "2503209"],
		["Cachoeira dos Índios", "2503308"],
		["Cacimba de Areia", "2503407"],
		["Cacimba de Dentro", "2503506"],
		["Cacimbas", "2503555"],
		["Caiçara", "2503605"],
		["Cajazeiras", "2503704"],
		["Cajazeirinhas", "2503753"],
		["Caldas Brandão", "2503803"],
		["Camalaú", "2503902"],
		["Campina Grande", "2504009"],
		["Capim", "2504033"],
		["Caraúbas", "2504074"],
		["Carrapateira", "2504108"],
		["Casserengue", "2504157"],
		["Catingueira", "2504207"],
		["Catolé do Rocha", "2504306"],
		["Caturité", "2504355"],
		["Conceição", "2504405"],
		["Condado", "2504504"],
		["Conde", "2504603"],
		["Congo", "2504702"],
		["Coremas", "2504801"],
		["Coxixola", "2504850"],
		["Cruz do Espírito Santo", "2504900"],
		["Cubati", "2505006"],
		["Cuité", "2505105"],
		["Cuité de Mamanguape", "2505238"],
		["Cuitegi", "2505204"],
		["Curral de Cima", "2505279"],
		["Curral Velho", "2505303"],
		["Damião", "2505352"],
		["Desterro", "2505402"],
		["Diamante", "2505600"],
		["Dona Inês", "2505709"],
		["Duas Estradas", "2505808"],
		["Emas", "2505907"],
		["Esperança", "2506004"],
		["Fagundes", "2506103"],
		["Frei Martinho", "2506202"],
		["Gado Bravo", "2506251"],
		["Guarabira", "2506301"],
		["Gurinhém", "2506400"],
		["Gurjão", "2506509"],
		["Ibiara", "2506608"],
		["Igaracy", "2502607"],
		["Imaculada", "2506707"],
		["Ingá", "2506806"],
		["Itabaiana", "2506905"],
		["Itaporanga", "2507002"],
		["Itapororoca", "2507101"],
		["Itatuba", "2507200"],
		["Jacaraú", "2507309"],
		["Jericó", "2507408"],
		["João Pessoa", "2507507"],
		["Joca Claudino", "2513653"],
		["Juarez Távora", "2507606"],
		["Juazeirinho", "2507705"],
		["Junco do Seridó", "2507804"],
		["Juripiranga", "2507903"],
		["Juru", "2508000"],
		["Lagoa", "2508109"],
		["Lagoa de Dentro", "2508208"],
		["Lagoa Seca", "2508307"],
		["Lastro", "2508406"],
		["Livramento", "2508505"],
		["Logradouro", "2508554"],
		["Lucena", "2508604"],
		["Mãe d'Água", "2508703"],
		["Malta", "2508802"],
		["Mamanguape", "2508901"],
		["Manaíra", "2509008"],
		["Marcação", "2509057"],
		["Mari", "2509107"],
		["Marizópolis", "2509156"],
		["Massaranduba", "2509206"],
		["Mataraca", "2509305"],
		["Matinhas", "2509339"],
		["Mato Grosso", "2509370"],
		["Maturéia", "2509396"],
		["Mogeiro", "2509404"],
		["Montadas", "2509503"],
		["Monte Horebe", "2509602"],
		["Monteiro", "2509701"],
		["Mulungu", "2509800"],
		["Natuba", "2509909"],
		["Nazarezinho", "2510006"],
		["Nova Floresta", "2510105"],
		["Nova Olinda", "2510204"],
		["Nova Palmeira", "2510303"],
		["Olho d'Água", "2510402"],
		["Olivedos", "2510501"],
		["Ouro Velho", "2510600"],
		["Parari", "2510659"],
		["Passagem", "2510709"],
		["Patos", "2510808"],
		["Paulista", "2510907"],
		["Pedra Branca", "2511004"],
		["Pedra Lavrada", "2511103"],
		["Pedras de Fogo", "2511202"],
		["Pedro Régis", "2512721"],
		["Piancó", "2511301"],
		["Picuí", "2511400"],
		["Pilar", "2511509"],
		["Pilões", "2511608"],
		["Pilõezinhos", "2511707"],
		["Pirpirituba", "2511806"],
		["Pitimbu", "2511905"],
		["Pocinhos", "2512002"],
		["Poço Dantas", "2512036"],
		["Poço de José de Moura", "2512077"],
		["Pombal", "2512101"],
		["Prata", "2512200"],
		["Princesa Isabel", "2512309"],
		["Puxinanã", "2512408"],
		["Queimadas", "2512507"],
		["Quixaba", "2512606"],
		["Remígio", "2512705"],
		["Riachão", "2512747"],
		["Riachão do Bacamarte", "2512754"],
		["Riachão do Poço", "2512762"],
		["Riacho de Santo Antônio", "2512788"],
		["Riacho dos Cavalos", "2512804"],
		["Rio Tinto", "2512903"],
		["Salgadinho", "2513000"],
		["Salgado de São Félix", "2513109"],
		["Santa Cecília", "2513158"],
		["Santa Cruz", "2513208"],
		["Santa Helena", "2513307"],
		["Santa Inês", "2513356"],
		["Santa Luzia", "2513406"],
		["Santa Rita", "2513703"],
		["Santa Teresinha", "2513802"],
		["Santana de Mangueira", "2513505"],
		["Santana dos Garrotes", "2513604"],
		["Santo André", "2513851"],
		["São Bentinho", "2513927"],
		["São Bento", "2513901"],
		["São Domingos", "2513968"],
		["São Domingos do Cariri", "2513943"],
		["São Francisco", "2513984"],
		["São João do Cariri", "2514008"],
		["São João do Rio do Peixe", "2500700"],
		["São João do Tigre", "2514107"],
		["São José da Lagoa Tapada", "2514206"],
		["São José de Caiana", "2514305"],
		["São José de Espinharas", "2514404"],
		["São José de Piranhas", "2514503"],
		["São José de Princesa", "2514552"],
		["São José do Bonfim", "2514602"],
		["São José do Brejo do Cruz", "2514651"],
		["São José do Sabugi", "2514701"],
		["São José dos Cordeiros", "2514800"],
		["São José dos Ramos", "2514453"],
		["São Mamede", "2514909"],
		["São Miguel de Taipu", "2515005"],
		["São Sebastião de Lagoa de Roça", "2515104"],
		["São Sebastião do Umbuzeiro", "2515203"],
		["São Vicente do Seridó", "2515401"],
		["Sapé", "2515302"],
		["Serra Branca", "2515500"],
		["Serra da Raiz", "2515609"],
		["Serra Grande", "2515708"],
		["Serra Redonda", "2515807"],
		["Serraria", "2515906"],
		["Sertãozinho", "2515930"],
		["Sobrado", "2515971"],
		["Solânea", "2516003"],
		["Soledade", "2516102"],
		["Sossêgo", "2516151"],
		["Sousa", "2516201"],
		["Sumé", "2516300"],
		["Tacima", "2516409"],
		["Taperoá", "2516508"],
		["Tavares", "2516607"],
		["Teixeira", "2516706"],
		["Tenório", "2516755"],
		["Triunfo", "2516805"],
		["Uiraúna", "2516904"],
		["Umbuzeiro", "2517001"],
		["Várzea", "2517100"],
		["Vieirópolis", "2517209"],
		["Vista Serrana", "2505501"],
		["Zabelê", "2517407"],
	],
	PE: [
		["Abreu e Lima", "2600054"],
		["Afogados da Ingazeira", "2600104"],
		["Afrânio", "2600203"],
		["Agrestina", "2600302"],
		["Água Preta", "2600401"],
		["Águas Belas", "2600500"],
		["Alagoinha", "2600609"],
		["Aliança", "2600708"],
		["Altinho", "2600807"],
		["Amaraji", "2600906"],
		["Angelim", "2601003"],
		["Araçoiaba", "2601052"],
		["Araripina", "2601102"],
		["Arcoverde", "2601201"],
		["Barra de Guabiraba", "2601300"],
		["Barreiros", "2601409"],
		["Belém de Maria", "2601508"],
		["Belém do São Francisco", "2601607"],
		["Belo Jardim", "2601706"],
		["Betânia", "2601805"],
		["Bezerros", "2601904"],
		["Bodocó", "2602001"],
		["Bom Conselho", "2602100"],
		["Bom Jardim", "2602209"],
		["Bonito", "2602308"],
		["Brejão", "2602407"],
		["Brejinho", "2602506"],
		["Brejo da Madre de Deus", "2602605"],
		["Buenos Aires", "2602704"],
		["Buíque", "2602803"],
		["Cabo de Santo Agostinho", "2602902"],
		["Cabrobó", "2603009"],
		["Cachoeirinha", "2603108"],
		["Caetés", "2603207"],
		["Calçado", "2603306"],
		["Calumbi", "2603405"],
		["Camaragibe", "2603454"],
		["Camocim de São Félix", "2603504"],
		["Camutanga", "2603603"],
		["Canhotinho", "2603702"],
		["Capoeiras", "2603801"],
		["Carnaíba", "2603900"],
		["Carnaubeira da Penha", "2603926"],
		["Carpina", "2604007"],
		["Caruaru", "2604106"],
		["Casinhas", "2604155"],
		["Catende", "2604205"],
		["Cedro", "2604304"],
		["Chã de Alegria", "2604403"],
		["Chã Grande", "2604502"],
		["Condado", "2604601"],
		["Correntes", "2604700"],
		["Cortês", "2604809"],
		["Cumaru", "2604908"],
		["Cupira", "2605004"],
		["Custódia", "2605103"],
		["Dormentes", "2605152"],
		["Escada", "2605202"],
		["Exu", "2605301"],
		["Feira Nova", "2605400"],
		["Fernando de Noronha", "2605459"],
		["Ferreiros", "2605509"],
		["Flores", "2605608"],
		["Floresta", "2605707"],
		["Frei Miguelinho", "2605806"],
		["Gameleira", "2605905"],
		["Garanhuns", "2606002"],
		["Glória do Goitá", "2606101"],
		["Goiana", "2606200"],
		["Granito", "2606309"],
		["Gravatá", "2606408"],
		["Iati", "2606507"],
		["Ibimirim", "2606606"],
		["Ibirajuba", "2606705"],
		["Igarassu", "2606804"],
		["Iguaracy", "2606903"],
		["Ilha de Itamaracá", "2607604"],
		["Inajá", "2607000"],
		["Ingazeira", "2607109"],
		["Ipojuca", "2607208"],
		["Ipubi", "2607307"],
		["Itacuruba", "2607406"],
		["Itaíba", "2607505"],
		["Itambé", "2607653"],
		["Itapetim", "2607703"],
		["Itapissuma", "2607752"],
		["Itaquitinga", "2607802"],
		["Jaboatão dos Guararapes", "2607901"],
		["Jaqueira", "2607950"],
		["Jataúba", "2608008"],
		["Jatobá", "2608057"],
		["João Alfredo", "2608107"],
		["Joaquim Nabuco", "2608206"],
		["Jucati", "2608255"],
		["Jupi", "2608305"],
		["Jurema", "2608404"],
		["Lagoa de Itaenga", "2608503"],
		["Lagoa do Carro", "2608453"],
		["Lagoa do Ouro", "2608602"],
		["Lagoa dos Gatos", "2608701"],
		["Lagoa Grande", "2608750"],
		["Lajedo", "2608800"],
		["Limoeiro", "2608909"],
		["Macaparana", "2609006"],
		["Machados", "2609105"],
		["Manari", "2609154"],
		["Maraial", "2609204"],
		["Mirandiba", "2609303"],
		["Moreilândia", "2614303"],
		["Moreno", "2609402"],
		["Nazaré da Mata", "2609501"],
		["Olinda", "2609600"],
		["Orobó", "2609709"],
		["Orocó", "2609808"],
		["Ouricuri", "2609907"],
		["Palmares", "2610004"],
		["Palmeirina", "2610103"],
		["Panelas", "2610202"],
		["Paranatama", "2610301"],
		["Parnamirim", "2610400"],
		["Passira", "2610509"],
		["Paudalho", "2610608"],
		["Paulista", "2610707"],
		["Pedra", "2610806"],
		["Pesqueira", "2610905"],
		["Petrolândia", "2611002"],
		["Petrolina", "2611101"],
		["Poção", "2611200"],
		["Pombos", "2611309"],
		["Primavera", "2611408"],
		["Quipapá", "2611507"],
		["Quixaba", "2611533"],
		["Recife", "2611606"],
		["Riacho das Almas", "2611705"],
		["Ribeirão", "2611804"],
		["Rio Formoso", "2611903"],
		["Sairé", "2612000"],
		["Salgadinho", "2612109"],
		["Salgueiro", "2612208"],
		["Saloá", "2612307"],
		["Sanharó", "2612406"],
		["Santa Cruz", "2612455"],
		["Santa Cruz da Baixa Verde", "2612471"],
		["Santa Cruz do Capibaribe", "2612505"],
		["Santa Filomena", "2612554"],
		["Santa Maria da Boa Vista", "2612604"],
		["Santa Maria do Cambucá", "2612703"],
		["Santa Terezinha", "2612802"],
		["São Benedito do Sul", "2612901"],
		["São Bento do Una", "2613008"],
		["São Caitano", "2613107"],
		["São João", "2613206"],
		["São Joaquim do Monte", "2613305"],
		["São José da Coroa Grande", "2613404"],
		["São José do Belmonte", "2613503"],
		["São José do Egito", "2613602"],
		["São Lourenço da Mata", "2613701"],
		["São Vicente Férrer", "2613800"],
		["Serra Talhada", "2613909"],
		["Serrita", "2614006"],
		["Sertânia", "2614105"],
		["Sirinhaém", "2614204"],
		["Solidão", "2614402"],
		["Surubim", "2614501"],
		["Tabira", "2614600"],
		["Tacaimbó", "2614709"],
		["Tacaratu", "2614808"],
		["Tamandaré", "2614857"],
		["Taquaritinga do Norte", "2615003"],
		["Terezinha", "2615102"],
		["Terra Nova", "2615201"],
		["Timbaúba", "2615300"],
		["Toritama", "2615409"],
		["Tracunhaém", "2615508"],
		["Trindade", "2615607"],
		["Triunfo", "2615706"],
		["Tupanatinga", "2615805"],
		["Tuparetama", "2615904"],
		["Venturosa", "2616001"],
		["Verdejante", "2616100"],
		["Vertente do Lério", "2616183"],
		["Vertentes", "2616209"],
		["Vicência", "2616308"],
		["Vitória de Santo Antão", "2616407"],
		["Xexéu", "2616506"],
	],
	PI: [
		["Acauã", "2200053"],
		["Agricolândia", "2200103"],
		["Água Branca", "2200202"],
		["Alagoinha do Piauí", "2200251"],
		["Alegrete do Piauí", "2200277"],
		["Alto Longá", "2200301"],
		["Altos", "2200400"],
		["Alvorada do Gurguéia", "2200459"],
		["Amarante", "2200509"],
		["Angical do Piauí", "2200608"],
		["Anísio de Abreu", "2200707"],
		["Antônio Almeida", "2200806"],
		["Aroazes", "2200905"],
		["Aroeiras do Itaim", "2200954"],
		["Arraial", "2201002"],
		["Assunção do Piauí", "2201051"],
		["Avelino Lopes", "2201101"],
		["Baixa Grande do Ribeiro", "2201150"],
		["Barra D'Alcântara", "2201176"],
		["Barras", "2201200"],
		["Barreiras do Piauí", "2201309"],
		["Barro Duro", "2201408"],
		["Batalha", "2201507"],
		["Bela Vista do Piauí", "2201556"],
		["Belém do Piauí", "2201572"],
		["Beneditinos", "2201606"],
		["Bertolínia", "2201705"],
		["Betânia do Piauí", "2201739"],
		["Boa Hora", "2201770"],
		["Bocaina", "2201804"],
		["Bom Jesus", "2201903"],
		["Bom Princípio do Piauí", "2201919"],
		["Bonfim do Piauí", "2201929"],
		["Boqueirão do Piauí", "2201945"],
		["Brasileira", "2201960"],
		["Brejo do Piauí", "2201988"],
		["Buriti dos Lopes", "2202000"],
		["Buriti dos Montes", "2202026"],
		["Cabeceiras do Piauí", "2202059"],
		["Cajazeiras do Piauí", "2202075"],
		["Cajueiro da Praia", "2202083"],
		["Caldeirão Grande do Piauí", "2202091"],
		["Campinas do Piauí", "2202109"],
		["Campo Alegre do Fidalgo", "2202117"],
		["Campo Grande do Piauí", "2202133"],
		["Campo Largo do Piauí", "2202174"],
		["Campo Maior", "2202208"],
		["Canavieira", "2202251"],
		["Canto do Buriti", "2202307"],
		["Capitão de Campos", "2202406"],
		["Capitão Gervásio Oliveira", "2202455"],
		["Caracol", "2202505"],
		["Caraúbas do Piauí", "2202539"],
		["Caridade do Piauí", "2202554"],
		["Castelo do Piauí", "2202604"],
		["Caxingó", "2202653"],
		["Cocal", "2202703"],
		["Cocal de Telha", "2202711"],
		["Cocal dos Alves", "2202729"],
		["Coivaras", "2202737"],
		["Colônia do Gurguéia", "2202752"],
		["Colônia do Piauí", "2202778"],
		["Conceição do Canindé", "2202802"],
		["Coronel José Dias", "2202851"],
		["Corrente", "2202901"],
		["Cristalândia do Piauí", "2203008"],
		["Cristino Castro", "2203107"],
		["Curimatá", "2203206"],
		["Currais", "2203230"],
		["Curral Novo do Piauí", "2203271"],
		["Curralinhos", "2203255"],
		["Demerval Lobão", "2203305"],
		["Dirceu Arcoverde", "2203354"],
		["Dom Expedito Lopes", "2203404"],
		["Dom Inocêncio", "2203453"],
		["Domingos Mourão", "2203420"],
		["Elesbão Veloso", "2203503"],
		["Eliseu Martins", "2203602"],
		["Esperantina", "2203701"],
		["Fartura do Piauí", "2203750"],
		["Flores do Piauí", "2203800"],
		["Floresta do Piauí", "2203859"],
		["Floriano", "2203909"],
		["Francinópolis", "2204006"],
		["Francisco Ayres", "2204105"],
		["Francisco Macedo", "2204154"],
		["Francisco Santos", "2204204"],
		["Fronteiras", "2204303"],
		["Geminiano", "2204352"],
		["Gilbués", "2204402"],
		["Guadalupe", "2204501"],
		["Guaribas", "2204550"],
		["Hugo Napoleão", "2204600"],
		["Ilha Grande", "2204659"],
		["Inhuma", "2204709"],
		["Ipiranga do Piauí", "2204808"],
		["Isaías Coelho", "2204907"],
		["Itainópolis", "2205003"],
		["Itaueira", "2205102"],
		["Jacobina do Piauí", "2205151"],
		["Jaicós", "2205201"],
		["Jardim do Mulato", "2205250"],
		["Jatobá do Piauí", "2205276"],
		["Jerumenha", "2205300"],
		["João Costa", "2205359"],
		["Joaquim Pires", "2205409"],
		["Joca Marques", "2205458"],
		["José de Freitas", "2205508"],
		["Juazeiro do Piauí", "2205516"],
		["Júlio Borges", "2205524"],
		["Jurema", "2205532"],
		["Lagoa Alegre", "2205557"],
		["Lagoa de São Francisco", "2205573"],
		["Lagoa do Barro do Piauí", "2205565"],
		["Lagoa do Piauí", "2205581"],
		["Lagoa do Sítio", "2205599"],
		["Lagoinha do Piauí", "2205540"],
		["Landri Sales", "2205607"],
		["Luís Correia", "2205706"],
		["Luzilândia", "2205805"],
		["Madeiro", "2205854"],
		["Manoel Emídio", "2205904"],
		["Marcolândia", "2205953"],
		["Marcos Parente", "2206001"],
		["Massapê do Piauí", "2206050"],
		["Matias Olímpio", "2206100"],
		["Miguel Alves", "2206209"],
		["Miguel Leão", "2206308"],
		["Milton Brandão", "2206357"],
		["Monsenhor Gil", "2206407"],
		["Monsenhor Hipólito", "2206506"],
		["Monte Alegre do Piauí", "2206605"],
		["Morro Cabeça no Tempo", "2206654"],
		["Morro do Chapéu do Piauí", "2206670"],
		["Murici dos Portelas", "2206696"],
		["Nazaré do Piauí", "2206704"],
		["Nazária", "2206720"],
		["Nossa Senhora de Nazaré", "2206753"],
		["Nossa Senhora dos Remédios", "2206803"],
		["Nova Santa Rita", "2207959"],
		["Novo Oriente do Piauí", "2206902"],
		["Novo Santo Antônio", "2206951"],
		["Oeiras", "2207009"],
		["Olho D'Água do Piauí", "2207108"],
		["Padre Marcos", "2207207"],
		["Paes Landim", "2207306"],
		["Pajeú do Piauí", "2207355"],
		["Palmeira do Piauí", "2207405"],
		["Palmeirais", "2207504"],
		["Paquetá", "2207553"],
		["Parnaguá", "2207603"],
		["Parnaíba", "2207702"],
		["Passagem Franca do Piauí", "2207751"],
		["Patos do Piauí", "2207777"],
		["Pau D'Arco do Piauí", "2207793"],
		["Paulistana", "2207801"],
		["Pavussu", "2207850"],
		["Pedro II", "2207900"],
		["Pedro Laurentino", "2207934"],
		["Picos", "2208007"],
		["Pimenteiras", "2208106"],
		["Pio IX", "2208205"],
		["Piracuruca", "2208304"],
		["Piripiri", "2208403"],
		["Porto", "2208502"],
		["Porto Alegre do Piauí", "2208551"],
		["Prata do Piauí", "2208601"],
		["Queimada Nova", "2208650"],
		["Redenção do Gurguéia", "2208700"],
		["Regeneração", "2208809"],
		["Riacho Frio", "2208858"],
		["Ribeira do Piauí", "2208874"],
		["Ribeiro Gonçalves", "2208908"],
		["Rio Grande do Piauí", "2209005"],
		["Santa Cruz do Piauí", "2209104"],
		["Santa Cruz dos Milagres", "2209153"],
		["Santa Filomena", "2209203"],
		["Santa Luz", "2209302"],
		["Santa Rosa do Piauí", "2209377"],
		["Santana do Piauí", "2209351"],
		["Santo Antônio de Lisboa", "2209401"],
		["Santo Antônio dos Milagres", "2209450"],
		["Santo Inácio do Piauí", "2209500"],
		["São Braz do Piauí", "2209559"],
		["São Félix do Piauí", "2209609"],
		["São Francisco de Assis do Piauí", "2209658"],
		["São Francisco do Piauí", "2209708"],
		["São Gonçalo do Gurguéia", "2209757"],
		["São Gonçalo do Piauí", "2209807"],
		["São João da Canabrava", "2209856"],
		["São João da Fronteira", "2209872"],
		["São João da Serra", "2209906"],
		["São João da Varjota", "2209955"],
		["São João do Arraial", "2209971"],
		["São João do Piauí", "2210003"],
		["São José do Divino", "2210052"],
		["São José do Peixe", "2210102"],
		["São José do Piauí", "2210201"],
		["São Julião", "2210300"],
		["São Lourenço do Piauí", "2210359"],
		["São Luis do Piauí", "2210375"],
		["São Miguel da Baixa Grande", "2210383"],
		["São Miguel do Fidalgo", "2210391"],
		["São Miguel do Tapuio", "2210409"],
		["São Pedro do Piauí", "2210508"],
		["São Raimundo Nonato", "2210607"],
		["Sebastião Barros", "2210623"],
		["Sebastião Leal", "2210631"],
		["Sigefredo Pacheco", "2210656"],
		["Simões", "2210706"],
		["Simplício Mendes", "2210805"],
		["Socorro do Piauí", "2210904"],
		["Sussuapara", "2210938"],
		["Tamboril do Piauí", "2210953"],
		["Tanque do Piauí", "2210979"],
		["Teresina", "2211001"],
		["União", "2211100"],
		["Uruçuí", "2211209"],
		["Valença do Piauí", "2211308"],
		["Várzea Branca", "2211357"],
		["Várzea Grande", "2211407"],
		["Vera Mendes", "2211506"],
		["Vila Nova do Piauí", "2211605"],
		["Wall Ferraz", "2211704"],
	],
	PR: [
		["Abatiá", "4100103"],
		["Adrianópolis", "4100202"],
		["Agudos do Sul", "4100301"],
		["Almirante Tamandaré", "4100400"],
		["Altamira do Paraná", "4100459"],
		["Alto Paraíso", "4128625"],
		["Alto Paraná", "4100608"],
		["Alto Piquiri", "4100707"],
		["Altônia", "4100509"],
		["Alvorada do Sul", "4100806"],
		["Amaporã", "4100905"],
		["Ampére", "4101002"],
		["Anahy", "4101051"],
		["Andirá", "4101101"],
		["Ângulo", "4101150"],
		["Antonina", "4101200"],
		["Antônio Olinto", "4101309"],
		["Apucarana", "4101408"],
		["Arapongas", "4101507"],
		["Arapoti", "4101606"],
		["Arapuã", "4101655"],
		["Araruna", "4101705"],
		["Araucária", "4101804"],
		["Ariranha do Ivaí", "4101853"],
		["Assaí", "4101903"],
		["Assis Chateaubriand", "4102000"],
		["Astorga", "4102109"],
		["Atalaia", "4102208"],
		["Balsa Nova", "4102307"],
		["Bandeirantes", "4102406"],
		["Barbosa Ferraz", "4102505"],
		["Barra do Jacaré", "4102703"],
		["Barracão", "4102604"],
		["Bela Vista da Caroba", "4102752"],
		["Bela Vista do Paraíso", "4102802"],
		["Bituruna", "4102901"],
		["Boa Esperança", "4103008"],
		["Boa Esperança do Iguaçu", "4103024"],
		["Boa Ventura de São Roque", "4103040"],
		["Boa Vista da Aparecida", "4103057"],
		["Bocaiúva do Sul", "4103107"],
		["Bom Jesus do Sul", "4103156"],
		["Bom Sucesso", "4103206"],
		["Bom Sucesso do Sul", "4103222"],
		["Borrazópolis", "4103305"],
		["Braganey", "4103354"],
		["Brasilândia do Sul", "4103370"],
		["Cafeara", "4103404"],
		["Cafelândia", "4103453"],
		["Cafezal do Sul", "4103479"],
		["Califórnia", "4103503"],
		["Cambará", "4103602"],
		["Cambé", "4103701"],
		["Cambira", "4103800"],
		["Campina da Lagoa", "4103909"],
		["Campina do Simão", "4103958"],
		["Campina Grande do Sul", "4104006"],
		["Campo Bonito", "4104055"],
		["Campo do Tenente", "4104105"],
		["Campo Largo", "4104204"],
		["Campo Magro", "4104253"],
		["Campo Mourão", "4104303"],
		["Cândido de Abreu", "4104402"],
		["Candói", "4104428"],
		["Cantagalo", "4104451"],
		["Capanema", "4104501"],
		["Capitão Leônidas Marques", "4104600"],
		["Carambeí", "4104659"],
		["Carlópolis", "4104709"],
		["Cascavel", "4104808"],
		["Castro", "4104907"],
		["Catanduvas", "4105003"],
		["Centenário do Sul", "4105102"],
		["Cerro Azul", "4105201"],
		["Céu Azul", "4105300"],
		["Chopinzinho", "4105409"],
		["Cianorte", "4105508"],
		["Cidade Gaúcha", "4105607"],
		["Clevelândia", "4105706"],
		["Colombo", "4105805"],
		["Colorado", "4105904"],
		["Congonhinhas", "4106001"],
		["Conselheiro Mairinck", "4106100"],
		["Contenda", "4106209"],
		["Corbélia", "4106308"],
		["Cornélio Procópio", "4106407"],
		["Coronel Domingos Soares", "4106456"],
		["Coronel Vivida", "4106506"],
		["Corumbataí do Sul", "4106555"],
		["Cruz Machado", "4106803"],
		["Cruzeiro do Iguaçu", "4106571"],
		["Cruzeiro do Oeste", "4106605"],
		["Cruzeiro do Sul", "4106704"],
		["Cruzmaltina", "4106852"],
		["Curitiba", "4106902"],
		["Curiúva", "4107009"],
		["Diamante D'Oeste", "4107157"],
		["Diamante do Norte", "4107108"],
		["Diamante do Sul", "4107124"],
		["Dois Vizinhos", "4107207"],
		["Douradina", "4107256"],
		["Doutor Camargo", "4107306"],
		["Doutor Ulysses", "4128633"],
		["Enéas Marques", "4107405"],
		["Engenheiro Beltrão", "4107504"],
		["Entre Rios do Oeste", "4107538"],
		["Esperança Nova", "4107520"],
		["Espigão Alto do Iguaçu", "4107546"],
		["Farol", "4107553"],
		["Faxinal", "4107603"],
		["Fazenda Rio Grande", "4107652"],
		["Fênix", "4107702"],
		["Fernandes Pinheiro", "4107736"],
		["Figueira", "4107751"],
		["Flor da Serra do Sul", "4107850"],
		["Floraí", "4107801"],
		["Floresta", "4107900"],
		["Florestópolis", "4108007"],
		["Flórida", "4108106"],
		["Formosa do Oeste", "4108205"],
		["Foz do Iguaçu", "4108304"],
		["Foz do Jordão", "4108452"],
		["Francisco Alves", "4108320"],
		["Francisco Beltrão", "4108403"],
		["General Carneiro", "4108502"],
		["Godoy Moreira", "4108551"],
		["Goioerê", "4108601"],
		["Goioxim", "4108650"],
		["Grandes Rios", "4108700"],
		["Guaíra", "4108809"],
		["Guairaçá", "4108908"],
		["Guamiranga", "4108957"],
		["Guapirama", "4109005"],
		["Guaporema", "4109104"],
		["Guaraci", "4109203"],
		["Guaraniaçu", "4109302"],
		["Guarapuava", "4109401"],
		["Guaraqueçaba", "4109500"],
		["Guaratuba", "4109609"],
		["Honório Serpa", "4109658"],
		["Ibaiti", "4109708"],
		["Ibema", "4109757"],
		["Ibiporã", "4109807"],
		["Icaraíma", "4109906"],
		["Iguaraçu", "4110003"],
		["Iguatu", "4110052"],
		["Imbaú", "4110078"],
		["Imbituva", "4110102"],
		["Inácio Martins", "4110201"],
		["Inajá", "4110300"],
		["Indianópolis", "4110409"],
		["Ipiranga", "4110508"],
		["Iporã", "4110607"],
		["Iracema do Oeste", "4110656"],
		["Irati", "4110706"],
		["Iretama", "4110805"],
		["Itaguajé", "4110904"],
		["Itaipulândia", "4110953"],
		["Itambaracá", "4111001"],
		["Itambé", "4111100"],
		["Itapejara d'Oeste", "4111209"],
		["Itaperuçu", "4111258"],
		["Itaúna do Sul", "4111308"],
		["Ivaí", "4111407"],
		["Ivaiporã", "4111506"],
		["Ivaté", "4111555"],
		["Ivatuba", "4111605"],
		["Jaboti", "4111704"],
		["Jacarezinho", "4111803"],
		["Jaguapitã", "4111902"],
		["Jaguariaíva", "4112009"],
		["Jandaia do Sul", "4112108"],
		["Janiópolis", "4112207"],
		["Japira", "4112306"],
		["Japurá", "4112405"],
		["Jardim Alegre", "4112504"],
		["Jardim Olinda", "4112603"],
		["Jataizinho", "4112702"],
		["Jesuítas", "4112751"],
		["Joaquim Távora", "4112801"],
		["Jundiaí do Sul", "4112900"],
		["Juranda", "4112959"],
		["Jussara", "4113007"],
		["Kaloré", "4113106"],
		["Lapa", "4113205"],
		["Laranjal", "4113254"],
		["Laranjeiras do Sul", "4113304"],
		["Leópolis", "4113403"],
		["Lidianópolis", "4113429"],
		["Lindoeste", "4113452"],
		["Loanda", "4113502"],
		["Lobato", "4113601"],
		["Londrina", "4113700"],
		["Luiziana", "4113734"],
		["Lunardelli", "4113759"],
		["Lupionópolis", "4113809"],
		["Mallet", "4113908"],
		["Mamborê", "4114005"],
		["Mandaguaçu", "4114104"],
		["Mandaguari", "4114203"],
		["Mandirituba", "4114302"],
		["Manfrinópolis", "4114351"],
		["Mangueirinha", "4114401"],
		["Manoel Ribas", "4114500"],
		["Marechal Cândido Rondon", "4114609"],
		["Maria Helena", "4114708"],
		["Marialva", "4114807"],
		["Marilândia do Sul", "4114906"],
		["Marilena", "4115002"],
		["Mariluz", "4115101"],
		["Maringá", "4115200"],
		["Mariópolis", "4115309"],
		["Maripá", "4115358"],
		["Marmeleiro", "4115408"],
		["Marquinho", "4115457"],
		["Marumbi", "4115507"],
		["Matelândia", "4115606"],
		["Matinhos", "4115705"],
		["Mato Rico", "4115739"],
		["Mauá da Serra", "4115754"],
		["Medianeira", "4115804"],
		["Mercedes", "4115853"],
		["Mirador", "4115903"],
		["Miraselva", "4116000"],
		["Missal", "4116059"],
		["Moreira Sales", "4116109"],
		["Morretes", "4116208"],
		["Munhoz de Melo", "4116307"],
		["Nossa Senhora das Graças", "4116406"],
		["Nova Aliança do Ivaí", "4116505"],
		["Nova América da Colina", "4116604"],
		["Nova Aurora", "4116703"],
		["Nova Cantu", "4116802"],
		["Nova Esperança", "4116901"],
		["Nova Esperança do Sudoeste", "4116950"],
		["Nova Fátima", "4117008"],
		["Nova Laranjeiras", "4117057"],
		["Nova Londrina", "4117107"],
		["Nova Olímpia", "4117206"],
		["Nova Prata do Iguaçu", "4117255"],
		["Nova Santa Bárbara", "4117214"],
		["Nova Santa Rosa", "4117222"],
		["Nova Tebas", "4117271"],
		["Novo Itacolomi", "4117297"],
		["Ortigueira", "4117305"],
		["Ourizona", "4117404"],
		["Ouro Verde do Oeste", "4117453"],
		["Paiçandu", "4117503"],
		["Palmas", "4117602"],
		["Palmeira", "4117701"],
		["Palmital", "4117800"],
		["Palotina", "4117909"],
		["Paraíso do Norte", "4118006"],
		["Paranacity", "4118105"],
		["Paranaguá", "4118204"],
		["Paranapoema", "4118303"],
		["Paranavaí", "4118402"],
		["Pato Bragado", "4118451"],
		["Pato Branco", "4118501"],
		["Paula Freitas", "4118600"],
		["Paulo Frontin", "4118709"],
		["Peabiru", "4118808"],
		["Perobal", "4118857"],
		["Pérola", "4118907"],
		["Pérola d'Oeste", "4119004"],
		["Piên", "4119103"],
		["Pinhais", "4119152"],
		["Pinhal de São Bento", "4119251"],
		["Pinhalão", "4119202"],
		["Pinhão", "4119301"],
		["Piraí do Sul", "4119400"],
		["Piraquara", "4119509"],
		["Pitanga", "4119608"],
		["Pitangueiras", "4119657"],
		["Planaltina do Paraná", "4119707"],
		["Planalto", "4119806"],
		["Ponta Grossa", "4119905"],
		["Pontal do Paraná", "4119954"],
		["Porecatu", "4120002"],
		["Porto Amazonas", "4120101"],
		["Porto Barreiro", "4120150"],
		["Porto Rico", "4120200"],
		["Porto Vitória", "4120309"],
		["Prado Ferreira", "4120333"],
		["Pranchita", "4120358"],
		["Presidente Castelo Branco", "4120408"],
		["Primeiro de Maio", "4120507"],
		["Prudentópolis", "4120606"],
		["Quarto Centenário", "4120655"],
		["Quatiguá", "4120705"],
		["Quatro Barras", "4120804"],
		["Quatro Pontes", "4120853"],
		["Quedas do Iguaçu", "4120903"],
		["Querência do Norte", "4121000"],
		["Quinta do Sol", "4121109"],
		["Quitandinha", "4121208"],
		["Ramilândia", "4121257"],
		["Rancho Alegre", "4121307"],
		["Rancho Alegre D'Oeste", "4121356"],
		["Realeza", "4121406"],
		["Rebouças", "4121505"],
		["Renascença", "4121604"],
		["Reserva", "4121703"],
		["Reserva do Iguaçu", "4121752"],
		["Ribeirão Claro", "4121802"],
		["Ribeirão do Pinhal", "4121901"],
		["Rio Azul", "4122008"],
		["Rio Bom", "4122107"],
		["Rio Bonito do Iguaçu", "4122156"],
		["Rio Branco do Ivaí", "4122172"],
		["Rio Branco do Sul", "4122206"],
		["Rio Negro", "4122305"],
		["Rolândia", "4122404"],
		["Roncador", "4122503"],
		["Rondon", "4122602"],
		["Rosário do Ivaí", "4122651"],
		["Sabáudia", "4122701"],
		["Salgado Filho", "4122800"],
		["Salto do Itararé", "4122909"],
		["Salto do Lontra", "4123006"],
		["Santa Amélia", "4123105"],
		["Santa Cecília do Pavão", "4123204"],
		["Santa Cruz de Monte Castelo", "4123303"],
		["Santa Fé", "4123402"],
		["Santa Helena", "4123501"],
		["Santa Inês", "4123600"],
		["Santa Isabel do Ivaí", "4123709"],
		["Santa Izabel do Oeste", "4123808"],
		["Santa Lúcia", "4123824"],
		["Santa Maria do Oeste", "4123857"],
		["Santa Mariana", "4123907"],
		["Santa Mônica", "4123956"],
		["Santa Tereza do Oeste", "4124020"],
		["Santa Terezinha de Itaipu", "4124053"],
		["Santana do Itararé", "4124004"],
		["Santo Antônio da Platina", "4124103"],
		["Santo Antônio do Caiuá", "4124202"],
		["Santo Antônio do Paraíso", "4124301"],
		["Santo Antônio do Sudoeste", "4124400"],
		["Santo Inácio", "4124509"],
		["São Carlos do Ivaí", "4124608"],
		["São Jerônimo da Serra", "4124707"],
		["São João", "4124806"],
		["São João do Caiuá", "4124905"],
		["São João do Ivaí", "4125001"],
		["São João do Triunfo", "4125100"],
		["São Jorge d'Oeste", "4125209"],
		["São Jorge do Ivaí", "4125308"],
		["São Jorge do Patrocínio", "4125357"],
		["São José da Boa Vista", "4125407"],
		["São José das Palmeiras", "4125456"],
		["São José dos Pinhais", "4125506"],
		["São Manoel do Paraná", "4125555"],
		["São Mateus do Sul", "4125605"],
		["São Miguel do Iguaçu", "4125704"],
		["São Pedro do Iguaçu", "4125753"],
		["São Pedro do Ivaí", "4125803"],
		["São Pedro do Paraná", "4125902"],
		["São Sebastião da Amoreira", "4126009"],
		["São Tomé", "4126108"],
		["Sapopema", "4126207"],
		["Sarandi", "4126256"],
		["Saudade do Iguaçu", "4126272"],
		["Sengés", "4126306"],
		["Serranópolis do Iguaçu", "4126355"],
		["Sertaneja", "4126405"],
		["Sertanópolis", "4126504"],
		["Siqueira Campos", "4126603"],
		["Sulina", "4126652"],
		["Tamarana", "4126678"],
		["Tamboara", "4126702"],
		["Tapejara", "4126801"],
		["Tapira", "4126900"],
		["Teixeira Soares", "4127007"],
		["Telêmaco Borba", "4127106"],
		["Terra Boa", "4127205"],
		["Terra Rica", "4127304"],
		["Terra Roxa", "4127403"],
		["Tibagi", "4127502"],
		["Tijucas do Sul", "4127601"],
		["Toledo", "4127700"],
		["Tomazina", "4127809"],
		["Três Barras do Paraná", "4127858"],
		["Tunas do Paraná", "4127882"],
		["Tuneiras do Oeste", "4127908"],
		["Tupãssi", "4127957"],
		["Turvo", "4127965"],
		["Ubiratã", "4128005"],
		["Umuarama", "4128104"],
		["União da Vitória", "4128203"],
		["Uniflor", "4128302"],
		["Uraí", "4128401"],
		["Ventania", "4128534"],
		["Vera Cruz do Oeste", "4128559"],
		["Verê", "4128609"],
		["Virmond", "4128658"],
		["Vitorino", "4128708"],
		["Wenceslau Braz", "4128500"],
		["Xambrê", "4128807"],
	],
	RJ: [
		["Angra dos Reis", "3300100"],
		["Aperibé", "3300159"],
		["Araruama", "3300209"],
		["Areal", "3300225"],
		["Armação dos Búzios", "3300233"],
		["Arraial do Cabo", "3300258"],
		["Barra do Piraí", "3300308"],
		["Barra Mansa", "3300407"],
		["Belford Roxo", "3300456"],
		["Bom Jardim", "3300506"],
		["Bom Jesus do Itabapoana", "3300605"],
		["Cabo Frio", "3300704"],
		["Cachoeiras de Macacu", "3300803"],
		["Cambuci", "3300902"],
		["Campos dos Goytacazes", "3301009"],
		["Cantagalo", "3301108"],
		["Carapebus", "3300936"],
		["Cardoso Moreira", "3301157"],
		["Carmo", "3301207"],
		["Casimiro de Abreu", "3301306"],
		["Comendador Levy Gasparian", "3300951"],
		["Conceição de Macabu", "3301405"],
		["Cordeiro", "3301504"],
		["Duas Barras", "3301603"],
		["Duque de Caxias", "3301702"],
		["Engenheiro Paulo de Frontin", "3301801"],
		["Guapimirim", "3301850"],
		["Iguaba Grande", "3301876"],
		["Itaboraí", "3301900"],
		["Itaguaí", "3302007"],
		["Italva", "3302056"],
		["Itaocara", "3302106"],
		["Itaperuna", "3302205"],
		["Itatiaia", "3302254"],
		["Japeri", "3302270"],
		["Laje do Muriaé", "3302304"],
		["Macaé", "3302403"],
		["Macuco", "3302452"],
		["Magé", "3302502"],
		["Mangaratiba", "3302601"],
		["Maricá", "3302700"],
		["Mendes", "3302809"],
		["Mesquita", "3302858"],
		["Miguel Pereira", "3302908"],
		["Miracema", "3303005"],
		["Natividade", "3303104"],
		["Nilópolis", "3303203"],
		["Niterói", "3303302"],
		["Nova Friburgo", "3303401"],
		["Nova Iguaçu", "3303500"],
		["Paracambi", "3303609"],
		["Paraíba do Sul", "3303708"],
		["Paraty", "3303807"],
		["Paty do Alferes", "3303856"],
		["Petrópolis", "3303906"],
		["Pinheiral", "3303955"],
		["Piraí", "3304003"],
		["Porciúncula", "3304102"],
		["Porto Real", "3304110"],
		["Quatis", "3304128"],
		["Queimados", "3304144"],
		["Quissamã", "3304151"],
		["Resende", "3304201"],
		["Rio Bonito", "3304300"],
		["Rio Claro", "3304409"],
		["Rio das Flores", "3304508"],
		["Rio das Ostras", "3304524"],
		["Rio de Janeiro", "3304557"],
		["Santa Maria Madalena", "3304607"],
		["Santo Antônio de Pádua", "3304706"],
		["São Fidélis", "3304805"],
		["São Francisco de Itabapoana", "3304755"],
		["São Gonçalo", "3304904"],
		["São João da Barra", "3305000"],
		["São João de Meriti", "3305109"],
		["São José de Ubá", "3305133"],
		["São José do Vale do Rio Preto", "3305158"],
		["São Pedro da Aldeia", "3305208"],
		["São Sebastião do Alto", "3305307"],
		["Sapucaia", "3305406"],
		["Saquarema", "3305505"],
		["Seropédica", "3305554"],
		["Silva Jardim", "3305604"],
		["Sumidouro", "3305703"],
		["Tanguá", "3305752"],
		["Teresópolis", "3305802"],
		["Trajano de Moraes", "3305901"],
		["Três Rios", "3306008"],
		["Valença", "3306107"],
		["Varre-Sai", "3306156"],
		["Vassouras", "3306206"],
		["Volta Redonda", "3306305"],
	],
	RN: [
		["Acari", "2400109"],
		["Afonso Bezerra", "2400307"],
		["Água Nova", "2400406"],
		["Alexandria", "2400505"],
		["Almino Afonso", "2400604"],
		["Alto do Rodrigues", "2400703"],
		["Angicos", "2400802"],
		["Antônio Martins", "2400901"],
		["Apodi", "2401008"],
		["Areia Branca", "2401107"],
		["Arez", "2401206"],
		["Assú", "2400208"],
		["Baía Formosa", "2401404"],
		["Baraúna", "2401453"],
		["Barcelona", "2401503"],
		["Bento Fernandes", "2401602"],
		["Bodó", "2401651"],
		["Bom Jesus", "2401701"],
		["Brejinho", "2401800"],
		["Caiçara do Norte", "2401859"],
		["Caiçara do Rio do Vento", "2401909"],
		["Caicó", "2402006"],
		["Campo Grande", "2401305"],
		["Campo Redondo", "2402105"],
		["Canguaretama", "2402204"],
		["Caraúbas", "2402303"],
		["Carnaúba dos Dantas", "2402402"],
		["Carnaubais", "2402501"],
		["Ceará-Mirim", "2402600"],
		["Cerro Corá", "2402709"],
		["Coronel Ezequiel", "2402808"],
		["Coronel João Pessoa", "2402907"],
		["Cruzeta", "2403004"],
		["Currais Novos", "2403103"],
		["Doutor Severiano", "2403202"],
		["Encanto", "2403301"],
		["Equador", "2403400"],
		["Espírito Santo", "2403509"],
		["Extremoz", "2403608"],
		["Felipe Guerra", "2403707"],
		["Fernando Pedroza", "2403756"],
		["Florânia", "2403806"],
		["Francisco Dantas", "2403905"],
		["Frutuoso Gomes", "2404002"],
		["Galinhos", "2404101"],
		["Goianinha", "2404200"],
		["Governador Dix-Sept Rosado", "2404309"],
		["Grossos", "2404408"],
		["Guamaré", "2404507"],
		["Ielmo Marinho", "2404606"],
		["Ipanguaçu", "2404705"],
		["Ipueira", "2404804"],
		["Itajá", "2404853"],
		["Itaú", "2404903"],
		["Jaçanã", "2405009"],
		["Jandaíra", "2405108"],
		["Janduís", "2405207"],
		["Januário Cicco", "2405306"],
		["Japi", "2405405"],
		["Jardim de Angicos", "2405504"],
		["Jardim de Piranhas", "2405603"],
		["Jardim do Seridó", "2405702"],
		["João Câmara", "2405801"],
		["João Dias", "2405900"],
		["José da Penha", "2406007"],
		["Jucurutu", "2406106"],
		["Jundiá", "2406155"],
		["Lagoa d'Anta", "2406205"],
		["Lagoa de Pedras", "2406304"],
		["Lagoa de Velhos", "2406403"],
		["Lagoa Nova", "2406502"],
		["Lagoa Salgada", "2406601"],
		["Lajes", "2406700"],
		["Lajes Pintadas", "2406809"],
		["Lucrécia", "2406908"],
		["Luís Gomes", "2407005"],
		["Macaíba", "2407104"],
		["Macau", "2407203"],
		["Major Sales", "2407252"],
		["Marcelino Vieira", "2407302"],
		["Martins", "2407401"],
		["Maxaranguape", "2407500"],
		["Messias Targino", "2407609"],
		["Montanhas", "2407708"],
		["Monte Alegre", "2407807"],
		["Monte das Gameleiras", "2407906"],
		["Mossoró", "2408003"],
		["Natal", "2408102"],
		["Nísia Floresta", "2408201"],
		["Nova Cruz", "2408300"],
		["Olho d'Água do Borges", "2408409"],
		["Ouro Branco", "2408508"],
		["Paraná", "2408607"],
		["Paraú", "2408706"],
		["Parazinho", "2408805"],
		["Parelhas", "2408904"],
		["Parnamirim", "2403251"],
		["Passa e Fica", "2409100"],
		["Passagem", "2409209"],
		["Patu", "2409308"],
		["Pau dos Ferros", "2409407"],
		["Pedra Grande", "2409506"],
		["Pedra Preta", "2409605"],
		["Pedro Avelino", "2409704"],
		["Pedro Velho", "2409803"],
		["Pendências", "2409902"],
		["Pilões", "2410009"],
		["Poço Branco", "2410108"],
		["Portalegre", "2410207"],
		["Porto do Mangue", "2410256"],
		["Pureza", "2410405"],
		["Rafael Fernandes", "2410504"],
		["Rafael Godeiro", "2410603"],
		["Riacho da Cruz", "2410702"],
		["Riacho de Santana", "2410801"],
		["Riachuelo", "2410900"],
		["Rio do Fogo", "2408953"],
		["Rodolfo Fernandes", "2411007"],
		["Ruy Barbosa", "2411106"],
		["Santa Cruz", "2411205"],
		["Santa Maria", "2409332"],
		["Santana do Matos", "2411403"],
		["Santana do Seridó", "2411429"],
		["Santo Antônio", "2411502"],
		["São Bento do Norte", "2411601"],
		["São Bento do Trairí", "2411700"],
		["São Fernando", "2411809"],
		["São Francisco do Oeste", "2411908"],
		["São Gonçalo do Amarante", "2412005"],
		["São João do Sabugi", "2412104"],
		["São José de Mipibu", "2412203"],
		["São José do Campestre", "2412302"],
		["São José do Seridó", "2412401"],
		["São Miguel", "2412500"],
		["São Miguel do Gostoso", "2412559"],
		["São Paulo do Potengi", "2412609"],
		["São Pedro", "2412708"],
		["São Rafael", "2412807"],
		["São Tomé", "2412906"],
		["São Vicente", "2413003"],
		["Senador Elói de Souza", "2413102"],
		["Senador Georgino Avelino", "2413201"],
		["Serra Caiada", "2410306"],
		["Serra de São Bento", "2413300"],
		["Serra do Mel", "2413359"],
		["Serra Negra do Norte", "2413409"],
		["Serrinha", "2413508"],
		["Serrinha dos Pintos", "2413557"],
		["Severiano Melo", "2413607"],
		["Sítio Novo", "2413706"],
		["Taboleiro Grande", "2413805"],
		["Taipu", "2413904"],
		["Tangará", "2414001"],
		["Tenente Ananias", "2414100"],
		["Tenente Laurentino Cruz", "2414159"],
		["Tibau", "2411056"],
		["Tibau do Sul", "2414209"],
		["Timbaúba dos Batistas", "2414308"],
		["Touros", "2414407"],
		["Triunfo Potiguar", "2414456"],
		["Umarizal", "2414506"],
		["Upanema", "2414605"],
		["Várzea", "2414704"],
		["Venha-Ver", "2414753"],
		["Vera Cruz", "2414803"],
		["Viçosa", "2414902"],
		["Vila Flor", "2415008"],
	],
	RO: [
		["Alta Floresta D'Oeste", "1100015"],
		["Alto Alegre dos Parecis", "1100379"],
		["Alto Paraíso", "1100403"],
		["Alvorada D'Oeste", "1100346"],
		["Ariquemes", "1100023"],
		["Buritis", "1100452"],
		["Cabixi", "1100031"],
		["Cacaulândia", "1100601"],
		["Cacoal", "1100049"],
		["Campo Novo de Rondônia", "1100700"],
		["Candeias do Jamari", "1100809"],
		["Castanheiras", "1100908"],
		["Cerejeiras", "1100056"],
		["Chupinguaia", "1100924"],
		["Colorado do Oeste", "1100064"],
		["Corumbiara", "1100072"],
		["Costa Marques", "1100080"],
		["Cujubim", "1100940"],
		["Espigão D'Oeste", "1100098"],
		["Governador Jorge Teixeira", "1101005"],
		["Guajará-Mirim", "1100106"],
		["Itapuã do Oeste", "1101104"],
		["Jaru", "1100114"],
		["Ji-Paraná", "1100122"],
		["Machadinho D'Oeste", "1100130"],
		["Ministro Andreazza", "1101203"],
		["Mirante da Serra", "1101302"],
		["Monte Negro", "1101401"],
		["Nova Brasilândia D'Oeste", "1100148"],
		["Nova Mamoré", "1100338"],
		["Nova União", "1101435"],
		["Novo Horizonte do Oeste", "1100502"],
		["Ouro Preto do Oeste", "1100155"],
		["Parecis", "1101450"],
		["Pimenta Bueno", "1100189"],
		["Pimenteiras do Oeste", "1101468"],
		["Porto Velho", "1100205"],
		["Presidente Médici", "1100254"],
		["Primavera de Rondônia", "1101476"],
		["Rio Crespo", "1100262"],
		["Rolim de Moura", "1100288"],
		["Santa Luzia D'Oeste", "1100296"],
		["São Felipe D'Oeste", "1101484"],
		["São Francisco do Guaporé", "1101492"],
		["São Miguel do Guaporé", "1100320"],
		["Seringueiras", "1101500"],
		["Teixeirópolis", "1101559"],
		["Theobroma", "1101609"],
		["Urupá", "1101708"],
		["Vale do Anari", "1101757"],
		["Vale do Paraíso", "1101807"],
		["Vilhena", "1100304"],
	],
	RR: [
		["Alto Alegre", "1400050"],
		["Amajari", "1400027"],
		["Boa Vista", "1400100"],
		["Bonfim", "1400159"],
		["Cantá", "1400175"],
		["Caracaraí", "1400209"],
		["Caroebe", "1400233"],
		["Iracema", "1400282"],
		["Mucajaí", "1400308"],
		["Normandia", "1400407"],
		["Pacaraima", "1400456"],
		["Rorainópolis", "1400472"],
		["São João da Baliza", "1400506"],
		["São Luiz do Anauá", "1400605"],
		["Uiramutã", "1400704"],
	],
	RS: [
		["Aceguá", "4300034"],
		["Água Santa", "4300059"],
		["Agudo", "4300109"],
		["Ajuricaba", "4300208"],
		["Alecrim", "4300307"],
		["Alegrete", "4300406"],
		["Alegria", "4300455"],
		["Almirante Tamandaré do Sul", "4300471"],
		["Alpestre", "4300505"],
		["Alto Alegre", "4300554"],
		["Alto Feliz", "4300570"],
		["Alvorada", "4300604"],
		["Amaral Ferrador", "4300638"],
		["Ametista do Sul", "4300646"],
		["André da Rocha", "4300661"],
		["Anta Gorda", "4300703"],
		["Antônio Prado", "4300802"],
		["Arambaré", "4300851"],
		["Araricá", "4300877"],
		["Aratiba", "4300901"],
		["Arroio do Meio", "4301008"],
		["Arroio do Padre", "4301073"],
		["Arroio do Sal", "4301057"],
		["Arroio do Tigre", "4301206"],
		["Arroio dos Ratos", "4301107"],
		["Arroio Grande", "4301305"],
		["Arvorezinha", "4301404"],
		["Augusto Pestana", "4301503"],
		["Áurea", "4301552"],
		["Bagé", "4301602"],
		["Balneário Pinhal", "4301636"],
		["Barão", "4301651"],
		["Barão de Cotegipe", "4301701"],
		["Barão do Triunfo", "4301750"],
		["Barra do Guarita", "4301859"],
		["Barra do Quaraí", "4301875"],
		["Barra do Ribeiro", "4301909"],
		["Barra do Rio Azul", "4301925"],
		["Barra Funda", "4301958"],
		["Barracão", "4301800"],
		["Barros Cassal", "4302006"],
		["Benjamin Constant do Sul", "4302055"],
		["Bento Gonçalves", "4302105"],
		["Boa Vista das Missões", "4302154"],
		["Boa Vista do Buricá", "4302204"],
		["Boa Vista do Cadeado", "4302220"],
		["Boa Vista do Incra", "4302238"],
		["Boa Vista do Sul", "4302253"],
		["Bom Jesus", "4302303"],
		["Bom Princípio", "4302352"],
		["Bom Progresso", "4302378"],
		["Bom Retiro do Sul", "4302402"],
		["Boqueirão do Leão", "4302451"],
		["Bossoroca", "4302501"],
		["Bozano", "4302584"],
		["Braga", "4302600"],
		["Brochier", "4302659"],
		["Butiá", "4302709"],
		["Caçapava do Sul", "4302808"],
		["Cacequi", "4302907"],
		["Cachoeira do Sul", "4303004"],
		["Cachoeirinha", "4303103"],
		["Cacique Doble", "4303202"],
		["Caibaté", "4303301"],
		["Caiçara", "4303400"],
		["Camaquã", "4303509"],
		["Camargo", "4303558"],
		["Cambará do Sul", "4303608"],
		["Campestre da Serra", "4303673"],
		["Campina das Missões", "4303707"],
		["Campinas do Sul", "4303806"],
		["Campo Bom", "4303905"],
		["Campo Novo", "4304002"],
		["Campos Borges", "4304101"],
		["Candelária", "4304200"],
		["Cândido Godói", "4304309"],
		["Candiota", "4304358"],
		["Canela", "4304408"],
		["Canguçu", "4304507"],
		["Canoas", "4304606"],
		["Canudos do Vale", "4304614"],
		["Capão Bonito do Sul", "4304622"],
		["Capão da Canoa", "4304630"],
		["Capão do Cipó", "4304655"],
		["Capão do Leão", "4304663"],
		["Capela de Santana", "4304689"],
		["Capitão", "4304697"],
		["Capivari do Sul", "4304671"],
		["Caraá", "4304713"],
		["Carazinho", "4304705"],
		["Carlos Barbosa", "4304804"],
		["Carlos Gomes", "4304853"],
		["Casca", "4304903"],
		["Caseiros", "4304952"],
		["Catuípe", "4305009"],
		["Caxias do Sul", "4305108"],
		["Centenário", "4305116"],
		["Cerrito", "4305124"],
		["Cerro Branco", "4305132"],
		["Cerro Grande", "4305157"],
		["Cerro Grande do Sul", "4305173"],
		["Cerro Largo", "4305207"],
		["Chapada", "4305306"],
		["Charqueadas", "4305355"],
		["Charrua", "4305371"],
		["Chiapetta", "4305405"],
		["Chuí", "4305439"],
		["Chuvisca", "4305447"],
		["Cidreira", "4305454"],
		["Ciríaco", "4305504"],
		["Colinas", "4305587"],
		["Colorado", "4305603"],
		["Condor", "4305702"],
		["Constantina", "4305801"],
		["Coqueiro Baixo", "4305835"],
		["Coqueiros do Sul", "4305850"],
		["Coronel Barros", "4305871"],
		["Coronel Bicaco", "4305900"],
		["Coronel Pilar", "4305934"],
		["Cotiporã", "4305959"],
		["Coxilha", "4305975"],
		["Crissiumal", "4306007"],
		["Cristal", "4306056"],
		["Cristal do Sul", "4306072"],
		["Cruz Alta", "4306106"],
		["Cruzaltense", "4306130"],
		["Cruzeiro do Sul", "4306205"],
		["David Canabarro", "4306304"],
		["Derrubadas", "4306320"],
		["Dezesseis de Novembro", "4306353"],
		["Dilermando de Aguiar", "4306379"],
		["Dois Irmãos", "4306403"],
		["Dois Irmãos das Missões", "4306429"],
		["Dois Lajeados", "4306452"],
		["Dom Feliciano", "4306502"],
		["Dom Pedrito", "4306601"],
		["Dom Pedro de Alcântara", "4306551"],
		["Dona Francisca", "4306700"],
		["Doutor Maurício Cardoso", "4306734"],
		["Doutor Ricardo", "4306759"],
		["Eldorado do Sul", "4306767"],
		["Encantado", "4306809"],
		["Encruzilhada do Sul", "4306908"],
		["Engenho Velho", "4306924"],
		["Entre Rios do Sul", "4306957"],
		["Entre-Ijuís", "4306932"],
		["Erebango", "4306973"],
		["Erechim", "4307005"],
		["Ernestina", "4307054"],
		["Erval Grande", "4307203"],
		["Erval Seco", "4307302"],
		["Esmeralda", "4307401"],
		["Esperança do Sul", "4307450"],
		["Espumoso", "4307500"],
		["Estação", "4307559"],
		["Estância Velha", "4307609"],
		["Esteio", "4307708"],
		["Estrela", "4307807"],
		["Estrela Velha", "4307815"],
		["Eugênio de Castro", "4307831"],
		["Fagundes Varela", "4307864"],
		["Farroupilha", "4307906"],
		["Faxinal do Soturno", "4308003"],
		["Faxinalzinho", "4308052"],
		["Fazenda Vilanova", "4308078"],
		["Feliz", "4308102"],
		["Flores da Cunha", "4308201"],
		["Floriano Peixoto", "4308250"],
		["Fontoura Xavier", "4308300"],
		["Formigueiro", "4308409"],
		["Forquetinha", "4308433"],
		["Fortaleza dos Valos", "4308458"],
		["Frederico Westphalen", "4308508"],
		["Garibaldi", "4308607"],
		["Garruchos", "4308656"],
		["Gaurama", "4308706"],
		["General Câmara", "4308805"],
		["Gentil", "4308854"],
		["Getúlio Vargas", "4308904"],
		["Giruá", "4309001"],
		["Glorinha", "4309050"],
		["Gramado", "4309100"],
		["Gramado dos Loureiros", "4309126"],
		["Gramado Xavier", "4309159"],
		["Gravataí", "4309209"],
		["Guabiju", "4309258"],
		["Guaíba", "4309308"],
		["Guaporé", "4309407"],
		["Guarani das Missões", "4309506"],
		["Harmonia", "4309555"],
		["Herval", "4307104"],
		["Herveiras", "4309571"],
		["Horizontina", "4309605"],
		["Hulha Negra", "4309654"],
		["Humaitá", "4309704"],
		["Ibarama", "4309753"],
		["Ibiaçá", "4309803"],
		["Ibiraiaras", "4309902"],
		["Ibirapuitã", "4309951"],
		["Ibirubá", "4310009"],
		["Igrejinha", "4310108"],
		["Ijuí", "4310207"],
		["Ilópolis", "4310306"],
		["Imbé", "4310330"],
		["Imigrante", "4310363"],
		["Independência", "4310405"],
		["Inhacorá", "4310413"],
		["Ipê", "4310439"],
		["Ipiranga do Sul", "4310462"],
		["Iraí", "4310504"],
		["Itaara", "4310538"],
		["Itacurubi", "4310553"],
		["Itapuca", "4310579"],
		["Itaqui", "4310603"],
		["Itati", "4310652"],
		["Itatiba do Sul", "4310702"],
		["Ivorá", "4310751"],
		["Ivoti", "4310801"],
		["Jaboticaba", "4310850"],
		["Jacuizinho", "4310876"],
		["Jacutinga", "4310900"],
		["Jaguarão", "4311007"],
		["Jaguari", "4311106"],
		["Jaquirana", "4311122"],
		["Jari", "4311130"],
		["Jóia", "4311155"],
		["Júlio de Castilhos", "4311205"],
		["Lagoa Bonita do Sul", "4311239"],
		["Lagoa dos Três Cantos", "4311270"],
		["Lagoa Vermelha", "4311304"],
		["Lagoão", "4311254"],
		["Lajeado", "4311403"],
		["Lajeado do Bugre", "4311429"],
		["Lavras do Sul", "4311502"],
		["Liberato Salzano", "4311601"],
		["Lindolfo Collor", "4311627"],
		["Linha Nova", "4311643"],
		["Maçambará", "4311718"],
		["Machadinho", "4311700"],
		["Mampituba", "4311734"],
		["Manoel Viana", "4311759"],
		["Maquiné", "4311775"],
		["Maratá", "4311791"],
		["Marau", "4311809"],
		["Marcelino Ramos", "4311908"],
		["Mariana Pimentel", "4311981"],
		["Mariano Moro", "4312005"],
		["Marques de Souza", "4312054"],
		["Mata", "4312104"],
		["Mato Castelhano", "4312138"],
		["Mato Leitão", "4312153"],
		["Mato Queimado", "4312179"],
		["Maximiliano de Almeida", "4312203"],
		["Minas do Leão", "4312252"],
		["Miraguaí", "4312302"],
		["Montauri", "4312351"],
		["Monte Alegre dos Campos", "4312377"],
		["Monte Belo do Sul", "4312385"],
		["Montenegro", "4312401"],
		["Mormaço", "4312427"],
		["Morrinhos do Sul", "4312443"],
		["Morro Redondo", "4312450"],
		["Morro Reuter", "4312476"],
		["Mostardas", "4312500"],
		["Muçum", "4312609"],
		["Muitos Capões", "4312617"],
		["Muliterno", "4312625"],
		["Não-Me-Toque", "4312658"],
		["Nicolau Vergueiro", "4312674"],
		["Nonoai", "4312708"],
		["Nova Alvorada", "4312757"],
		["Nova Araçá", "4312807"],
		["Nova Bassano", "4312906"],
		["Nova Boa Vista", "4312955"],
		["Nova Bréscia", "4313003"],
		["Nova Candelária", "4313011"],
		["Nova Esperança do Sul", "4313037"],
		["Nova Hartz", "4313060"],
		["Nova Pádua", "4313086"],
		["Nova Palma", "4313102"],
		["Nova Petrópolis", "4313201"],
		["Nova Prata", "4313300"],
		["Nova Ramada", "4313334"],
		["Nova Roma do Sul", "4313359"],
		["Nova Santa Rita", "4313375"],
		["Novo Barreiro", "4313490"],
		["Novo Cabrais", "4313391"],
		["Novo Hamburgo", "4313409"],
		["Novo Machado", "4313425"],
		["Novo Tiradentes", "4313441"],
		["Novo Xingu", "4313466"],
		["Osório", "4313508"],
		["Paim Filho", "4313607"],
		["Palmares do Sul", "4313656"],
		["Palmeira das Missões", "4313706"],
		["Palmitinho", "4313805"],
		["Panambi", "4313904"],
		["Pantano Grande", "4313953"],
		["Paraí", "4314001"],
		["Paraíso do Sul", "4314027"],
		["Pareci Novo", "4314035"],
		["Parobé", "4314050"],
		["Passa Sete", "4314068"],
		["Passo do Sobrado", "4314076"],
		["Passo Fundo", "4314100"],
		["Paulo Bento", "4314134"],
		["Paverama", "4314159"],
		["Pedras Altas", "4314175"],
		["Pedro Osório", "4314209"],
		["Pejuçara", "4314308"],
		["Pelotas", "4314407"],
		["Picada Café", "4314423"],
		["Pinhal", "4314456"],
		["Pinhal da Serra", "4314464"],
		["Pinhal Grande", "4314472"],
		["Pinheirinho do Vale", "4314498"],
		["Pinheiro Machado", "4314506"],
		["Pinto Bandeira", "4314548"],
		["Pirapó", "4314555"],
		["Piratini", "4314605"],
		["Planalto", "4314704"],
		["Poço das Antas", "4314753"],
		["Pontão", "4314779"],
		["Ponte Preta", "4314787"],
		["Portão", "4314803"],
		["Porto Alegre", "4314902"],
		["Porto Lucena", "4315008"],
		["Porto Mauá", "4315057"],
		["Porto Vera Cruz", "4315073"],
		["Porto Xavier", "4315107"],
		["Pouso Novo", "4315131"],
		["Presidente Lucena", "4315149"],
		["Progresso", "4315156"],
		["Protásio Alves", "4315172"],
		["Putinga", "4315206"],
		["Quaraí", "4315305"],
		["Quatro Irmãos", "4315313"],
		["Quevedos", "4315321"],
		["Quinze de Novembro", "4315354"],
		["Redentora", "4315404"],
		["Relvado", "4315453"],
		["Restinga Sêca", "4315503"],
		["Rio dos Índios", "4315552"],
		["Rio Grande", "4315602"],
		["Rio Pardo", "4315701"],
		["Riozinho", "4315750"],
		["Roca Sales", "4315800"],
		["Rodeio Bonito", "4315909"],
		["Rolador", "4315958"],
		["Rolante", "4316006"],
		["Ronda Alta", "4316105"],
		["Rondinha", "4316204"],
		["Roque Gonzales", "4316303"],
		["Rosário do Sul", "4316402"],
		["Sagrada Família", "4316428"],
		["Saldanha Marinho", "4316436"],
		["Salto do Jacuí", "4316451"],
		["Salvador das Missões", "4316477"],
		["Salvador do Sul", "4316501"],
		["Sananduva", "4316600"],
		["Sant'Ana do Livramento", "4317103"],
		["Santa Bárbara do Sul", "4316709"],
		["Santa Cecília do Sul", "4316733"],
		["Santa Clara do Sul", "4316758"],
		["Santa Cruz do Sul", "4316808"],
		["Santa Margarida do Sul", "4316972"],
		["Santa Maria", "4316907"],
		["Santa Maria do Herval", "4316956"],
		["Santa Rosa", "4317202"],
		["Santa Tereza", "4317251"],
		["Santa Vitória do Palmar", "4317301"],
		["Santana da Boa Vista", "4317004"],
		["Santiago", "4317400"],
		["Santo Ângelo", "4317509"],
		["Santo Antônio da Patrulha", "4317608"],
		["Santo Antônio das Missões", "4317707"],
		["Santo Antônio do Palma", "4317558"],
		["Santo Antônio do Planalto", "4317756"],
		["Santo Augusto", "4317806"],
		["Santo Cristo", "4317905"],
		["Santo Expedito do Sul", "4317954"],
		["São Borja", "4318002"],
		["São Domingos do Sul", "4318051"],
		["São Francisco de Assis", "4318101"],
		["São Francisco de Paula", "4318200"],
		["São Gabriel", "4318309"],
		["São Jerônimo", "4318408"],
		["São João da Urtiga", "4318424"],
		["São João do Polêsine", "4318432"],
		["São Jorge", "4318440"],
		["São José das Missões", "4318457"],
		["São José do Herval", "4318465"],
		["São José do Hortêncio", "4318481"],
		["São José do Inhacorá", "4318499"],
		["São José do Norte", "4318507"],
		["São José do Ouro", "4318606"],
		["São José do Sul", "4318614"],
		["São José dos Ausentes", "4318622"],
		["São Leopoldo", "4318705"],
		["São Lourenço do Sul", "4318804"],
		["São Luiz Gonzaga", "4318903"],
		["São Marcos", "4319000"],
		["São Martinho", "4319109"],
		["São Martinho da Serra", "4319125"],
		["São Miguel das Missões", "4319158"],
		["São Nicolau", "4319208"],
		["São Paulo das Missões", "4319307"],
		["São Pedro da Serra", "4319356"],
		["São Pedro das Missões", "4319364"],
		["São Pedro do Butiá", "4319372"],
		["São Pedro do Sul", "4319406"],
		["São Sebastião do Caí", "4319505"],
		["São Sepé", "4319604"],
		["São Valentim", "4319703"],
		["São Valentim do Sul", "4319711"],
		["São Valério do Sul", "4319737"],
		["São Vendelino", "4319752"],
		["São Vicente do Sul", "4319802"],
		["Sapiranga", "4319901"],
		["Sapucaia do Sul", "4320008"],
		["Sarandi", "4320107"],
		["Seberi", "4320206"],
		["Sede Nova", "4320230"],
		["Segredo", "4320263"],
		["Selbach", "4320305"],
		["Senador Salgado Filho", "4320321"],
		["Sentinela do Sul", "4320354"],
		["Serafina Corrêa", "4320404"],
		["Sério", "4320453"],
		["Sertão", "4320503"],
		["Sertão Santana", "4320552"],
		["Sete de Setembro", "4320578"],
		["Severiano de Almeida", "4320602"],
		["Silveira Martins", "4320651"],
		["Sinimbu", "4320677"],
		["Sobradinho", "4320701"],
		["Soledade", "4320800"],
		["Tabaí", "4320859"],
		["Tapejara", "4320909"],
		["Tapera", "4321006"],
		["Tapes", "4321105"],
		["Taquara", "4321204"],
		["Taquari", "4321303"],
		["Taquaruçu do Sul", "4321329"],
		["Tavares", "4321352"],
		["Tenente Portela", "4321402"],
		["Terra de Areia", "4321436"],
		["Teutônia", "4321451"],
		["Tio Hugo", "4321469"],
		["Tiradentes do Sul", "4321477"],
		["Toropi", "4321493"],
		["Torres", "4321501"],
		["Tramandaí", "4321600"],
		["Travesseiro", "4321626"],
		["Três Arroios", "4321634"],
		["Três Cachoeiras", "4321667"],
		["Três Coroas", "4321709"],
		["Três de Maio", "4321808"],
		["Três Forquilhas", "4321832"],
		["Três Palmeiras", "4321857"],
		["Três Passos", "4321907"],
		["Trindade do Sul", "4321956"],
		["Triunfo", "4322004"],
		["Tucunduva", "4322103"],
		["Tunas", "4322152"],
		["Tupanci do Sul", "4322186"],
		["Tupanciretã", "4322202"],
		["Tupandi", "4322251"],
		["Tuparendi", "4322301"],
		["Turuçu", "4322327"],
		["Ubiretama", "4322343"],
		["União da Serra", "4322350"],
		["Unistalda", "4322376"],
		["Uruguaiana", "4322400"],
		["Vacaria", "4322509"],
		["Vale do Sol", "4322533"],
		["Vale Real", "4322541"],
		["Vale Verde", "4322525"],
		["Vanini", "4322558"],
		["Venâncio Aires", "4322608"],
		["Vera Cruz", "4322707"],
		["Veranópolis", "4322806"],
		["Vespasiano Corrêa", "4322855"],
		["Viadutos", "4322905"],
		["Viamão", "4323002"],
		["Vicente Dutra", "4323101"],
		["Victor Graeff", "4323200"],
		["Vila Flores", "4323309"],
		["Vila Lângaro", "4323358"],
		["Vila Maria", "4323408"],
		["Vila Nova do Sul", "4323457"],
		["Vista Alegre", "4323507"],
		["Vista Alegre do Prata", "4323606"],
		["Vista Gaúcha", "4323705"],
		["Vitória das Missões", "4323754"],
		["Westfália", "4323770"],
		["Xangri-lá", "4323804"],
	],
	SC: [
		["Abdon Batista", "4200051"],
		["Abelardo Luz", "4200101"],
		["Agrolândia", "4200200"],
		["Agronômica", "4200309"],
		["Água Doce", "4200408"],
		["Águas de Chapecó", "4200507"],
		["Águas Frias", "4200556"],
		["Águas Mornas", "4200606"],
		["Alfredo Wagner", "4200705"],
		["Alto Bela Vista", "4200754"],
		["Anchieta", "4200804"],
		["Angelina", "4200903"],
		["Anita Garibaldi", "4201000"],
		["Anitápolis", "4201109"],
		["Antônio Carlos", "4201208"],
		["Apiúna", "4201257"],
		["Arabutã", "4201273"],
		["Araquari", "4201307"],
		["Araranguá", "4201406"],
		["Armazém", "4201505"],
		["Arroio Trinta", "4201604"],
		["Arvoredo", "4201653"],
		["Ascurra", "4201703"],
		["Atalanta", "4201802"],
		["Aurora", "4201901"],
		["Balneário Arroio do Silva", "4201950"],
		["Balneário Barra do Sul", "4202057"],
		["Balneário Camboriú", "4202008"],
		["Balneário Gaivota", "4202073"],
		["Balneário Piçarras", "4212809"],
		["Balneário Rincão", "4220000"],
		["Bandeirante", "4202081"],
		["Barra Bonita", "4202099"],
		["Barra Velha", "4202107"],
		["Bela Vista do Toldo", "4202131"],
		["Belmonte", "4202156"],
		["Benedito Novo", "4202206"],
		["Biguaçu", "4202305"],
		["Blumenau", "4202404"],
		["Bocaina do Sul", "4202438"],
		["Bom Jardim da Serra", "4202503"],
		["Bom Jesus", "4202537"],
		["Bom Jesus do Oeste", "4202578"],
		["Bom Retiro", "4202602"],
		["Bombinhas", "4202453"],
		["Botuverá", "4202701"],
		["Braço do Norte", "4202800"],
		["Braço do Trombudo", "4202859"],
		["Brunópolis", "4202875"],
		["Brusque", "4202909"],
		["Caçador", "4203006"],
		["Caibi", "4203105"],
		["Calmon", "4203154"],
		["Camboriú", "4203204"],
		["Campo Alegre", "4203303"],
		["Campo Belo do Sul", "4203402"],
		["Campo Erê", "4203501"],
		["Campos Novos", "4203600"],
		["Canelinha", "4203709"],
		["Canoinhas", "4203808"],
		["Capão Alto", "4203253"],
		["Capinzal", "4203907"],
		["Capivari de Baixo", "4203956"],
		["Catanduvas", "4204004"],
		["Caxambu do Sul", "4204103"],
		["Celso Ramos", "4204152"],
		["Cerro Negro", "4204178"],
		["Chapadão do Lageado", "4204194"],
		["Chapecó", "4204202"],
		["Cocal do Sul", "4204251"],
		["Concórdia", "4204301"],
		["Cordilheira Alta", "4204350"],
		["Coronel Freitas", "4204400"],
		["Coronel Martins", "4204459"],
		["Correia Pinto", "4204558"],
		["Corupá", "4204509"],
		["Criciúma", "4204608"],
		["Cunha Porã", "4204707"],
		["Cunhataí", "4204756"],
		["Curitibanos", "4204806"],
		["Descanso", "4204905"],
		["Dionísio Cerqueira", "4205001"],
		["Dona Emma", "4205100"],
		["Doutor Pedrinho", "4205159"],
		["Entre Rios", "4205175"],
		["Ermo", "4205191"],
		["Erval Velho", "4205209"],
		["Faxinal dos Guedes", "4205308"],
		["Flor do Sertão", "4205357"],
		["Florianópolis", "4205407"],
		["Formosa do Sul", "4205431"],
		["Forquilhinha", "4205456"],
		["Fraiburgo", "4205506"],
		["Frei Rogério", "4205555"],
		["Galvão", "4205605"],
		["Garopaba", "4205704"],
		["Garuva", "4205803"],
		["Gaspar", "4205902"],
		["Governador Celso Ramos", "4206009"],
		["Grão-Pará", "4206108"],
		["Gravatal", "4206207"],
		["Guabiruba", "4206306"],
		["Guaraciaba", "4206405"],
		["Guaramirim", "4206504"],
		["Guarujá do Sul", "4206603"],
		["Guatambú", "4206652"],
		["Herval d'Oeste", "4206702"],
		["Ibiam", "4206751"],
		["Ibicaré", "4206801"],
		["Ibirama", "4206900"],
		["Içara", "4207007"],
		["Ilhota", "4207106"],
		["Imaruí", "4207205"],
		["Imbituba", "4207304"],
		["Imbuia", "4207403"],
		["Indaial", "4207502"],
		["Iomerê", "4207577"],
		["Ipira", "4207601"],
		["Iporã do Oeste", "4207650"],
		["Ipuaçu", "4207684"],
		["Ipumirim", "4207700"],
		["Iraceminha", "4207759"],
		["Irani", "4207809"],
		["Irati", "4207858"],
		["Irineópolis", "4207908"],
		["Itá", "4208005"],
		["Itaiópolis", "4208104"],
		["Itajaí", "4208203"],
		["Itapema", "4208302"],
		["Itapiranga", "4208401"],
		["Itapoá", "4208450"],
		["Ituporanga", "4208500"],
		["Jaborá", "4208609"],
		["Jacinto Machado", "4208708"],
		["Jaguaruna", "4208807"],
		["Jaraguá do Sul", "4208906"],
		["Jardinópolis", "4208955"],
		["Joaçaba", "4209003"],
		["Joinville", "4209102"],
		["José Boiteux", "4209151"],
		["Jupiá", "4209177"],
		["Lacerdópolis", "4209201"],
		["Lages", "4209300"],
		["Laguna", "4209409"],
		["Lajeado Grande", "4209458"],
		["Laurentino", "4209508"],
		["Lauro Müller", "4209607"],
		["Lebon Régis", "4209706"],
		["Leoberto Leal", "4209805"],
		["Lindóia do Sul", "4209854"],
		["Lontras", "4209904"],
		["Luiz Alves", "4210001"],
		["Luzerna", "4210035"],
		["Macieira", "4210050"],
		["Mafra", "4210100"],
		["Major Gercino", "4210209"],
		["Major Vieira", "4210308"],
		["Maracajá", "4210407"],
		["Maravilha", "4210506"],
		["Marema", "4210555"],
		["Massaranduba", "4210605"],
		["Matos Costa", "4210704"],
		["Meleiro", "4210803"],
		["Mirim Doce", "4210852"],
		["Modelo", "4210902"],
		["Mondaí", "4211009"],
		["Monte Carlo", "4211058"],
		["Monte Castelo", "4211108"],
		["Morro da Fumaça", "4211207"],
		["Morro Grande", "4211256"],
		["Navegantes", "4211306"],
		["Nova Erechim", "4211405"],
		["Nova Itaberaba", "4211454"],
		["Nova Trento", "4211504"],
		["Nova Veneza", "4211603"],
		["Novo Horizonte", "4211652"],
		["Orleans", "4211702"],
		["Otacílio Costa", "4211751"],
		["Ouro", "4211801"],
		["Ouro Verde", "4211850"],
		["Paial", "4211876"],
		["Painel", "4211892"],
		["Palhoça", "4211900"],
		["Palma Sola", "4212007"],
		["Palmeira", "4212056"],
		["Palmitos", "4212106"],
		["Papanduva", "4212205"],
		["Paraíso", "4212239"],
		["Passo de Torres", "4212254"],
		["Passos Maia", "4212270"],
		["Paulo Lopes", "4212304"],
		["Pedras Grandes", "4212403"],
		["Penha", "4212502"],
		["Peritiba", "4212601"],
		["Pescaria Brava", "4212650"],
		["Petrolândia", "4212700"],
		["Pinhalzinho", "4212908"],
		["Pinheiro Preto", "4213005"],
		["Piratuba", "4213104"],
		["Planalto Alegre", "4213153"],
		["Pomerode", "4213203"],
		["Ponte Alta", "4213302"],
		["Ponte Alta do Norte", "4213351"],
		["Ponte Serrada", "4213401"],
		["Porto Belo", "4213500"],
		["Porto União", "4213609"],
		["Pouso Redondo", "4213708"],
		["Praia Grande", "4213807"],
		["Presidente Castello Branco", "4213906"],
		["Presidente Getúlio", "4214003"],
		["Presidente Nereu", "4214102"],
		["Princesa", "4214151"],
		["Quilombo", "4214201"],
		["Rancho Queimado", "4214300"],
		["Rio das Antas", "4214409"],
		["Rio do Campo", "4214508"],
		["Rio do Oeste", "4214607"],
		["Rio do Sul", "4214805"],
		["Rio dos Cedros", "4214706"],
		["Rio Fortuna", "4214904"],
		["Rio Negrinho", "4215000"],
		["Rio Rufino", "4215059"],
		["Riqueza", "4215075"],
		["Rodeio", "4215109"],
		["Romelândia", "4215208"],
		["Salete", "4215307"],
		["Saltinho", "4215356"],
		["Salto Veloso", "4215406"],
		["Sangão", "4215455"],
		["Santa Cecília", "4215505"],
		["Santa Helena", "4215554"],
		["Santa Rosa de Lima", "4215604"],
		["Santa Rosa do Sul", "4215653"],
		["Santa Terezinha", "4215679"],
		["Santa Terezinha do Progresso", "4215687"],
		["Santiago do Sul", "4215695"],
		["Santo Amaro da Imperatriz", "4215703"],
		["São Bento do Sul", "4215802"],
		["São Bernardino", "4215752"],
		["São Bonifácio", "4215901"],
		["São Carlos", "4216008"],
		["São Cristóvão do Sul", "4216057"],
		["São Domingos", "4216107"],
		["São Francisco do Sul", "4216206"],
		["São João Batista", "4216305"],
		["São João do Itaperiú", "4216354"],
		["São João do Oeste", "4216255"],
		["São João do Sul", "4216404"],
		["São Joaquim", "4216503"],
		["São José", "4216602"],
		["São José do Cedro", "4216701"],
		["São José do Cerrito", "4216800"],
		["São Lourenço do Oeste", "4216909"],
		["São Ludgero", "4217006"],
		["São Martinho", "4217105"],
		["São Miguel da Boa Vista", "4217154"],
		["São Miguel do Oeste", "4217204"],
		["São Pedro de Alcântara", "4217253"],
		["Saudades", "4217303"],
		["Schroeder", "4217402"],
		["Seara", "4217501"],
		["Serra Alta", "4217550"],
		["Siderópolis", "4217600"],
		["Sombrio", "4217709"],
		["Sul Brasil", "4217758"],
		["Taió", "4217808"],
		["Tangará", "4217907"],
		["Tigrinhos", "4217956"],
		["Tijucas", "4218004"],
		["Timbé do Sul", "4218103"],
		["Timbó", "4218202"],
		["Timbó Grande", "4218251"],
		["Três Barras", "4218301"],
		["Treviso", "4218350"],
		["Treze de Maio", "4218400"],
		["Treze Tílias", "4218509"],
		["Trombudo Central", "4218608"],
		["Tubarão", "4218707"],
		["Tunápolis", "4218756"],
		["Turvo", "4218806"],
		["União do Oeste", "4218855"],
		["Urubici", "4218905"],
		["Urupema", "4218954"],
		["Urussanga", "4219002"],
		["Vargeão", "4219101"],
		["Vargem", "4219150"],
		["Vargem Bonita", "4219176"],
		["Vidal Ramos", "4219200"],
		["Videira", "4219309"],
		["Vitor Meireles", "4219358"],
		["Witmarsum", "4219408"],
		["Xanxerê", "4219507"],
		["Xavantina", "4219606"],
		["Xaxim", "4219705"],
		["Zortéa", "4219853"],
	],
	SE: [
		["Amparo do São Francisco", "2800100"],
		["Aquidabã", "2800209"],
		["Aracaju", "2800308"],
		["Arauá", "2800407"],
		["Areia Branca", "2800506"],
		["Barra dos Coqueiros", "2800605"],
		["Boquim", "2800670"],
		["Brejo Grande", "2800704"],
		["Campo do Brito", "2801009"],
		["Canhoba", "2801108"],
		["Canindé de São Francisco", "2801207"],
		["Capela", "2801306"],
		["Carira", "2801405"],
		["Carmópolis", "2801504"],
		["Cedro de São João", "2801603"],
		["Cristinápolis", "2801702"],
		["Cumbe", "2801900"],
		["Divina Pastora", "2802007"],
		["Estância", "2802106"],
		["Feira Nova", "2802205"],
		["Frei Paulo", "2802304"],
		["Gararu", "2802403"],
		["General Maynard", "2802502"],
		["Graccho Cardoso", "2802601"],
		["Ilha das Flores", "2802700"],
		["Indiaroba", "2802809"],
		["Itabaiana", "2802908"],
		["Itabaianinha", "2803005"],
		["Itabi", "2803104"],
		["Itaporanga d'Ajuda", "2803203"],
		["Japaratuba", "2803302"],
		["Japoatã", "2803401"],
		["Lagarto", "2803500"],
		["Laranjeiras", "2803609"],
		["Macambira", "2803708"],
		["Malhada dos Bois", "2803807"],
		["Malhador", "2803906"],
		["Maruim", "2804003"],
		["Moita Bonita", "2804102"],
		["Monte Alegre de Sergipe", "2804201"],
		["Muribeca", "2804300"],
		["Neópolis", "2804409"],
		["Nossa Senhora Aparecida", "2804458"],
		["Nossa Senhora da Glória", "2804508"],
		["Nossa Senhora das Dores", "2804607"],
		["Nossa Senhora de Lourdes", "2804706"],
		["Nossa Senhora do Socorro", "2804805"],
		["Pacatuba", "2804904"],
		["Pedra Mole", "2805000"],
		["Pedrinhas", "2805109"],
		["Pinhão", "2805208"],
		["Pirambu", "2805307"],
		["Poço Redondo", "2805406"],
		["Poço Verde", "2805505"],
		["Porto da Folha", "2805604"],
		["Propriá", "2805703"],
		["Riachão do Dantas", "2805802"],
		["Riachuelo", "2805901"],
		["Ribeirópolis", "2806008"],
		["Rosário do Catete", "2806107"],
		["Salgado", "2806206"],
		["Santa Luzia do Itanhy", "2806305"],
		["Santa Rosa de Lima", "2806503"],
		["Santana do São Francisco", "2806404"],
		["Santo Amaro das Brotas", "2806602"],
		["São Cristóvão", "2806701"],
		["São Domingos", "2806800"],
		["São Francisco", "2806909"],
		["São Miguel do Aleixo", "2807006"],
		["Simão Dias", "2807105"],
		["Siriri", "2807204"],
		["Telha", "2807303"],
		["Tobias Barreto", "2807402"],
		["Tomar do Geru", "2807501"],
		["Umbaúba", "2807600"],
	],
	SP: [
		["Adamantina", "3500105"],
		["Adolfo", "3500204"],
		["Aguaí", "3500303"],
		["Águas da Prata", "3500402"],
		["Águas de Lindóia", "3500501"],
		["Águas de Santa Bárbara", "3500550"],
		["Águas de São Pedro", "3500600"],
		["Agudos", "3500709"],
		["Alambari", "3500758"],
		["Alfredo Marcondes", "3500808"],
		["Altair", "3500907"],
		["Altinópolis", "3501004"],
		["Alto Alegre", "3501103"],
		["Alumínio", "3501152"],
		["Álvares Florence", "3501202"],
		["Álvares Machado", "3501301"],
		["Álvaro de Carvalho", "3501400"],
		["Alvinlândia", "3501509"],
		["Americana", "3501608"],
		["Américo Brasiliense", "3501707"],
		["Américo de Campos", "3501806"],
		["Amparo", "3501905"],
		["Analândia", "3502002"],
		["Andradina", "3502101"],
		["Angatuba", "3502200"],
		["Anhembi", "3502309"],
		["Anhumas", "3502408"],
		["Aparecida", "3502507"],
		["Aparecida d'Oeste", "3502606"],
		["Apiaí", "3502705"],
		["Araçariguama", "3502754"],
		["Araçatuba", "3502804"],
		["Araçoiaba da Serra", "3502903"],
		["Aramina", "3503000"],
		["Arandu", "3503109"],
		["Arapeí", "3503158"],
		["Araraquara", "3503208"],
		["Araras", "3503307"],
		["Arco-Íris", "3503356"],
		["Arealva", "3503406"],
		["Areias", "3503505"],
		["Areiópolis", "3503604"],
		["Ariranha", "3503703"],
		["Artur Nogueira", "3503802"],
		["Arujá", "3503901"],
		["Aspásia", "3503950"],
		["Assis", "3504008"],
		["Atibaia", "3504107"],
		["Auriflama", "3504206"],
		["Avaí", "3504305"],
		["Avanhandava", "3504404"],
		["Avaré", "3504503"],
		["Bady Bassitt", "3504602"],
		["Balbinos", "3504701"],
		["Bálsamo", "3504800"],
		["Bananal", "3504909"],
		["Barão de Antonina", "3505005"],
		["Barbosa", "3505104"],
		["Bariri", "3505203"],
		["Barra Bonita", "3505302"],
		["Barra do Chapéu", "3505351"],
		["Barra do Turvo", "3505401"],
		["Barretos", "3505500"],
		["Barrinha", "3505609"],
		["Barueri", "3505708"],
		["Bastos", "3505807"],
		["Batatais", "3505906"],
		["Bauru", "3506003"],
		["Bebedouro", "3506102"],
		["Bento de Abreu", "3506201"],
		["Bernardino de Campos", "3506300"],
		["Bertioga", "3506359"],
		["Bilac", "3506409"],
		["Birigui", "3506508"],
		["Biritiba Mirim", "3506607"],
		["Boa Esperança do Sul", "3506706"],
		["Bocaina", "3506805"],
		["Bofete", "3506904"],
		["Boituva", "3507001"],
		["Bom Jesus dos Perdões", "3507100"],
		["Bom Sucesso de Itararé", "3507159"],
		["Borá", "3507209"],
		["Boracéia", "3507308"],
		["Borborema", "3507407"],
		["Borebi", "3507456"],
		["Botucatu", "3507506"],
		["Bragança Paulista", "3507605"],
		["Braúna", "3507704"],
		["Brejo Alegre", "3507753"],
		["Brodowski", "3507803"],
		["Brotas", "3507902"],
		["Buri", "3508009"],
		["Buritama", "3508108"],
		["Buritizal", "3508207"],
		["Cabrália Paulista", "3508306"],
		["Cabreúva", "3508405"],
		["Caçapava", "3508504"],
		["Cachoeira Paulista", "3508603"],
		["Caconde", "3508702"],
		["Cafelândia", "3508801"],
		["Caiabu", "3508900"],
		["Caieiras", "3509007"],
		["Caiuá", "3509106"],
		["Cajamar", "3509205"],
		["Cajati", "3509254"],
		["Cajobi", "3509304"],
		["Cajuru", "3509403"],
		["Campina do Monte Alegre", "3509452"],
		["Campinas", "3509502"],
		["Campo Limpo Paulista", "3509601"],
		["Campos do Jordão", "3509700"],
		["Campos Novos Paulista", "3509809"],
		["Cananéia", "3509908"],
		["Canas", "3509957"],
		["Cândido Mota", "3510005"],
		["Cândido Rodrigues", "3510104"],
		["Canitar", "3510153"],
		["Capão Bonito", "3510203"],
		["Capela do Alto", "3510302"],
		["Capivari", "3510401"],
		["Caraguatatuba", "3510500"],
		["Carapicuíba", "3510609"],
		["Cardoso", "3510708"],
		["Casa Branca", "3510807"],
		["Cássia dos Coqueiros", "3510906"],
		["Castilho", "3511003"],
		["Catanduva", "3511102"],
		["Catiguá", "3511201"],
		["Cedral", "3511300"],
		["Cerqueira César", "3511409"],
		["Cerquilho", "3511508"],
		["Cesário Lange", "3511607"],
		["Charqueada", "3511706"],
		["Chavantes", "3557204"],
		["Clementina", "3511904"],
		["Colina", "3512001"],
		["Colômbia", "3512100"],
		["Conchal", "3512209"],
		["Conchas", "3512308"],
		["Cordeirópolis", "3512407"],
		["Coroados", "3512506"],
		["Coronel Macedo", "3512605"],
		["Corumbataí", "3512704"],
		["Cosmópolis", "3512803"],
		["Cosmorama", "3512902"],
		["Cotia", "3513009"],
		["Cravinhos", "3513108"],
		["Cristais Paulista", "3513207"],
		["Cruzália", "3513306"],
		["Cruzeiro", "3513405"],
		["Cubatão", "3513504"],
		["Cunha", "3513603"],
		["Descalvado", "3513702"],
		["Diadema", "3513801"],
		["Dirce Reis", "3513850"],
		["Divinolândia", "3513900"],
		["Dobrada", "3514007"],
		["Dois Córregos", "3514106"],
		["Dolcinópolis", "3514205"],
		["Dourado", "3514304"],
		["Dracena", "3514403"],
		["Duartina", "3514502"],
		["Dumont", "3514601"],
		["Echaporã", "3514700"],
		["Eldorado", "3514809"],
		["Elias Fausto", "3514908"],
		["Elisiário", "3514924"],
		["Embaúba", "3514957"],
		["Embu das Artes", "3515004"],
		["Embu-Guaçu", "3515103"],
		["Emilianópolis", "3515129"],
		["Engenheiro Coelho", "3515152"],
		["Espírito Santo do Pinhal", "3515186"],
		["Espírito Santo do Turvo", "3515194"],
		["Estiva Gerbi", "3557303"],
		["Estrela d'Oeste", "3515202"],
		["Estrela do Norte", "3515301"],
		["Euclides da Cunha Paulista", "3515350"],
		["Fartura", "3515400"],
		["Fernando Prestes", "3515608"],
		["Fernandópolis", "3515509"],
		["Fernão", "3515657"],
		["Ferraz de Vasconcelos", "3515707"],
		["Flora Rica", "3515806"],
		["Floreal", "3515905"],
		["Flórida Paulista", "3516002"],
		["Florínea", "3516101"],
		["Franca", "3516200"],
		["Francisco Morato", "3516309"],
		["Franco da Rocha", "3516408"],
		["Gabriel Monteiro", "3516507"],
		["Gália", "3516606"],
		["Garça", "3516705"],
		["Gastão Vidigal", "3516804"],
		["Gavião Peixoto", "3516853"],
		["General Salgado", "3516903"],
		["Getulina", "3517000"],
		["Glicério", "3517109"],
		["Guaiçara", "3517208"],
		["Guaimbê", "3517307"],
		["Guaíra", "3517406"],
		["Guapiaçu", "3517505"],
		["Guapiara", "3517604"],
		["Guará", "3517703"],
		["Guaraçaí", "3517802"],
		["Guaraci", "3517901"],
		["Guarani d'Oeste", "3518008"],
		["Guarantã", "3518107"],
		["Guararapes", "3518206"],
		["Guararema", "3518305"],
		["Guaratinguetá", "3518404"],
		["Guareí", "3518503"],
		["Guariba", "3518602"],
		["Guarujá", "3518701"],
		["Guarulhos", "3518800"],
		["Guatapará", "3518859"],
		["Guzolândia", "3518909"],
		["Herculândia", "3519006"],
		["Holambra", "3519055"],
		["Hortolândia", "3519071"],
		["Iacanga", "3519105"],
		["Iacri", "3519204"],
		["Iaras", "3519253"],
		["Ibaté", "3519303"],
		["Ibirá", "3519402"],
		["Ibirarema", "3519501"],
		["Ibitinga", "3519600"],
		["Ibiúna", "3519709"],
		["Icém", "3519808"],
		["Iepê", "3519907"],
		["Igaraçu do Tietê", "3520004"],
		["Igarapava", "3520103"],
		["Igaratá", "3520202"],
		["Iguape", "3520301"],
		["Ilha Comprida", "3520426"],
		["Ilha Solteira", "3520442"],
		["Ilhabela", "3520400"],
		["Indaiatuba", "3520509"],
		["Indiana", "3520608"],
		["Indiaporã", "3520707"],
		["Inúbia Paulista", "3520806"],
		["Ipaussu", "3520905"],
		["Iperó", "3521002"],
		["Ipeúna", "3521101"],
		["Ipiguá", "3521150"],
		["Iporanga", "3521200"],
		["Ipuã", "3521309"],
		["Iracemápolis", "3521408"],
		["Irapuã", "3521507"],
		["Irapuru", "3521606"],
		["Itaberá", "3521705"],
		["Itaí", "3521804"],
		["Itajobi", "3521903"],
		["Itaju", "3522000"],
		["Itanhaém", "3522109"],
		["Itaoca", "3522158"],
		["Itapecerica da Serra", "3522208"],
		["Itapetininga", "3522307"],
		["Itapeva", "3522406"],
		["Itapevi", "3522505"],
		["Itapira", "3522604"],
		["Itapirapuã Paulista", "3522653"],
		["Itápolis", "3522703"],
		["Itaporanga", "3522802"],
		["Itapuí", "3522901"],
		["Itapura", "3523008"],
		["Itaquaquecetuba", "3523107"],
		["Itararé", "3523206"],
		["Itariri", "3523305"],
		["Itatiba", "3523404"],
		["Itatinga", "3523503"],
		["Itirapina", "3523602"],
		["Itirapuã", "3523701"],
		["Itobi", "3523800"],
		["Itu", "3523909"],
		["Itupeva", "3524006"],
		["Ituverava", "3524105"],
		["Jaborandi", "3524204"],
		["Jaboticabal", "3524303"],
		["Jacareí", "3524402"],
		["Jaci", "3524501"],
		["Jacupiranga", "3524600"],
		["Jaguariúna", "3524709"],
		["Jales", "3524808"],
		["Jambeiro", "3524907"],
		["Jandira", "3525003"],
		["Jardinópolis", "3525102"],
		["Jarinu", "3525201"],
		["Jaú", "3525300"],
		["Jeriquara", "3525409"],
		["Joanópolis", "3525508"],
		["João Ramalho", "3525607"],
		["José Bonifácio", "3525706"],
		["Júlio Mesquita", "3525805"],
		["Jumirim", "3525854"],
		["Jundiaí", "3525904"],
		["Junqueirópolis", "3526001"],
		["Juquiá", "3526100"],
		["Juquitiba", "3526209"],
		["Lagoinha", "3526308"],
		["Laranjal Paulista", "3526407"],
		["Lavínia", "3526506"],
		["Lavrinhas", "3526605"],
		["Leme", "3526704"],
		["Lençóis Paulista", "3526803"],
		["Limeira", "3526902"],
		["Lindóia", "3527009"],
		["Lins", "3527108"],
		["Lorena", "3527207"],
		["Lourdes", "3527256"],
		["Louveira", "3527306"],
		["Lucélia", "3527405"],
		["Lucianópolis", "3527504"],
		["Luís Antônio", "3527603"],
		["Luiziânia", "3527702"],
		["Lupércio", "3527801"],
		["Lutécia", "3527900"],
		["Macatuba", "3528007"],
		["Macaubal", "3528106"],
		["Macedônia", "3528205"],
		["Magda", "3528304"],
		["Mairinque", "3528403"],
		["Mairiporã", "3528502"],
		["Manduri", "3528601"],
		["Marabá Paulista", "3528700"],
		["Maracaí", "3528809"],
		["Marapoama", "3528858"],
		["Mariápolis", "3528908"],
		["Marília", "3529005"],
		["Marinópolis", "3529104"],
		["Martinópolis", "3529203"],
		["Matão", "3529302"],
		["Mauá", "3529401"],
		["Mendonça", "3529500"],
		["Meridiano", "3529609"],
		["Mesópolis", "3529658"],
		["Miguelópolis", "3529708"],
		["Mineiros do Tietê", "3529807"],
		["Mira Estrela", "3530003"],
		["Miracatu", "3529906"],
		["Mirandópolis", "3530102"],
		["Mirante do Paranapanema", "3530201"],
		["Mirassol", "3530300"],
		["Mirassolândia", "3530409"],
		["Mococa", "3530508"],
		["Mogi das Cruzes", "3530607"],
		["Mogi Guaçu", "3530706"],
		["Mogi Mirim", "3530805"],
		["Mombuca", "3530904"],
		["Monções", "3531001"],
		["Mongaguá", "3531100"],
		["Monte Alegre do Sul", "3531209"],
		["Monte Alto", "3531308"],
		["Monte Aprazível", "3531407"],
		["Monte Azul Paulista", "3531506"],
		["Monte Castelo", "3531605"],
		["Monte Mor", "3531803"],
		["Monteiro Lobato", "3531704"],
		["Morro Agudo", "3531902"],
		["Morungaba", "3532009"],
		["Motuca", "3532058"],
		["Murutinga do Sul", "3532108"],
		["Nantes", "3532157"],
		["Narandiba", "3532207"],
		["Natividade da Serra", "3532306"],
		["Nazaré Paulista", "3532405"],
		["Neves Paulista", "3532504"],
		["Nhandeara", "3532603"],
		["Nipoã", "3532702"],
		["Nova Aliança", "3532801"],
		["Nova Campina", "3532827"],
		["Nova Canaã Paulista", "3532843"],
		["Nova Castilho", "3532868"],
		["Nova Europa", "3532900"],
		["Nova Granada", "3533007"],
		["Nova Guataporanga", "3533106"],
		["Nova Independência", "3533205"],
		["Nova Luzitânia", "3533304"],
		["Nova Odessa", "3533403"],
		["Novais", "3533254"],
		["Novo Horizonte", "3533502"],
		["Nuporanga", "3533601"],
		["Ocauçu", "3533700"],
		["Óleo", "3533809"],
		["Olímpia", "3533908"],
		["Onda Verde", "3534005"],
		["Oriente", "3534104"],
		["Orindiúva", "3534203"],
		["Orlândia", "3534302"],
		["Osasco", "3534401"],
		["Oscar Bressane", "3534500"],
		["Osvaldo Cruz", "3534609"],
		["Ourinhos", "3534708"],
		["Ouro Verde", "3534807"],
		["Ouroeste", "3534757"],
		["Pacaembu", "3534906"],
		["Palestina", "3535002"],
		["Palmares Paulista", "3535101"],
		["Palmeira d'Oeste", "3535200"],
		["Palmital", "3535309"],
		["Panorama", "3535408"],
		["Paraguaçu Paulista", "3535507"],
		["Paraibuna", "3535606"],
		["Paraíso", "3535705"],
		["Paranapanema", "3535804"],
		["Paranapuã", "3535903"],
		["Parapuã", "3536000"],
		["Pardinho", "3536109"],
		["Pariquera-Açu", "3536208"],
		["Parisi", "3536257"],
		["Patrocínio Paulista", "3536307"],
		["Paulicéia", "3536406"],
		["Paulínia", "3536505"],
		["Paulistânia", "3536570"],
		["Paulo de Faria", "3536604"],
		["Pederneiras", "3536703"],
		["Pedra Bela", "3536802"],
		["Pedranópolis", "3536901"],
		["Pedregulho", "3537008"],
		["Pedreira", "3537107"],
		["Pedrinhas Paulista", "3537156"],
		["Pedro de Toledo", "3537206"],
		["Penápolis", "3537305"],
		["Pereira Barreto", "3537404"],
		["Pereiras", "3537503"],
		["Peruíbe", "3537602"],
		["Piacatu", "3537701"],
		["Piedade", "3537800"],
		["Pilar do Sul", "3537909"],
		["Pindamonhangaba", "3538006"],
		["Pindorama", "3538105"],
		["Pinhalzinho", "3538204"],
		["Piquerobi", "3538303"],
		["Piquete", "3538501"],
		["Piracaia", "3538600"],
		["Piracicaba", "3538709"],
		["Piraju", "3538808"],
		["Pirajuí", "3538907"],
		["Pirangi", "3539004"],
		["Pirapora do Bom Jesus", "3539103"],
		["Pirapozinho", "3539202"],
		["Pirassununga", "3539301"],
		["Piratininga", "3539400"],
		["Pitangueiras", "3539509"],
		["Planalto", "3539608"],
		["Platina", "3539707"],
		["Poá", "3539806"],
		["Poloni", "3539905"],
		["Pompéia", "3540002"],
		["Pongaí", "3540101"],
		["Pontal", "3540200"],
		["Pontalinda", "3540259"],
		["Pontes Gestal", "3540309"],
		["Populina", "3540408"],
		["Porangaba", "3540507"],
		["Porto Feliz", "3540606"],
		["Porto Ferreira", "3540705"],
		["Potim", "3540754"],
		["Potirendaba", "3540804"],
		["Pracinha", "3540853"],
		["Pradópolis", "3540903"],
		["Praia Grande", "3541000"],
		["Pratânia", "3541059"],
		["Presidente Alves", "3541109"],
		["Presidente Bernardes", "3541208"],
		["Presidente Epitácio", "3541307"],
		["Presidente Prudente", "3541406"],
		["Presidente Venceslau", "3541505"],
		["Promissão", "3541604"],
		["Quadra", "3541653"],
		["Quatá", "3541703"],
		["Queiroz", "3541802"],
		["Queluz", "3541901"],
		["Quintana", "3542008"],
		["Rafard", "3542107"],
		["Rancharia", "3542206"],
		["Redenção da Serra", "3542305"],
		["Regente Feijó", "3542404"],
		["Reginópolis", "3542503"],
		["Registro", "3542602"],
		["Restinga", "3542701"],
		["Ribeira", "3542800"],
		["Ribeirão Bonito", "3542909"],
		["Ribeirão Branco", "3543006"],
		["Ribeirão Corrente", "3543105"],
		["Ribeirão do Sul", "3543204"],
		["Ribeirão dos Índios", "3543238"],
		["Ribeirão Grande", "3543253"],
		["Ribeirão Pires", "3543303"],
		["Ribeirão Preto", "3543402"],
		["Rifaina", "3543600"],
		["Rincão", "3543709"],
		["Rinópolis", "3543808"],
		["Rio Claro", "3543907"],
		["Rio das Pedras", "3544004"],
		["Rio Grande da Serra", "3544103"],
		["Riolândia", "3544202"],
		["Riversul", "3543501"],
		["Rosana", "3544251"],
		["Roseira", "3544301"],
		["Rubiácea", "3544400"],
		["Rubinéia", "3544509"],
		["Sabino", "3544608"],
		["Sagres", "3544707"],
		["Sales", "3544806"],
		["Sales Oliveira", "3544905"],
		["Salesópolis", "3545001"],
		["Salmourão", "3545100"],
		["Saltinho", "3545159"],
		["Salto", "3545209"],
		["Salto de Pirapora", "3545308"],
		["Salto Grande", "3545407"],
		["Sandovalina", "3545506"],
		["Santa Adélia", "3545605"],
		["Santa Albertina", "3545704"],
		["Santa Bárbara d'Oeste", "3545803"],
		["Santa Branca", "3546009"],
		["Santa Clara d'Oeste", "3546108"],
		["Santa Cruz da Conceição", "3546207"],
		["Santa Cruz da Esperança", "3546256"],
		["Santa Cruz das Palmeiras", "3546306"],
		["Santa Cruz do Rio Pardo", "3546405"],
		["Santa Ernestina", "3546504"],
		["Santa Fé do Sul", "3546603"],
		["Santa Gertrudes", "3546702"],
		["Santa Isabel", "3546801"],
		["Santa Lúcia", "3546900"],
		["Santa Maria da Serra", "3547007"],
		["Santa Mercedes", "3547106"],
		["Santa Rita d'Oeste", "3547403"],
		["Santa Rita do Passa Quatro", "3547502"],
		["Santa Rosa de Viterbo", "3547601"],
		["Santa Salete", "3547650"],
		["Santana da Ponte Pensa", "3547205"],
		["Santana de Parnaíba", "3547304"],
		["Santo Anastácio", "3547700"],
		["Santo André", "3547809"],
		["Santo Antônio da Alegria", "3547908"],
		["Santo Antônio de Posse", "3548005"],
		["Santo Antônio do Aracanguá", "3548054"],
		["Santo Antônio do Jardim", "3548104"],
		["Santo Antônio do Pinhal", "3548203"],
		["Santo Expedito", "3548302"],
		["Santópolis do Aguapeí", "3548401"],
		["Santos", "3548500"],
		["São Bento do Sapucaí", "3548609"],
		["São Bernardo do Campo", "3548708"],
		["São Caetano do Sul", "3548807"],
		["São Carlos", "3548906"],
		["São Francisco", "3549003"],
		["São João da Boa Vista", "3549102"],
		["São João das Duas Pontes", "3549201"],
		["São João de Iracema", "3549250"],
		["São João do Pau d'Alho", "3549300"],
		["São Joaquim da Barra", "3549409"],
		["São José da Bela Vista", "3549508"],
		["São José do Barreiro", "3549607"],
		["São José do Rio Pardo", "3549706"],
		["São José do Rio Preto", "3549805"],
		["São José dos Campos", "3549904"],
		["São Lourenço da Serra", "3549953"],
		["São Luiz do Paraitinga", "3550001"],
		["São Manuel", "3550100"],
		["São Miguel Arcanjo", "3550209"],
		["São Paulo", "3550308"],
		["São Pedro", "3550407"],
		["São Pedro do Turvo", "3550506"],
		["São Roque", "3550605"],
		["São Sebastião", "3550704"],
		["São Sebastião da Grama", "3550803"],
		["São Simão", "3550902"],
		["São Vicente", "3551009"],
		["Sarapuí", "3551108"],
		["Sarutaiá", "3551207"],
		["Sebastianópolis do Sul", "3551306"],
		["Serra Azul", "3551405"],
		["Serra Negra", "3551603"],
		["Serrana", "3551504"],
		["Sertãozinho", "3551702"],
		["Sete Barras", "3551801"],
		["Severínia", "3551900"],
		["Silveiras", "3552007"],
		["Socorro", "3552106"],
		["Sorocaba", "3552205"],
		["Sud Mennucci", "3552304"],
		["Sumaré", "3552403"],
		["Suzanápolis", "3552551"],
		["Suzano", "3552502"],
		["Tabapuã", "3552601"],
		["Tabatinga", "3552700"],
		["Taboão da Serra", "3552809"],
		["Taciba", "3552908"],
		["Taguaí", "3553005"],
		["Taiaçu", "3553104"],
		["Taiúva", "3553203"],
		["Tambaú", "3553302"],
		["Tanabi", "3553401"],
		["Tapiraí", "3553500"],
		["Tapiratiba", "3553609"],
		["Taquaral", "3553658"],
		["Taquaritinga", "3553708"],
		["Taquarituba", "3553807"],
		["Taquarivaí", "3553856"],
		["Tarabai", "3553906"],
		["Tarumã", "3553955"],
		["Tatuí", "3554003"],
		["Taubaté", "3554102"],
		["Tejupá", "3554201"],
		["Teodoro Sampaio", "3554300"],
		["Terra Roxa", "3554409"],
		["Tietê", "3554508"],
		["Timburi", "3554607"],
		["Torre de Pedra", "3554656"],
		["Torrinha", "3554706"],
		["Trabiju", "3554755"],
		["Tremembé", "3554805"],
		["Três Fronteiras", "3554904"],
		["Tuiuti", "3554953"],
		["Tupã", "3555000"],
		["Tupi Paulista", "3555109"],
		["Turiúba", "3555208"],
		["Turmalina", "3555307"],
		["Ubarana", "3555356"],
		["Ubatuba", "3555406"],
		["Ubirajara", "3555505"],
		["Uchoa", "3555604"],
		["União Paulista", "3555703"],
		["Urânia", "3555802"],
		["Uru", "3555901"],
		["Urupês", "3556008"],
		["Valentim Gentil", "3556107"],
		["Valinhos", "3556206"],
		["Valparaíso", "3556305"],
		["Vargem", "3556354"],
		["Vargem Grande do Sul", "3556404"],
		["Vargem Grande Paulista", "3556453"],
		["Várzea Paulista", "3556503"],
		["Vera Cruz", "3556602"],
		["Vinhedo", "3556701"],
		["Viradouro", "3556800"],
		["Vista Alegre do Alto", "3556909"],
		["Vitória Brasil", "3556958"],
		["Votorantim", "3557006"],
		["Votuporanga", "3557105"],
		["Zacarias", "3557154"],
	],
	TO: [
		["Abreulândia", "1700251"],
		["Aguiarnópolis", "1700301"],
		["Aliança do Tocantins", "1700350"],
		["Almas", "1700400"],
		["Alvorada", "1700707"],
		["Ananás", "1701002"],
		["Angico", "1701051"],
		["Aparecida do Rio Negro", "1701101"],
		["Aragominas", "1701309"],
		["Araguacema", "1701903"],
		["Araguaçu", "1702000"],
		["Araguaína", "1702109"],
		["Araguanã", "1702158"],
		["Araguatins", "1702208"],
		["Arapoema", "1702307"],
		["Arraias", "1702406"],
		["Augustinópolis", "1702554"],
		["Aurora do Tocantins", "1702703"],
		["Axixá do Tocantins", "1702901"],
		["Babaçulândia", "1703008"],
		["Bandeirantes do Tocantins", "1703057"],
		["Barra do Ouro", "1703073"],
		["Barrolândia", "1703107"],
		["Bernardo Sayão", "1703206"],
		["Bom Jesus do Tocantins", "1703305"],
		["Brasilândia do Tocantins", "1703602"],
		["Brejinho de Nazaré", "1703701"],
		["Buriti do Tocantins", "1703800"],
		["Cachoeirinha", "1703826"],
		["Campos Lindos", "1703842"],
		["Cariri do Tocantins", "1703867"],
		["Carmolândia", "1703883"],
		["Carrasco Bonito", "1703891"],
		["Caseara", "1703909"],
		["Centenário", "1704105"],
		["Chapada da Natividade", "1705102"],
		["Chapada de Areia", "1704600"],
		["Colinas do Tocantins", "1705508"],
		["Colméia", "1716703"],
		["Combinado", "1705557"],
		["Conceição do Tocantins", "1705607"],
		["Couto Magalhães", "1706001"],
		["Cristalândia", "1706100"],
		["Crixás do Tocantins", "1706258"],
		["Darcinópolis", "1706506"],
		["Dianópolis", "1707009"],
		["Divinópolis do Tocantins", "1707108"],
		["Dois Irmãos do Tocantins", "1707207"],
		["Dueré", "1707306"],
		["Esperantina", "1707405"],
		["Fátima", "1707553"],
		["Figueirópolis", "1707652"],
		["Filadélfia", "1707702"],
		["Formoso do Araguaia", "1708205"],
		["Goianorte", "1708304"],
		["Goiatins", "1709005"],
		["Guaraí", "1709302"],
		["Gurupi", "1709500"],
		["Ipueiras", "1709807"],
		["Itacajá", "1710508"],
		["Itaguatins", "1710706"],
		["Itapiratins", "1710904"],
		["Itaporã do Tocantins", "1711100"],
		["Jaú do Tocantins", "1711506"],
		["Juarina", "1711803"],
		["Lagoa da Confusão", "1711902"],
		["Lagoa do Tocantins", "1711951"],
		["Lajeado", "1712009"],
		["Lavandeira", "1712157"],
		["Lizarda", "1712405"],
		["Luzinópolis", "1712454"],
		["Marianópolis do Tocantins", "1712504"],
		["Mateiros", "1712702"],
		["Maurilândia do Tocantins", "1712801"],
		["Miracema do Tocantins", "1713205"],
		["Miranorte", "1713304"],
		["Monte do Carmo", "1713601"],
		["Monte Santo do Tocantins", "1713700"],
		["Muricilândia", "1713957"],
		["Natividade", "1714203"],
		["Nazaré", "1714302"],
		["Nova Olinda", "1714880"],
		["Nova Rosalândia", "1715002"],
		["Novo Acordo", "1715101"],
		["Novo Alegre", "1715150"],
		["Novo Jardim", "1715259"],
		["Oliveira de Fátima", "1715507"],
		["Palmas", "1721000"],
		["Palmeirante", "1715705"],
		["Palmeiras do Tocantins", "1713809"],
		["Palmeirópolis", "1715754"],
		["Paraíso do Tocantins", "1716109"],
		["Paranã", "1716208"],
		["Pau D'Arco", "1716307"],
		["Pedro Afonso", "1716505"],
		["Peixe", "1716604"],
		["Pequizeiro", "1716653"],
		["Pindorama do Tocantins", "1717008"],
		["Piraquê", "1717206"],
		["Pium", "1717503"],
		["Ponte Alta do Bom Jesus", "1717800"],
		["Ponte Alta do Tocantins", "1717909"],
		["Porto Alegre do Tocantins", "1718006"],
		["Porto Nacional", "1718204"],
		["Praia Norte", "1718303"],
		["Presidente Kennedy", "1718402"],
		["Pugmil", "1718451"],
		["Recursolândia", "1718501"],
		["Riachinho", "1718550"],
		["Rio da Conceição", "1718659"],
		["Rio dos Bois", "1718709"],
		["Rio Sono", "1718758"],
		["Sampaio", "1718808"],
		["Sandolândia", "1718840"],
		["Santa Fé do Araguaia", "1718865"],
		["Santa Maria do Tocantins", "1718881"],
		["Santa Rita do Tocantins", "1718899"],
		["Santa Rosa do Tocantins", "1718907"],
		["Santa Tereza do Tocantins", "1719004"],
		["Santa Terezinha do Tocantins", "1720002"],
		["São Bento do Tocantins", "1720101"],
		["São Félix do Tocantins", "1720150"],
		["São Miguel do Tocantins", "1720200"],
		["São Salvador do Tocantins", "1720259"],
		["São Sebastião do Tocantins", "1720309"],
		["São Valério", "1720499"],
		["Silvanópolis", "1720655"],
		["Sítio Novo do Tocantins", "1720804"],
		["Sucupira", "1720853"],
		["Tabocão", "1708254"],
		["Taguatinga", "1720903"],
		["Taipas do Tocantins", "1720937"],
		["Talismã", "1720978"],
		["Tocantínia", "1721109"],
		["Tocantinópolis", "1721208"],
		["Tupirama", "1721257"],
		["Tupiratins", "1721307"],
		["Wanderlândia", "1722081"],
		["Xambioá", "1722107"],
	],
};
